import { useState, useEffect, useMemo, useCallback, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  EMPTY_CROP_CALENDAR_FORM,
  createFarmsViaDigisakaApi,
  deleteFarmViaDigisakaApi,
  farmerIdFromExplorerGeometryPayload,
  fetchFieldCropInformation,
  fetchFarmDetailViaDigisakaApi,
  fetchSavedFieldsListForUser,
  fieldCropHasDisplayableInfo,
  formValuesFromFieldCrop,
  getDigisakaAuthHeaders,
  isCropCalendarFormReady,
  parseExplorerFarmerId,
  displayBlockContextLabel,
  displayFarmName,
  farmRowsToBlockFeatureCollection,
  resolveFarmGeoJsonFromRow,
  resolveFarmGeoJsonWithDetail,
  updateFarmViaDigisakaApi,
  type CropCalendarFormValues,
  type FieldCropInformation,
} from '../../../config/digisakaApi';
import Sidebar from '../components/Sidebar';
import DroneProcessingPanel, { type DroneJobOverlay } from '../../drone-processing/DroneProcessingPage';
import { DRONE_PROCESSING_COMING_SOON } from '../../drone-processing/comingSoon';
import SearchBar from '../../shared/utils/SearchBar';
import AnalysisPanel from '../../shared/components/AnalysisPanel';
import { getRailIconCircleStyle, RAIL_LAYER_ACTIVE_COLOR, RAIL_TAB_META, RailToolIcon, type MapToolRailIcon } from '../../shared/components/MapToolRail';
import type { AnalysisHeaderSection } from '../components/AnalysisNavBar';
import {
  layerKeysToEnable,
  visibleLargeScaleNavSections,
} from '../components/analysisNavSections';
import {
  droneProvinceNavId,
  findDroneLayer,
  isDroneProvinceNavId,
} from '../../shared/constants/droneSites';
import MapView from '../../shared/components/MapView';
import {
  getPreviousTwoMonthWindow,
  getTwoMonthWindowEnding,
  type HeatMapObservationScene,
} from '../../shared/components/HeatMapObservationBar';
import {
  defaultSatelliteForIndex,
  resolveHeatmapSatellite,
  satelliteFilterOptionsForIndex,
  type IndexMapSatellite,
  type IndexMapSatelliteFilter,
} from '../../shared/constants/indexMapSatellites';
import {
  fetchSentinel2ObservationDatesCached,
  peekSentinel2ObservationDatesCache,
  scenesFromObservationDateResponse,
} from '../../../services/sentinel2ObservationDateCache';
import DronePrescriptionPanel, { type DronePrescriptionState } from '../../shared/components/DronePrescriptionPanel';
import type { DroneMissionConfig } from '../../shared/constants/dronePrescriptionLayers';
import {
  fetchProvinces,
  type MultitemporalZonalLayer,
  type MultitemporalZonalPanelState,
} from '../../shared/utils/multitemporalZonalAnalytics';
import SaveFieldModal from '../../shared/components/SaveFieldModal';
import UpdateFieldModal from '../../shared/components/UpdateFieldModal';
import FarmSplitProgressOverlay from '../../shared/components/FarmSplitProgressOverlay';
import { bufferPointToPolygon, isDegenerateOrPointGeometry, formatBufferAreaMessage } from '../../shared/utils/geometryBuffer';
import { calculatePolygonAreaHectares, getPolygonInteriorLonLat, getPolygonCentroid, geoJsonGeometryToPolygonRings } from '../../shared/components/DrawingUtils';
import { clipFeaturesToParentPolygon } from '../../shared/utils/clipPolygonsInsideParent';
import {
  adminPcodesToDigisakaCodes,
  fetchCentroidLocation,
} from '../../shared/utils/philippinesLocationFromCentroid';
import GeoJSON from 'ol/format/GeoJSON';
import Toast from '../../shared/components/Toast';
import FieldCropInfoCard from '../../shared/components/FieldCropInfoCard';
import CropCalendarInputs from '../../shared/components/CropCalendarInputs';
import { useTheme } from '../../shared/theme/ThemeContext';
import {
  getAnalysisResultStripTheme,
  getConfirmDialogTheme,
  showFieldsPanelTabClass,
  pageShellClass,
} from '../../shared/theme/themeStyles';
import LayerSwitchConfirmDialog from '../../shared/components/LayerSwitchConfirmDialog';
import { useWMSLayers } from '../../shared/utils/hooks/useWMSLayers';
import type { WMSLayerData } from '../../shared/utils/hooks/useWMSLayers';
import logo from "../../../assets/logo-transparent.png";
import DigisakaLLMChat, {
  isValidGptFieldContext,
} from '../../shared/components/DigisakaLLMChat';

/** How many separate polygons this saved field has (1 when it is a single block). */
function selectedFieldBlockCount(field: {
  polygons?: unknown;
  plot_count?: unknown;
  geometry?: { type?: string; coordinates?: unknown; geometry?: { type?: string; coordinates?: unknown } } | null;
  _geometry?: { type?: string; coordinates?: unknown } | null;
} | null): number {
  if (!field) return 1;
  if (Array.isArray(field.polygons) && field.polygons.length > 0) return field.polygons.length;
  const plot = Number(field.plot_count);
  if (Number.isFinite(plot) && plot > 1) return plot;
  const raw = field.geometry ?? field._geometry;
  const geometry = raw && 'geometry' in raw && raw.geometry ? raw.geometry : raw;
  if (geometry?.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
    return Math.max(1, geometry.coordinates.length);
  }
  return 1;
}
import type { FloodDamageData } from '../../../types/floodDamage';
import floodDamageDataJson from '../../../assets/floodDamageData.json';
import {
  DEFAULT_FLOOD_DAMAGE_EVENT,
  flattenFloodDamagePlaces,
  floodEventKey,
  placesForFloodEvent,
} from '../../shared/utils/floodDamagePlaces';
import FloodDamagePopup from '../../shared/components/FloodDamageModal';
import { DEFAULT_MAX_CLOUD_COVERAGE_PERCENT } from '../../shared/constants/cloudFilter';
import {
  cropfieldDelineateFromViewport,
  type CropfieldDelineationModel,
} from '../../shared/utils/cropfieldDelineateViewport';
import {
  assertFarmLevelMapZoom,
  getOlMapZoom,
} from '../../shared/utils/farmLevelMapZoom';
import { checkAgriculturalLand } from '../../shared/utils/checkAgriculturalLand';
import { polygonFeatureLabel } from '../../shared/utils/polygonFeatureLabel';
import { findDelineationIndicesOverlappingSavedFields } from '../../shared/utils/delineationSavedOverlap';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { DIGISAKA_API_FIELDDELINEATION, digisakaAuthRequestInit, gptInferenceMultipartAuthHeaders } from '../../../config/digisakaGptInference';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';
import WelcomeAccessModal from '../components/WelcomeAccessModal';
import GuestTimeUpOverlay from '../../shared/components/GuestTimeUpOverlay';
import DashboardTopbar from '../components/DashboardTopbar';
import DashboardNavSidebar, {
  NAV_SIDEBAR_COLLAPSED_PX,
  NAV_SIDEBAR_EXPANDED_PX,
  NAV_SIDEBAR_STORAGE_KEY,
} from '../components/DashboardNavSidebar';
import UserProfileModal from '../components/UserProfileModal';
import { requestClosePandoy } from '../../shared/utils/pandoyEvents';
import TourGuide, { tourStorage, createFarmTourStorage, type TourStep } from '../../shared/components/TourGuide';
import TourPromptModal from '../../shared/components/TourPromptModal';
import WhatsNewModal from '../components/WhatsNewModal';
import whatsNewService, { type WhatsNewActiveItem } from '../../../services/whatsNewService';
import { isProjectDeveloper } from '../../carbon-projects/homePath';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import authService from '../../../services/authService';
import {
  buildAllowedEntitlementSet,
  getEffectiveAllowedLayerKeysForMap,
  shouldShowDigisakaGpt,
  type LayerTogglePanelTabId,
} from '../../../config/analysisLayerEntitlements';

/** Viewport delineation defaults. ``model`` is chosen in the field-tools panel. */
const AI_FIELD_YOLO_VIEWPORT_PARAMS = {
  tile_size: 1280,
  overlap: 128,
  resolution: 1.0,
  conf_threshold: 0.25,
  include_preview: false,
  tta: false,
  smooth_epsilon: 4,
  max_polygon_vertices: 100,
};

// Sample WMS date arrays - Replace with actual API data
const wmsDateArrays: WMSLayerData = {
  yield: [
    '2024-01-01', '2024-02-01', '2024-03-01', '2024-04-01',
    '2024-05-01', '2024-06-01', '2024-07-01',
    // August to December 2024 excluded
    '2025-01-01', '2025-02-01', '2025-03-01',
  ],
  vhi: [
    '2024-01-01', '2024-02-01', '2024-03-01', '2024-04-01',
    '2024-05-01', '2024-06-01', '2024-07-01', '2024-08-01',
    '2024-09-01', '2024-10-01', '2024-11-01', '2024-12-01'
  ],
  lst: [
    '2023-01-01', '2023-02-01', '2023-03-01', '2023-04-01',
    '2023-05-01', '2023-06-01', '2023-07-01', '2023-08-01',
    '2023-09-01', '2023-10-01', '2023-11-01', '2023-12-01'
  ]
};

export default function Home() {
  const navigate = useNavigate();
  const { user: authUser, refreshUser, isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const rsEmerald = useMemo(() => getAnalysisResultStripTheme(isDark, 'emerald'), [isDark]);
  const rsRed = useMemo(() => getAnalysisResultStripTheme(isDark, 'red'), [isDark]);
  const deleteConfirmTheme = useMemo(() => getConfirmDialogTheme(isDark), [isDark]);
  const effectiveAllowedLayerKeys = useMemo(
    () => getEffectiveAllowedLayerKeysForMap(authUser ?? null),
    [authUser]
  );
  /** Entitlement set for `MapToolRail` — same source as `AnalysisPanel`/`MapView` internally use. */
  const layerAllowedEntitlementSet = useMemo(
    () => buildAllowedEntitlementSet(effectiveAllowedLayerKeys),
    [effectiveAllowedLayerKeys]
  );

  // Refresh /auth/me when returning to the tab (e.g. admin updated role default layers)
  useEffect(() => {
    if (!isAuthenticated) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void refreshUser().catch(() => {});
      }, 400);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, refreshUser]);

  // UI State
  // Sidebar visibility: default open on lg+ and closed on smaller screens. The top-bar
  // control hides whichever panel is open (fields, farm analysis, or the active layer group)
  // so the map is clear. Switching to another nav tool opens that tool's panel again.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024
  );
  /** Left main menu (Farm Analysis, Rice Yield Forecast, …): full 272px, or the 72px icon rail. */
  const [navMenuExpanded, setNavMenuExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      const saved = window.localStorage.getItem(NAV_SIDEBAR_STORAGE_KEY);
      if (saved === '1') return true;
      if (saved === '0') return false;
    } catch {
      /* storage unavailable */
    }
    return window.innerWidth >= 1280;
  });
  const toggleNavMenu = useCallback(() => {
    setNavMenuExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(NAV_SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  }, []);
  const layoutBeforeAiFieldsRef = useRef<{ navMenuExpanded: boolean } | null>(null);

  /** Collapse only the main nav rail so the map is wider. Keep Field Tools open. */
  const collapseNavForAiFields = useCallback(() => {
    setNavMenuExpanded((current) => {
      if (layoutBeforeAiFieldsRef.current === null) {
        layoutBeforeAiFieldsRef.current = { navMenuExpanded: current };
      }
      return false;
    });
  }, []);

  const restoreNavAfterAiFields = useCallback(() => {
    const prev = layoutBeforeAiFieldsRef.current;
    if (!prev) return;
    layoutBeforeAiFieldsRef.current = null;
    setNavMenuExpanded(prev.navMenuExpanded);
  }, []);
  /** Sidebar visibility before Pandoy AI full-screen — restored when chat exits full-screen. */
  const sidebarBeforeChatFullscreenRef = useRef<boolean | null>(null);
  const handleChatFullscreenChange = useCallback((isFullscreen: boolean) => {
    if (isFullscreen) {
      setSidebarOpen((wasOpen) => {
        sidebarBeforeChatFullscreenRef.current = wasOpen;
        return false;
      });
    } else if (sidebarBeforeChatFullscreenRef.current !== null) {
      const restore = sidebarBeforeChatFullscreenRef.current;
      sidebarBeforeChatFullscreenRef.current = null;
      setSidebarOpen(restore);
    }
  }, []);

  /**
   * Tracks whether the viewport is below the Tailwind `sm` breakpoint (640px). Used to decide
   * how to mount the analysis panel:
   *   - `false` (`sm+`): a CSS-docked card next to the map tool rail (same overlay as the rail).
   *   - `true` (phones): as a full-screen sheet so the panel is reachable from the collapsed rail.
   *
   * We only mount one `<AnalysisPanel>` at a time (instead of two with one hidden) because the
   * panel has expensive side effects/effects and we want to avoid duplicate API requests.
   */
  const [isCompactViewport, setIsCompactViewport] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth < 640
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setIsCompactViewport(window.innerWidth < 640);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // ---- Onboarding tour --------------------------------------------------------------------
  // First-time users see a small modal asking if they want a guided tour. If yes, `TourGuide`
  // walks them through the major dashboard components with an anchored tooltip + spotlight.
  // The "seen" flag is stored in localStorage via `tourStorage` so the prompt doesn't repeat.
  // The "?" help button in the top-right header always re-launches the tour on demand.
  const [tourPromptOpen, setTourPromptOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  /** `dashboard` = full product tour; `create-farm` = empty-catalog how-to. */
  const [tourKind, setTourKind] = useState<'dashboard' | 'create-farm'>('dashboard');
  /** BLO/plantation catalog reported empty after first fields fetch. */
  const [bloFarmCatalogEmpty, setBloFarmCatalogEmpty] = useState(false);
  /** False until first-visit tour is skipped/finished (or already seen) so What's New doesn't race it. */
  const [tourGateDone, setTourGateDone] = useState(() => tourStorage.hasSeen());

  // Show the prompt once, after a short delay so the page has settled. Gated on
  // `isAuthenticated` so it never stacks with `WelcomeAccessModal` (which is shown to
  // unauthenticated visitors). Guest sessions become "authenticated" once `continueAsGuest`
  // returns, so guests still get the tour after the welcome flow.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (tourStorage.hasSeen()) {
      setTourGateDone(true);
      return;
    }
    const id = window.setTimeout(() => setTourPromptOpen(true), 1200);
    return () => window.clearTimeout(id);
  }, [isAuthenticated]);

  // ---- What's New (admin-authored release notes) ----------------------------------------
  // After real login (not system guests), fetch the latest published update the user has not
  // dismissed. Wait until the onboarding tour prompt/tour is out of the way so modals don't stack.
  const [whatsNewItem, setWhatsNewItem] = useState<WhatsNewActiveItem | null>(null);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewDismissing, setWhatsNewDismissing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !authUser || authUser.is_system_guest) {
      setWhatsNewItem(null);
      setWhatsNewOpen(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const item = await whatsNewService.getActive();
        if (!cancelled) setWhatsNewItem(item);
      } catch {
        if (!cancelled) setWhatsNewItem(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authUser?.id, authUser?.is_system_guest]);

  useEffect(() => {
    if (!whatsNewItem) return;
    if (!tourGateDone || tourActive || tourPromptOpen) return;
    const id = window.setTimeout(() => setWhatsNewOpen(true), 900);
    return () => window.clearTimeout(id);
  }, [whatsNewItem, tourGateDone, tourActive, tourPromptOpen]);

  const handleWhatsNewDismiss = async () => {
    if (!whatsNewItem) {
      setWhatsNewOpen(false);
      return;
    }
    setWhatsNewDismissing(true);
    try {
      await whatsNewService.dismiss(whatsNewItem.id);
    } catch {
      // Still close locally so the user is not stuck if the network blips.
    } finally {
      setWhatsNewDismissing(false);
      setWhatsNewOpen(false);
      setWhatsNewItem(null);
    }
  };

  /**
   * Tour step list. Each `target` is a `data-tour-id` selector applied below. `beforeStep`
   * makes sure the highlighted element is visible (e.g. opens the sidebar so the fields-panel
   * step has something to spotlight when the user collapsed it).
   */
  const dashboardTourSteps: TourStep[] = useMemo(
    () => [
      {
        target: '[data-tour-id="map-rail"]',
        title: 'Side menu',
        description:
          'Every analysis tool lives here. Farm Analysis is selected by default. Pick a tool and its layers open right underneath it; pick a layer to show it on the map. Under Drone, Drone Processing is your flight and Sample Data holds the existing flights. Settings is at the bottom, and the arrow shrinks the menu to icons. On phones, open the menu from the top bar.',
        placement: 'right',
      },
      {
        target: '[data-tour-id="sidebar"]',
        title: 'Fields panel',
        description:
          'Browse saved farms, draw new boundaries, upload GeoJSON / shapefile / KML, or run AI field delineation. Fold the panel to widen the map for analysis.',
        placement: 'right',
        beforeStep: () => {
          setProfileModalOpen(false);
          setSidebarOpen(true);
        },
      },
      {
        target: '[data-tour-id="top-search"]',
        title: 'Search places & coordinates',
        description:
          'Find any location in the Philippines, or paste latitude and longitude (e.g. 14.5995, 120.9842) to drop a marker and fly the map there.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-id="map-controls"]',
        title: 'Map controls',
        description:
          'Zoom, switch the base layer (satellite / hybrid / terrain), locate yourself, and toggle fullscreen.',
        placement: 'right',
      },
      {
        target: '[data-tour-id="digisaka-gpt"]',
        title: 'Pandoy AI',
        description:
          'Ask Pandoy AI about your map, field health, or results. Open it from this button. It can explain what you see after you run a check.',
        placement: 'left',
      },
      {
        target: '[data-tour-id="theme-toggle"]',
        title: 'Light & dark mode',
        description:
          'Switch themes anytime. The side menu, analysis panels, legends, result strips, and time-series controls all follow your choice.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-id="top-user"]',
        title: 'Notifications & profile',
        description:
          'Open notifications and your account menu from the avatar. On mobile these sit in the top header beside Help and the theme toggle.',
        placement: 'bottom',
      },
    ],
    []
  );

  /** Guided path when a BLO has no farms yet — how to create the first field. */
  const createFarmTourSteps: TourStep[] = useMemo(
    () => [
      {
        target: '[data-tour-id="field-tools"]',
        title: 'Create your first farm',
        description:
          'You don’t have any saved farms yet. Use Field tools to add one: draw by hand, detect with AI, or upload a boundary file. After you save, it appears in this list and on the map.',
        placement: 'right',
        beforeStep: () => setSidebarOpen(true),
      },
      {
        target: '[data-tour-id="tool-draw-field"]',
        title: 'Draw field',
        description:
          'Zoom the map to farm level, then tap Draw field and click around the boundary. Finish the polygon, then save the field from the prompt.',
        placement: 'right',
        beforeStep: () => setSidebarOpen(true),
      },
      {
        target: '[data-tour-id="tool-ai-delineation"]',
        title: 'AI delineation',
        description:
          'At farm-level zoom, AI can detect field blocks from the satellite view. Pick a model, run detection, review the blocks, then save the ones you want.',
        placement: 'right',
        beforeStep: () => setSidebarOpen(true),
      },
      {
        target: '[data-tour-id="tool-upload-field"]',
        title: 'Upload a file',
        description:
          'Already have a boundary? Upload GeoJSON, shapefile, KML/KMZ, or GeoPackage. Review it on the map, then save to your Digisaka farm list.',
        placement: 'right',
        beforeStep: () => setSidebarOpen(true),
      },
    ],
    []
  );

  const tourSteps = tourKind === 'create-farm' ? createFarmTourSteps : dashboardTourSteps;

  const handleTourPromptStart = () => {
    setTourPromptOpen(false);
    setTourKind('dashboard');
    setTourStep(0);
    setTourActive(true);
  };
  const handleTourPromptLater = () => {
    // "Maybe later" — dismiss for this session but allow the prompt to appear again next visit.
    setTourPromptOpen(false);
    setTourGateDone(true);
  };
  const handleTourPromptDontShowAgain = () => {
    tourStorage.markSeen();
    setTourPromptOpen(false);
    setTourGateDone(true);
  };
  const handleTourSkip = () => {
    setTourActive(false);
    if (tourKind === 'create-farm') {
      createFarmTourStorage.markSeen();
    } else {
      tourStorage.markSeen();
      setTourGateDone(true);
    }
  };
  const handleTourComplete = () => {
    setTourActive(false);
    if (tourKind === 'create-farm') {
      createFarmTourStorage.markSeen();
    } else {
      tourStorage.markSeen();
      setTourGateDone(true);
    }
  };
  const handleStartTourManually = () => {
    setProfileModalOpen(false);
    setTourKind('dashboard');
    setTourStep(0);
    setTourActive(true);
  };
  const handleStartCreateFarmTour = useCallback(() => {
    setTourPromptOpen(false);
    setSidebarOpen(true);
    setTourKind('create-farm');
    setTourStep(0);
    setTourActive(true);
  }, []);

  // The empty farm list is also true before login. Do not open a tour over the welcome screen.
  useEffect(() => {
    if (isAuthenticated) return;
    setBloFarmCatalogEmpty(false);
    setTourActive(false);
    setTourPromptOpen(false);
  }, [isAuthenticated]);

  // After the main onboarding gate, if a signed-in BLO still has zero farms, offer the create-farm tour once.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!bloFarmCatalogEmpty) return;
    if (!tourGateDone || tourActive || tourPromptOpen) return;
    if (createFarmTourStorage.hasSeen()) return;
    const role = String(authUser?.role || '').toLowerCase();
    if (role === 'lgu') return;
    const id = window.setTimeout(() => handleStartCreateFarmTour(), 1400);
    return () => window.clearTimeout(id);
  }, [
    isAuthenticated,
    bloFarmCatalogEmpty,
    tourGateDone,
    tourActive,
    tourPromptOpen,
    authUser?.role,
    handleStartCreateFarmTour,
  ]);

  const [analysisPanelOpen, setAnalysisPanelOpen] = useState(true);
  const [analysisMode, setAnalysisMode] = useState<'field-level' | 'large-scale'>('field-level');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [droneProcessingOpen, setDroneProcessingOpen] = useState(false);
  const [joinedWorkspaceId, setJoinedWorkspaceId] = useState<string | null>(null);
  /** Pandoy opened from the side menu fills the main area. The corner button stays a popup. */
  const [pandoyPageOpen, setPandoyPageOpen] = useState(false);
  const [droneJobOverlay, setDroneJobOverlay] = useState<DroneJobOverlay | null>(null);
  const handleDroneOverlay = useCallback((overlay: DroneJobOverlay | null) => {
    setDroneJobOverlay(overlay);
  }, []);
  /**
   * `MapToolRail` active icon. Single source of truth for which floating tool card is open.
   * Synced with the legacy `analysisPanelOpen` flag below (we keep both during the transition
   * because many existing handlers still call `setAnalysisPanelOpen(true/false)`).
   */
  const [activeRailIcon, setActiveRailIcon] = useState<MapToolRailIcon | null>('farm');
  const openDroneProcessing = useCallback(() => {
    if (DRONE_PROCESSING_COMING_SOON) return;
    setProfileModalOpen(false);
    setPandoyPageOpen(false);
    requestClosePandoy();
    setSidebarOpen(true);
    setActiveRailIcon(null);
    setAnalysisPanelOpen(false);
    setDroneProcessingOpen(true);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('open') !== 'drone-processing') return;
    const workspace = searchParams.get('workspace') || '';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(workspace)) {
      setJoinedWorkspaceId(workspace);
    }
    openDroneProcessing();
    setToast({ message: 'You joined the workspace. Drone Processing is open.', type: 'success' });
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    next.delete('workspace');
    setSearchParams(next, { replace: true });
  }, [openDroneProcessing, searchParams, setSearchParams]);

  useEffect(() => {
    const tool = searchParams.get('tool');
    if (!tool) return;
    const next = new URLSearchParams(searchParams);
    next.delete('tool');
    setSearchParams(next, { replace: true });
    if (!(tool in RAIL_TAB_META) || tool === 'farm') return;
    setActiveRailIcon(tool as MapToolRailIcon);
    setAnalysisPanelOpen(true);
    setAnalysisMode('large-scale');
  }, [searchParams, setSearchParams]);

  /**
   * When `true`, the analysis panel is "minimized": the docked card is hidden via
   * `display: none` (state preserved, no remount) and a small floating chip is shown so the
   * user can restore it with one click. Reset to `false` whenever the active rail icon changes
   * (you can't minimize a tool you're not viewing) and when the panel is closed.
   */
  const [analysisPanelMinimized, setAnalysisPanelMinimized] = useState(false);
  useEffect(() => {
    setAnalysisPanelMinimized(false);
  }, [activeRailIcon]);

  /**
   * One sidebar at a time, and the same hide control covers every one of them.
   * Farm analysis keeps field tools and saved fields.
   * Any other layer group replaces that sidebar with its own panel.
   * Switching the nav tool opens the new panel. A manual hide stays until the next tool.
   */
  useEffect(() => {
    if (!activeRailIcon) return;
    setDroneProcessingOpen(false);
    setSidebarOpen(true);
  }, [activeRailIcon]);

  /**
   * After confirming a switch from farm-level → large-scale via a rail click, this remembers
   * which large-scale tab to auto-open once the switch completes.
   */
  const [pendingRailIconAfterSwitch, setPendingRailIconAfterSwitch] = useState<LayerTogglePanelTabId | null>(null);

  // Field and Analysis State
  const [selectedField, setSelectedField] = useState(null);
  const [fieldGeoJson, setFieldGeoJson] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingType, setProcessingType] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isLoadingGeoJson, setIsLoadingGeoJson] = useState(false);

  // Analysis Data States
  const [indexData, setIndexData] = useState(null);
  const [vhiData, setVhiData] = useState(null);
  const [indexHeatMapData, setIndexHeatMapData] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Drawing States
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [manualDrawEditing, setManualDrawEditing] = useState(false);
  /** Polygon still on map after Stop Drawing — offer Edit / Discard / Confirm */
  const [manualDrawRecoverable, setManualDrawRecoverable] = useState(false);
  const [drawnField, setDrawnField] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  /** Soft advisory from GEE ag-land check after manual draw (null = no warning). */
  const [agLandWarning, setAgLandWarning] = useState<string | null>(null);
  const agLandCheckGenRef = useRef(0);
  /** Rings present when boundary edit started — used to detect append vs reshape. */
  const editOriginalRingsRef = useRef<number[][][]>([]);

  // Update boundary mode states
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);
  const [fieldToUpdate, setFieldToUpdate] = useState<any | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isSplittingFarm, setIsSplittingFarm] = useState(false);
  const [isSavingFarmSplit, setIsSavingFarmSplit] = useState(false);
  /** After AI split detection — user chooses Save or Discard. */
  const [farmSplitReview, setFarmSplitReview] = useState<{
    parentField: any;
    /** From explorer-fields/{id} geometry properties; required by explorer-fields-create. */
    farmer_id: number | null;
    features: Array<{ type: string; geometry: { type: string; coordinates: any }; properties?: any }>;
    parentAdmin: { region: string; province: string; municipality: string; barangay: string };
    baseName: string;
    /** All farm polygon rings before this split. */
    parentPolygons: number[][][];
    /** Which block was split (replaced by saved pieces); null = whole-farm split. */
    sourceBlockIndex: number | null;
    /** Current farm id (updates after create+delete replace). */
    farmId: string | number;
  } | null>(null);
  /** Split review UX — picking subplots (click/list) or editing vertices. */
  const [splitReviewStep, setSplitReviewStep] = useState<'picking' | 'editing'>('picking');
  const [splitSelectedIndex, setSplitSelectedIndex] = useState<number | null>(null);
  /** Stores the confirmed geometry after user finishes adjusting boundary */
  const [confirmedUpdatedGeometry, setConfirmedUpdatedGeometry] = useState<any | null>(null);

  // Change Detection States
  const [isChangeDetectionMode, setIsChangeDetectionMode] = useState(false);
  const [changeDetectionData, setChangeDetectionData] = useState(null);

  // Track if analysis panel should be available (but not auto-opened)
  const [analysisPanelReady, setAnalysisPanelReady] = useState(true);

  // Field reload states
  const [reloadFields, setReloadFields] = useState(false);

  // Cloud filtering states
  const [showCloudFilter, setShowCloudFilter] = useState(false);
  const [maxCloudCoverage, setMaxCloudCoverage] = useState(DEFAULT_MAX_CLOUD_COVERAGE_PERCENT);

  // Heat map observation bar — Sentinel-2 dates via /sentinel2observationdate/ (2-month windows)
  const [farmAnalysisTab, setFarmAnalysisTab] = useState('heatmap');
  const [farmAnalysisTabRemembered, setFarmAnalysisTabRemembered] = useState(false);
  const [farmNavSections, setFarmNavSections] = useState<AnalysisHeaderSection[]>([]);
  const [pinnedNavSection, setPinnedNavSection] = useState<string | null>(null);
  const [navInfoSection, setNavInfoSection] = useState<string | null>(null);
  const [farmTabRequest, setFarmTabRequest] = useState<{ id: string; n: number } | null>(null);
  const [farmWorkCancelEpoch, setFarmWorkCancelEpoch] = useState(0);
  const cancelInFlightFarmWork = () => setFarmWorkCancelEpoch((n) => n + 1);
  const [heatMapIndex, setHeatMapIndex] = useState('ndvi');
  const [heatMapSatellite, setHeatMapSatellite] = useState<IndexMapSatelliteFilter>('ALL');
  const [heatMapSceneSatellite, setHeatMapSceneSatellite] = useState<IndexMapSatellite>('S2');
  const heatMapAllowedSatellites = satelliteFilterOptionsForIndex(heatMapIndex);
  const heatMapApiSatellite = resolveHeatmapSatellite(heatMapSatellite, heatMapSceneSatellite);
  const [heatMapCloudFilterEnabled, setHeatMapCloudFilterEnabled] = useState(true);
  const [heatMapMaxCloudCover, setHeatMapMaxCloudCover] = useState(100);
  const [heatMapSelectedSceneId, setHeatMapSelectedSceneId] = useState<string | null>(null);
  const [heatMapObservationDate, setHeatMapObservationDate] = useState<string | null>(null);
  const [heatMapObservations, setHeatMapObservations] = useState<HeatMapObservationScene[]>([]);
  const [heatMapLoadingInitial, setHeatMapLoadingInitial] = useState(false);
  const [heatMapLoadingMore, setHeatMapLoadingMore] = useState(false);
  const [heatMapHasMore, setHeatMapHasMore] = useState(true);
  const heatMapFetchGenRef = useRef(0);
  /** Oldest date boundary already requested — next "load more" fetches the prior 2 months. */
  const heatMapOldestBoundaryRef = useRef<string | null>(null);

  const showHeatMapObservationBar =
    analysisPanelOpen &&
    analysisMode === 'field-level' &&
    !!selectedField &&
    !!fieldGeoJson &&
    farmAnalysisTab === 'heatmap' &&
    !isDrawingMode &&
    !manualDrawEditing &&
    !isSplittingFarm &&
    !isSavingFarmSplit &&
    !farmSplitReview;

  const heatMapIndexPrevRef = useRef(heatMapIndex);
  const handleHeatMapIndexChange = useCallback((index: string) => {
    setHeatMapIndex(index);
  }, []);

  // When Index Type changes, reset satellite filter (ALL for optical, S1 for radar, etc.)
  useEffect(() => {
    if (heatMapIndexPrevRef.current === heatMapIndex) return;
    heatMapIndexPrevRef.current = heatMapIndex;
    setHeatMapSatellite(defaultSatelliteForIndex(heatMapIndex));
  }, [heatMapIndex]);

  // Initial / satellite-change: 2-month observation window for the active satellite
  useEffect(() => {
    if (!showHeatMapObservationBar || !fieldGeoJson) return;

    const gen = ++heatMapFetchGenRef.current;
    const { start_date, end_date } = getTwoMonthWindowEnding(new Date());
    heatMapOldestBoundaryRef.current = start_date;

    setHeatMapHasMore(true);
    setHeatMapSelectedSceneId(null);
    setHeatMapObservationDate(null);

    const cached = peekSentinel2ObservationDatesCache({
      geometry: fieldGeoJson,
      start_date,
      end_date,
      satellite: heatMapSatellite,
    });
    if (cached) {
      const scenes = scenesFromObservationDateResponse(cached, heatMapSatellite);
      setHeatMapObservations(scenes);
      if (scenes.length > 0) {
        setHeatMapSelectedSceneId(scenes[0].id);
        setHeatMapObservationDate(scenes[0].date);
        setHeatMapSceneSatellite(
          resolveHeatmapSatellite(heatMapSatellite, String(scenes[0].satellite))
        );
      }
      setHeatMapLoadingInitial(false);
      return;
    }

    setHeatMapObservations([]);
    setHeatMapLoadingInitial(true);

    (async () => {
      try {
        const data = await fetchSentinel2ObservationDatesCached({
          geometry: fieldGeoJson,
          start_date,
          end_date,
          satellite: heatMapSatellite,
        });
        if (gen !== heatMapFetchGenRef.current) return;
        const scenes = scenesFromObservationDateResponse(data, heatMapSatellite);
        setHeatMapObservations(scenes);
        if (scenes.length > 0) {
          setHeatMapSelectedSceneId(scenes[0].id);
          setHeatMapObservationDate(scenes[0].date);
          setHeatMapSceneSatellite(
            resolveHeatmapSatellite(heatMapSatellite, String(scenes[0].satellite))
          );
        }
        setHeatMapHasMore(true);
      } catch (err) {
        if (gen !== heatMapFetchGenRef.current) return;
        console.error('Error fetching observation dates:', err);
        setHeatMapObservations([]);
        setHeatMapHasMore(true);
      } finally {
        if (gen === heatMapFetchGenRef.current) {
          setHeatMapLoadingInitial(false);
        }
      }
    })();
  }, [showHeatMapObservationBar, fieldGeoJson, selectedField?.farm_id, heatMapSatellite]);

  const loadMoreHeatMapObservations = useCallback(async () => {
    if (!fieldGeoJson || heatMapLoadingMore || heatMapLoadingInitial || !heatMapHasMore) return;
    const boundary = heatMapOldestBoundaryRef.current;
    if (!boundary) return;

    const gen = heatMapFetchGenRef.current;
    const { start_date, end_date } = getPreviousTwoMonthWindow(boundary);
    heatMapOldestBoundaryRef.current = start_date;
    setHeatMapLoadingMore(true);
    try {
      const data = await fetchSentinel2ObservationDatesCached({
        geometry: fieldGeoJson,
        start_date,
        end_date,
        satellite: heatMapSatellite,
      });
      if (gen !== heatMapFetchGenRef.current) return;
      const scenes = scenesFromObservationDateResponse(data, heatMapSatellite);
      if (scenes.length === 0) {
        const startYear = Number(start_date.slice(0, 4));
        if (startYear < new Date().getFullYear() - 5) {
          setHeatMapHasMore(false);
        }
        return;
      }
      setHeatMapObservations((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...scenes.filter((s) => !seen.has(s.id))];
      });
    } catch (err) {
      if (gen !== heatMapFetchGenRef.current) return;
      console.error('Error loading more observation dates:', err);
      setHeatMapHasMore(false);
    } finally {
      if (gen === heatMapFetchGenRef.current) {
        setHeatMapLoadingMore(false);
      }
    }
  }, [
    fieldGeoJson,
    heatMapLoadingMore,
    heatMapLoadingInitial,
    heatMapHasMore,
    heatMapSatellite,
  ]);

  const handleHeatMapLoadMore = useCallback(() => {
    void loadMoreHeatMapObservations();
  }, [loadMoreHeatMapObservations]);
  const [filteredTimeSeriesData, setFilteredTimeSeriesData] = useState(null);
  // Farm-level time series selection from MapView player (by date)
  const [externalTimeSeriesDate, setExternalTimeSeriesDate] = useState<string | undefined>(undefined);

  // Layer control states (exposed from MapView)
  const [layerControls, setLayerControls] = useState<any>(null);

  // Forecast results state (for display below map)
  const [forecastResults, setForecastResults] = useState<any>(null);
  // Mobile-only tab for forecast panel: 'summary' (yield & price) | 'chart' (yield range)
  const [forecastResultsMobileTab, setForecastResultsMobileTab] = useState<'summary' | 'chart'>('summary');

  // Straw burn results state (for display below map)
  const [strawBurnResults, setStrawBurnResults] = useState<any>(null);
  const [strawBurnResultsMobileTab, setStrawBurnResultsMobileTab] = useState<'summary' | 'info'>('summary');

  // Drone NDVI prescription stats — left sidebar panel (same slot as fields sidebar)
  const [dronePrescriptionPanel, setDronePrescriptionPanel] = useState<{
    state: DronePrescriptionState;
    mission: DroneMissionConfig;
    usingMissionExtent: boolean;
  } | null>(null);

  const showDronePrescriptionSidebar =
    !!dronePrescriptionPanel && dronePrescriptionPanel.state.status !== 'idle';

  // CADI / pest risk municipality zonal stats — left sidebar (same slot as fields / drone)
  const [multitemporalZonalPanel, setMultitemporalZonalPanel] = useState<{
    state: MultitemporalZonalPanelState;
    layer: MultitemporalZonalLayer;
    frameLabel: string;
    pestLabel?: string;
  } | null>(null);
  const [multitemporalProvinces, setMultitemporalProvinces] = useState<string[]>([]);
  const [multitemporalSelectedProvince, setMultitemporalSelectedProvince] = useState('');

  const multitemporalLayerVisible =
    !!layerControls?.layerVisibility?.cadiDrought || !!layerControls?.layerVisibility?.pestRisk;

  const showLeftAnalyticsSidebar = showDronePrescriptionSidebar;

  // Tree detection results state (for display below map)
  const [treeDetectionResults, setTreeDetectionResults] = useState<any>(null);
  const [showTreeDetectionModal, setShowTreeDetectionModal] = useState(false);
  const [treeDetectionResultsMobileTab, setTreeDetectionResultsMobileTab] = useState<'summary' | 'confidence' | 'visualization'>('summary');

  // Crown segmentation results state (for display below map)
  const [crownSegmentationResults, setCrownSegmentationResults] = useState<any>(null);
  const [showCrownSegmentationModal, setShowCrownSegmentationModal] = useState(false);
  const [crownSegmentationResultsMobileTab, setCrownSegmentationResultsMobileTab] = useState<'summary' | 'confidence' | 'visualization'>('summary');

  // Crop-field boundary delineation (Large-scale analysis — georeferenced ortho)
  const [cropfieldDelineationResults, setCropfieldDelineationResults] = useState<any>(null);
  const [showCropfieldDelineationModal, setShowCropfieldDelineationModal] = useState(false);
  const [cropfieldDelineationResultsMobileTab, setCropfieldDelineationResultsMobileTab] = useState<
    'summary' | 'confidence' | 'visualization'
  >('summary');

  // Flood damage (Loss & Damage) table state – display below map at 30% height
  const [floodDamageSelectedData, setFloodDamageSelectedData] = useState<FloodDamageData | null>(null);

  // Ordered list of flattened municipalities for the active flood event (prev/next)
  const floodDamagePlaces = useMemo(
    () => flattenFloodDamagePlaces(floodDamageDataJson as FloodDamageData[]),
    []
  );
  const floodDamageOrderedList = useMemo(() => {
    const eventKey = floodDamageSelectedData
      ? floodEventKey(floodDamageSelectedData.data[0]?.Event)
      : DEFAULT_FLOOD_DAMAGE_EVENT;
    return placesForFloodEvent(floodDamagePlaces, eventKey);
  }, [floodDamagePlaces, floodDamageSelectedData]);

  const floodDamageCurrentIndex = floodDamageSelectedData
    ? floodDamageOrderedList.findIndex((m) => m.id === floodDamageSelectedData.id)
    : -1;
  const floodDamageHasPrev = floodDamageCurrentIndex > 0;
  const floodDamageHasNext =
    floodDamageCurrentIndex >= 0 && floodDamageCurrentIndex < floodDamageOrderedList.length - 1;

  const lastFloodDamageFlySignatureRef = useRef<string | null>(null);

  // Show RapidDamage GeoTIFF and zoom when a municipality is selected
  useEffect(() => {
    const showRaster = layerControls?.showFloodDamageRaster;
    if (!floodDamageSelectedData) {
      lastFloodDamageFlySignatureRef.current = null;
      showRaster?.(null);
      return;
    }
    if (!showRaster) return;

    const raster = floodDamageSelectedData.raster;
    const { lat, lon, id } = floodDamageSelectedData;
    const bbox = raster?.bbox;

    const sig =
      bbox && bbox.length === 4
        ? `${id}:raster:${bbox.join(',')}`
        : typeof lat === 'number' && typeof lon === 'number'
          ? `${id}:${lat}:${lon}`
          : id;

    const shouldFit = lastFloodDamageFlySignatureRef.current !== sig;
    if (shouldFit) lastFloodDamageFlySignatureRef.current = sig;

    // Baseline yield raster is off for now; still zoom to the municipality extent.
    showRaster(raster ?? null, shouldFit ? { fit: true, showLayer: false } : { showLayer: false });

    if (
      shouldFit &&
      (!bbox || bbox.length !== 4) &&
      typeof lat === 'number' &&
      typeof lon === 'number'
    ) {
      layerControls.flyToLocation?.([lon, lat], 12);
    }
  }, [floodDamageSelectedData, layerControls]);

  // Track previous tab to only clear when actually switching tabs
  const previousTabRef = useRef<LayerTogglePanelTabId | null>(null);

  // Wrapper functions to clear other results when switching layers
  const handleForecastResultsUpdate = useCallback(
    (results: any) => {
      setForecastResults(results);
      if (results) {
        setForecastResultsMobileTab('summary');
        // Trigger Digisaka GPT auto analysis for rice yield forecast
        console.log('[HomePage] Setting autoGptRequest for riceForecast', {
          hasResults: !!results,
          source: 'riceForecast',
        });
        setAutoGptRequest({
          source: 'riceForecast',
          payload: results,
        });

        setStrawBurnResults(null);
        setTreeDetectionResults(null);
        setCrownSegmentationResults(null);
        setCropfieldDelineationResults(null);
        setFloodDamageSelectedData(null);
        setShowTreeDetectionModal(false);
        setShowCrownSegmentationModal(false);
        setShowCropfieldDelineationModal(false);
        if (layerControls?.handleLayerToggle) {
          layerControls.handleLayerToggle('strawBurn', false);
          layerControls.handleLayerToggle('treeDetection', false);
        }
        if (layerControls?.handleStrawBurnDataClear) layerControls.handleStrawBurnDataClear();
        if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
        if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
        if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);
      } else {
        setStrawBurnResults(null);
        setTreeDetectionResults(null);
        setCrownSegmentationResults(null);
        setCropfieldDelineationResults(null);
        setShowTreeDetectionModal(false);
        setShowCrownSegmentationModal(false);
        setShowCropfieldDelineationModal(false);
        if (layerControls?.handleLayerToggle) {
          layerControls.handleLayerToggle('strawBurn', false);
          layerControls.handleLayerToggle('treeDetection', false);
        }
        if (layerControls?.handleStrawBurnDataClear) layerControls.handleStrawBurnDataClear();
        if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
        if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
        if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);
      }
    },
    [layerControls]
  );

  const handleStrawBurnResultsUpdate = useCallback((results: any) => {
    setStrawBurnResults(results);
    if (results) {
      setStrawBurnResultsMobileTab('summary');
      setAutoGptRequest({ source: 'strawBurn', payload: results });
      setForecastResults(null);
      setTreeDetectionResults(null);
      setCrownSegmentationResults(null);
      setCropfieldDelineationResults(null);
      setFloodDamageSelectedData(null);
      setShowTreeDetectionModal(false);
      setShowCrownSegmentationModal(false);
      setShowCropfieldDelineationModal(false);
      if (layerControls?.handleLayerToggle) {
        layerControls.handleLayerToggle('riceForecast', false);
        layerControls.handleLayerToggle('treeDetection', false);
      }
      if (layerControls?.handleForecastDataUpdate) layerControls.handleForecastDataUpdate(null);
      if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
      if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
      if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);
    } else {
      setForecastResults(null);
      setTreeDetectionResults(null);
      setCrownSegmentationResults(null);
      setCropfieldDelineationResults(null);
      setFloodDamageSelectedData(null);
      setShowTreeDetectionModal(false);
      setShowCrownSegmentationModal(false);
      setShowCropfieldDelineationModal(false);
      if (layerControls?.handleLayerToggle) {
        layerControls.handleLayerToggle('riceForecast', false);
        layerControls.handleLayerToggle('treeDetection', false);
      }
      if (layerControls?.handleForecastDataUpdate) layerControls.handleForecastDataUpdate(null);
      if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
      if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
      if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);
    }
  }, [layerControls]);

  const handleDronePrescriptionUpdate = useCallback(
    (
      payload: {
        state: DronePrescriptionState;
        mission: DroneMissionConfig;
        usingMissionExtent: boolean;
      } | null
    ) => {
      setDronePrescriptionPanel(payload);
    },
    []
  );

  const handleCloseDronePrescriptionSidebar = useCallback(() => {
    setDronePrescriptionPanel(null);
    layerControls?.clearDronePrescriptionStats?.();
    layerControls?.handleLayerToggle?.('isabelaPhilriceNDVI', false);
    layerControls?.handleLayerToggle?.('caviteSilangNDVI', false);
  }, [layerControls]);

  const handleMultitemporalZonalUpdate = useCallback(
    (
      payload: {
        state: MultitemporalZonalPanelState;
        layer: MultitemporalZonalLayer;
        frameLabel: string;
        pestLabel?: string;
      } | null
    ) => {
      setMultitemporalZonalPanel(payload);
    },
    []
  );

  const handleMultitemporalProvinceChange = useCallback(
    (province: string) => {
      setMultitemporalSelectedProvince(province);
      layerControls?.requestMultitemporalProvinceStats?.(province);
    },
    [layerControls]
  );

  const handleSelectMultitemporalMunicipality = useCallback(
    (municipality: string | null) => {
      layerControls?.selectMultitemporalMunicipality?.(municipality);
    },
    [layerControls]
  );

  useEffect(() => {
    if (!multitemporalLayerVisible) {
      setMultitemporalSelectedProvince('');
      return;
    }
    let cancelled = false;
    fetchProvinces()
      .then((list) => {
        if (!cancelled) setMultitemporalProvinces(list);
      })
      .catch(() => {
        if (!cancelled) setMultitemporalProvinces([]);
      });
    return () => {
      cancelled = true;
    };
  }, [multitemporalLayerVisible]);

  // Tree detection: update map layer and store results for panel below map
  const handleTreeDetectionDataUpdate = useCallback((data: any) => {
    layerControls?.handleTreeDetectionDataUpdate?.(data);
    setTreeDetectionResults(data);
    if (data) {
      setTreeDetectionResultsMobileTab('summary');
      setAutoGptRequest({ source: 'treeDetection', payload: data });
      setShowTreeDetectionModal(false);
      setCrownSegmentationResults(null);
      setFloodDamageSelectedData(null);
      setShowCrownSegmentationModal(false);
      layerControls?.handleCrownSegmentationMapUpdate?.(null);
    }
  }, [layerControls]);

  // Crown segmentation: store results for panel below map; polygons on map when API includes geojson
  const handleCrownSegmentationResultsUpdate = useCallback((data: any) => {
    setCrownSegmentationResults(data);
    layerControls?.handleCrownSegmentationMapUpdate?.(data ?? null);
    if (data) {
      setCrownSegmentationResultsMobileTab('summary');
      setAutoGptRequest({ source: 'crownSegmentation', payload: data });
      setShowCrownSegmentationModal(false);
      setTreeDetectionResults(null);
      setFloodDamageSelectedData(null);
      setShowTreeDetectionModal(false);
      if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
      const hasGeoOnMap =
        (Array.isArray(data.geojson_wgs84?.features) && data.geojson_wgs84.features.length > 0) ||
        (Array.isArray(data.geojson?.features) && data.geojson.features.length > 0);
      // Zoom only if no polygon GeoJSON (e.g. empty run); map layer fit handles the normal case
      if (!hasGeoOnMap) {
        const lon = data?.centroid?.lon;
        const lat = data?.centroid?.lat;
        if (typeof lon === 'number' && typeof lat === 'number' && layerControls?.flyToLocation) {
          layerControls.flyToLocation([lon, lat], 16);
        }
      }
    }
  }, [layerControls]);

  const handleCropfieldDelineationUpdate = useCallback(
    (data: any) => {
      layerControls?.handleCropfieldDelineationUpdate?.(data);
      setCropfieldDelineationResults(data);
      if (data) {
        setCropfieldDelineationResultsMobileTab('summary');
        setShowCropfieldDelineationModal(false);
        setAutoGptRequest({ source: 'cropfieldDelineation', payload: data });
      }
    },
    [layerControls]
  );

  const handleExportCropfieldGeoJson = useCallback(() => {
    // Prefer WGS84 (EPSG:4326) from API when present; else native raster CRS (e.g. EPSG:3857)
    const fc =
      cropfieldDelineationResults?.geojson_wgs84 ?? cropfieldDelineationResults?.geojson;
    if (!fc || typeof fc !== 'object') return;
    const json = JSON.stringify(fc, null, 2);
    const blob = new Blob([json], { type: 'application/geo+json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const crsPart = cropfieldDelineationResults.geojson_wgs84
      ? 'EPSG4326'
      : cropfieldDelineationResults.crs
        ? String(cropfieldDelineationResults.crs).replace(/[^\w.:-]/g, '_').slice(0, 48)
        : 'native';
    a.download = `field_delineation_${crsPart}_${new Date().toISOString().slice(0, 10)}.geojson`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [cropfieldDelineationResults]);

  const handleExportTreeGeoJson = useCallback(() => {
    const fc = treeDetectionResults?.geojson ?? treeDetectionResults?.geojson_wgs84;
    if (!fc || typeof fc !== 'object') return;
    const json = JSON.stringify(fc, null, 2);
    const blob = new Blob([json], { type: 'application/geo+json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const crsPart = treeDetectionResults?.geojson_crs
      ? String(treeDetectionResults.geojson_crs).replace(/[^\w.:-]/g, '_').slice(0, 48)
      : 'WGS84';
    a.download = `tree_detection_${crsPart}_${new Date().toISOString().slice(0, 10)}.geojson`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [treeDetectionResults]);

  const handleExportCrownGeoJson = useCallback(() => {
    const fc = crownSegmentationResults?.geojson_wgs84 ?? crownSegmentationResults?.geojson;
    if (!fc || typeof fc !== 'object') return;
    const json = JSON.stringify(fc, null, 2);
    const blob = new Blob([json], { type: 'application/geo+json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const crsPart = crownSegmentationResults.geojson_wgs84
      ? 'EPSG4326'
      : crownSegmentationResults.crs
        ? String(crownSegmentationResults.crs).replace(/[^\w.:-]/g, '_').slice(0, 48)
        : 'native';
    a.download = `crown_segmentation_${crsPart}_${new Date().toISOString().slice(0, 10)}.geojson`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [crownSegmentationResults]);

  const handleBSRIAnalyseRequest = useCallback((range: { start_date: string; end_date: string }) => {
    setAutoGptRequest({
      source: 'bsri',
      payload: range,
    });
  }, []);
  const handleFarmForecastResultsUpdate = useCallback((result: any) => {
    setAutoGptRequest({
      source: 'riceForecast',
      payload: result,
    });
  }, []);

  // Export straw burn results to PDF
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const handleStrawBurnDownloadPDF = useCallback(async () => {
    if (!strawBurnResults) return;
    setIsGeneratingPDF(true);
    try {
      let logoDataUrl: string | null = null;
      try {
        const logoRes = await fetch(logo);
        const logoBlob = await logoRes.blob();
        logoDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('logo read failed'));
          reader.readAsDataURL(logoBlob);
        });
      } catch {
        logoDataUrl = null;
      }

      const stats = strawBurnResults.statistics;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = 210;
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentW = pageW - margin * 2;
      let y = 20;

      const fmtHa = (n: number | undefined | null) =>
        n == null || Number.isNaN(Number(n))
          ? '--'
          : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
              Number(n)
            );

      const fmtPct = (n: number | undefined | null) =>
        n == null || Number.isNaN(Number(n))
          ? '--'
          : new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
              Number(n)
            );

      const burnedHa = stats?.burned_area_hectares;
      const pctRaw = stats?.burned_percentage;
      const riceHa =
        stats?.rice_area_hectares ??
        stats?.total_rice_area_hectares ??
        stats?.rice_area ??
        (burnedHa != null && pctRaw != null && pctRaw > 0 ? burnedHa / (pctRaw / 100) : undefined);

      const analyzedHa =
        stats?.analyzed_area_hectares ?? stats?.analysis_extent_hectares ?? stats?.total_area_hectares;

      const burnRateOfRice =
        riceHa != null && riceHa > 0 && burnedHa != null
          ? (burnedHa / riceHa) * 100
          : pctRaw ?? undefined;

      const rbiMin = strawBurnResults.burned_rbi_min;
      const rbiMax = strawBurnResults.burned_rbi_max;
      const rbiThresholdText =
        rbiMin != null && rbiMax != null
          ? `RBI ${Number(rbiMin).toFixed(1)} to ${Number(rbiMax).toFixed(1)}`
          : 'RBI 0.0 to 0.9';

      const periodLabel = strawBurnResults.period || 'this harvest window';

      const maxTextW = contentW;
      const footerReserve = 34;
      const ensureSpace = (h: number) => {
        if (y + h > pageH - footerReserve) {
          pdf.addPage();
          y = 20;
        }
      };

      const writeln = (text: string, lineHeight = 4.8) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(45, 45, 45);
        const lines = pdf.splitTextToSize(text, maxTextW);
        for (const line of lines) {
          ensureSpace(lineHeight);
          pdf.text(line, margin, y);
          y += lineHeight;
        }
      };

      const writeHeading = (text: string) => {
        y += 4;
        ensureSpace(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(40, 40, 40);
        pdf.text(text, margin, y);
        y += 7;
      };

      const writeBullet = (text: string, indent = 6) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(55, 55, 55);
        const lines = pdf.splitTextToSize(text, maxTextW - indent);
        lines.forEach((line: string, i: number) => {
          ensureSpace(5);
          if (i === 0) pdf.text('•', margin + 1, y);
          pdf.text(line, margin + indent, y);
          y += 5;
        });
      };

      // Header bar
      pdf.setFillColor(220, 38, 38);
      pdf.rect(0, 0, pageW, 36, 'F');
      if (logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', margin, 5, 14, 14);
      }
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Rice Straw Burning Detection Report', pageW / 2, 16, { align: 'center' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageW / 2, 28, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('Digisaka WebGIS', pageW / 2, 33, { align: 'center' });
      y = 46;

      // Map screenshot
      if (mapScreenshotRef.current) {
        try {
          const canvas = await html2canvas(mapScreenshotRef.current, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#1f2937',
            scale: 1.5,
            logging: false,
          });
          const imgData = canvas.toDataURL('image/jpeg', 0.85);
          const imgAspect = canvas.height / canvas.width;
          const imgW = contentW;
          const imgH = imgW * imgAspect;
          const maxImgH = 100;
          const finalH = Math.min(imgH, maxImgH);

          pdf.setDrawColor(100, 100, 100);
          pdf.setLineWidth(0.3);
          pdf.rect(margin - 0.5, y - 0.5, imgW + 1, finalH + 1);
          pdf.addImage(imgData, 'JPEG', margin, y, imgW, finalH);
          y += finalH + 8;
        } catch {
          // If screenshot fails, continue without it
        }
      }

      // Location section
      pdf.setTextColor(40, 40, 40);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Location & Period', 20, y);
      y += 8;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const areaLabel = strawBurnResults.province ? 'Province' : 'Municipality';
      const areaName =
        strawBurnResults.province ?? strawBurnResults.municipality ?? 'N/A';
      const meta = [
        [areaLabel, areaName],
        ['Region', strawBurnResults.region || 'N/A'],
        ['Analysis Period', strawBurnResults.period || 'N/A'],
        ['Images Processed', String(strawBurnResults.images_processed ?? 'N/A')],
      ];
      meta.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${label}:`, 24, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(value, 70, y);
        y += 6;
      });
      y += 6;

      // Separator line
      pdf.setDrawColor(220, 38, 38);
      pdf.setLineWidth(0.5);
      pdf.line(20, y, pageW - 20, y);
      y += 10;

      // Key Findings
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Key Findings', 20, y);
      y += 10;

      // Burned area card
      pdf.setFillColor(254, 242, 242);
      pdf.roundedRect(20, y, 78, 36, 3, 3, 'F');
      pdf.setTextColor(185, 28, 28);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${stats?.burned_area_hectares?.toFixed(2) ?? '--'} ha`, 59, y + 16, { align: 'center' });
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Burned Area', 59, y + 28, { align: 'center' });

      // Burned percentage card
      pdf.setFillColor(255, 247, 237);
      pdf.roundedRect(112, y, 78, 36, 3, 3, 'F');
      pdf.setTextColor(194, 65, 12);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${stats?.burned_percentage?.toFixed(2) ?? '--'}%`, 151, y + 16, { align: 'center' });
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Burned Percentage', 151, y + 28, { align: 'center' });
      y += 46;

      // Total area
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(20, y, 170, 18, 3, 3, 'F');
      pdf.setTextColor(30, 64, 175);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Over Total Rice Area: ${stats?.total_area_hectares?.toFixed(2) ?? '--'} ha`, pageW / 2, y + 11, { align: 'center' });
      y += 28;

      pdf.setDrawColor(220, 38, 38);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y, pageW - margin, y);
      y += 8;

      // Narrative — Burn Detection Summary (values from API where available)
      writeHeading('Burn Detection Summary');
      writeln(`Analyzed Area: ${fmtHa(analyzedHa)} hectares`);
      writeln(`Rice Area: ${fmtHa(riceHa)} hectares`);
      writeln(`Burned Area: ${fmtHa(burnedHa)} ha`);
      writeln(`Burn Rate: ${fmtPct(burnRateOfRice ?? pctRaw)}% of rice area`);
      y += 2;
      writeln(
        `The map shows concentrated burn hotspots (red patches) indicating significant straw burning activity during ${periodLabel}.`
      );

      writeHeading('Environmental Impact');
      const burnPctForCopy = burnRateOfRice ?? pctRaw ?? 0;
      if (burnPctForCopy >= 30) {
        writeBullet(
          `High burn rate (~${fmtPct(burnPctForCopy)}%) suggests widespread traditional residue management.`
        );
      } else if (burnPctForCopy >= 15) {
        writeBullet(
          `Moderate burn rate (~${fmtPct(burnPctForCopy)}%) indicates notable residue burning during the harvest window.`
        );
      } else {
        writeBullet(
          `Burn rate (${fmtPct(burnPctForCopy)}%) suggests relatively limited burning relative to rice area; continue monitoring.`
        );
      }
      writeBullet(
        'This releases methane and CO₂, degrades air quality, and damages soil microbiology.'
      );
      writeBullet(`The Rice Burn Index (RBI) threshold used for detection: ${rbiThresholdText}.`);

      writeHeading('Recommended Actions');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      ensureSpace(8);
      pdf.text('For Municipal/Regional Officers:', margin, y);
      y += 6;
      writeBullet('Identify hotspot clusters — prioritize these barangays for farmer education.');
      writeBullet(
        'Promote AWD (Alternate Wetting and Drying) — our Low Carbon Rice program reduces emissions without burning.'
      );
      writeBullet('Straw recycling programs — composting or fodder use alternatives.');
      writeBullet('Monitor compliance — re-run detection next season to track improvement.');
      y += 2;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      ensureSpace(8);
      pdf.text('For Individual Farmers:', margin, y);
      y += 6;
      writeBullet(
        'Switch to non-burn residue management (composting, mulching, mechanical incorporation).'
      );
      writeBullet(
        'Consider AWD water management to lower carbon footprint and qualify for carbon credits.'
      );

      // Algorithm note
      if (strawBurnResults.algorithm) {
        y += 4;
        ensureSpace(8);
        pdf.setTextColor(88, 28, 135);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'italic');
        pdf.text('Calibrated by drone data', margin, y);
        y += 6;
      }

      if (y > pageH - footerReserve) {
        pdf.addPage();
        y = 20;
      }
      y += 4;

      // Footer — Digisaka branding & contact (no logo here; logo remains in header only)
      const footBottom = pageH - 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(8);
      pdf.text('Contact: deploy@leadsagri.com', pageW / 2, footBottom, { align: 'center' });
      pdf.text('digisaka.com', pageW / 2, footBottom - 6, { align: 'center' });
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.text('Digisaka', pageW / 2, footBottom - 13, { align: 'center' });

      pdf.save(
        `straw-burn-report-${strawBurnResults.province || strawBurnResults.municipality || 'report'}-${new Date().toISOString().slice(0, 10)}.pdf`
      );
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [strawBurnResults]);

  // Export large-scale rice yield forecast to PDF (same report style as straw burning: map + templated narrative)
  const [isGeneratingForecastPDF, setIsGeneratingForecastPDF] = useState(false);
  const handleRiceForecastDownloadPDF = useCallback(async () => {
    if (!forecastResults?.forecasts) return;
    setIsGeneratingForecastPDF(true);
    try {
      let logoDataUrl: string | null = null;
      try {
        const logoRes = await fetch(logo);
        const logoBlob = await logoRes.blob();
        logoDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('logo read failed'));
          reader.readAsDataURL(logoBlob);
        });
      } catch {
        logoDataUrl = null;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = 210;
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentW = pageW - margin * 2;
      let y = 20;

      const maxTextW = contentW;
      const footerReserve = 34;
      const ensureSpace = (h: number) => {
        if (y + h > pageH - footerReserve) {
          pdf.addPage();
          y = 20;
        }
      };

      const writeln = (text: string, lineHeight = 4.8) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(45, 45, 45);
        const lines = pdf.splitTextToSize(text, maxTextW);
        for (const line of lines) {
          ensureSpace(lineHeight);
          pdf.text(line, margin, y);
          y += lineHeight;
        }
      };

      const writeHeading = (text: string) => {
        y += 4;
        ensureSpace(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(40, 40, 40);
        pdf.text(text, margin, y);
        y += 7;
      };

      const writeBullet = (text: string, indent = 6) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(55, 55, 55);
        const lines = pdf.splitTextToSize(text, maxTextW - indent);
        lines.forEach((line: string, i: number) => {
          ensureSpace(5);
          if (i === 0) pdf.text('•', margin + 1, y);
          pdf.text(line, margin + indent, y);
          y += 5;
        });
      };

      const regionName = forecastResults.region_name || 'Selected region';
      const scopeLabel = forecastResults.scope_label || regionName;
      const monthEntries = Object.entries(forecastResults.forecasts as Record<string, any>);

      // Header (emerald — matches rice forecast UI)
      pdf.setFillColor(5, 150, 105);
      pdf.rect(0, 0, pageW, 36, 'F');
      if (logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', margin, 5, 14, 14);
      }
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Rice Yield Forecast Report', pageW / 2, 16, { align: 'center' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageW / 2, 28, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('Digisaka WebGIS — Large-scale analysis', pageW / 2, 33, { align: 'center' });
      y = 46;

      if (mapScreenshotRef.current) {
        try {
          const canvas = await html2canvas(mapScreenshotRef.current, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#1f2937',
            scale: 1.5,
            logging: false,
          });
          const imgData = canvas.toDataURL('image/jpeg', 0.85);
          const imgAspect = canvas.height / canvas.width;
          const imgW = contentW;
          const imgH = imgW * imgAspect;
          const maxImgH = 100;
          const finalH = Math.min(imgH, maxImgH);
          pdf.setDrawColor(100, 100, 100);
          pdf.setLineWidth(0.3);
          pdf.rect(margin - 0.5, y - 0.5, imgW + 1, finalH + 1);
          pdf.addImage(imgData, 'JPEG', margin, y, imgW, finalH);
          y += finalH + 8;
        } catch {
          /* continue without map */
        }
      }

      pdf.setTextColor(40, 40, 40);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Forecast scope', 20, y);
      y += 8;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setFont('helvetica', 'bold');
      pdf.text('Area / scope:', 24, y);
      pdf.setFont('helvetica', 'normal');
      const scopeLines = pdf.splitTextToSize(scopeLabel, pageW - 80);
      if (scopeLines.length > 0) {
        pdf.text(scopeLines[0], 70, y);
        for (let i = 1; i < scopeLines.length; i++) {
          y += 5;
          pdf.text(scopeLines[i], 24, y);
        }
      }
      y += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Forecast month(s):', 24, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(monthEntries.map(([m]) => m).join(', ') || 'N/A', 70, y);
      y += 10;

      pdf.setDrawColor(5, 150, 105);
      pdf.setLineWidth(0.5);
      pdf.line(20, y, pageW - 20, y);
      y += 10;

      // Per-month key figures
      for (const [monthLabel, forecast] of monthEntries) {
        const stats = forecast?.region_statistics as
          | { mean_yield?: number; min_yield?: number; max_yield?: number }
          | undefined;
        const baseY = forecast?.base_yield;
        const price = forecast?.rice_price;

        writeHeading(`${monthLabel} — Key figures`);
        pdf.setFillColor(236, 253, 245);
        pdf.roundedRect(20, y, 170, 22, 2, 2, 'F');
        pdf.setTextColor(6, 95, 70);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const line1 = `Base yield: ${baseY != null && !Number.isNaN(Number(baseY)) ? Number(baseY).toFixed(2) : '—'} t/ha   ·   Palay price: ${price != null && !Number.isNaN(Number(price)) ? `₱${Number(price).toFixed(2)}/kg` : '—'}`;
        pdf.text(line1, pageW / 2, y + 8, { align: 'center' });
        if (stats) {
          const mean = stats.mean_yield;
          const min = stats.min_yield;
          const max = stats.max_yield;
          const line2 = `Regional yield range (model): min ${min != null ? Number(min).toFixed(2) : '—'} · mean ${mean != null ? Number(mean).toFixed(2) : '—'} · max ${max != null ? Number(max).toFixed(2) : '—'} t/ha`;
          pdf.text(line2, pageW / 2, y + 16, { align: 'center' });
        }
        y += 30;
      }

      pdf.setDrawColor(5, 150, 105);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y, pageW - margin, y);
      y += 8;

      writeHeading('Yield forecast summary');
      const first = monthEntries[0]?.[1] as any;
      const firstStats = first?.region_statistics;
      const meanY = firstStats?.mean_yield;
      const minY = firstStats?.min_yield;
      const maxY = firstStats?.max_yield;
      const spread =
        meanY != null && minY != null && maxY != null && maxY > minY
          ? ((maxY - minY) / meanY) * 100
          : null;

      writeln(
        `This report summarizes the large-scale rice yield outlook for ${regionName}. Base yield and palay price reference values are shown alongside a regional yield envelope (min–mean–max) from the forecast model.`
      );
      if (spread != null && !Number.isNaN(spread)) {
        if (spread > 35) {
          writeln(
            `The regional yield band is relatively wide (~${spread.toFixed(0)}% spread vs mean), indicating heterogeneous growing conditions or model uncertainty across the region.`
          );
        } else {
          writeln(
            `The regional yield band is comparatively tight (~${spread.toFixed(0)}% spread vs mean), suggesting more consistent expected performance across the area.`
          );
        }
      } else {
        writeln(
          'Use the min–mean–max range to judge upside and downside risk relative to the base yield for planning and procurement.'
        );
      }

      writeHeading('Market & price outlook');
      const samplePrice = (monthEntries[0]?.[1] as any)?.rice_price;
      if (samplePrice != null && !Number.isNaN(Number(samplePrice))) {
        writeln(
          `Reference palay price in this forecast is approximately ₱${Number(samplePrice).toFixed(2)} per kg (as returned by the service). Use this as a planning indicator alongside local market intelligence.`
        );
      } else {
        writeln(
          'Palay price inputs were not available for all rows; validate prices against local market quotes before financial decisions.'
        );
      }

      writeHeading('Recommended actions');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      ensureSpace(8);
      pdf.text('For planners & extension:', margin, y);
      y += 6;
      writeBullet('Compare base yield to the regional min–mean–max band to set realistic expectations by municipality or farm group.');
      writeBullet('Prioritize advisory or inputs where the forecast shows the widest yield spread or lowest min yield.');
      writeBullet('Align drying, storage, and logistics with the forecast month window to reduce post-harvest losses.');
      y += 2;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      ensureSpace(8);
      pdf.text('For farmers & cooperatives:', margin, y);
      y += 6;
      writeBullet('Treat base yield as a planning reference; field-level NDVI and soil moisture tools in Digisaka refine this further.');
      writeBullet('Lock in input plans early when the outlook is favorable; hedge price risk when palay price volatility is high.');
      writeBullet('Re-run the forecast when planting dates or weather regimes change materially.');

      if (y > pageH - footerReserve) {
        pdf.addPage();
        y = 20;
      }
      y += 4;
      const footBottom = pageH - 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(8);
      pdf.text('Contact: deploy@leadsagri.com', pageW / 2, footBottom, { align: 'center' });
      pdf.text('digisaka.com', pageW / 2, footBottom - 6, { align: 'center' });
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.text('Digisaka', pageW / 2, footBottom - 13, { align: 'center' });

      const safeRegion = String(scopeLabel).replace(/[^\w\-]+/g, '-').slice(0, 56);
      pdf.save(`rice-yield-forecast-${safeRegion}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setIsGeneratingForecastPDF(false);
    }
  }, [forecastResults]);

  // Handle tab changes - clear results when switching tabs
  const handleExpandedTabChange = useCallback((tab: LayerTogglePanelTabId | null) => {
    // Only clear results if actually switching tabs (not when staying on the same tab)
    const previousTab = previousTabRef.current;
    
    // Update the previous tab reference
    previousTabRef.current = tab;
    
    // If it's the same tab, don't clear anything
    if (previousTab === tab) {
      return;
    }

    setAutoGptRequest(null);

    // Clear results when switching to a different tab (keep only the active tab's results)
    if (tab !== 'forecast' && tab !== 'disasterLossDamage' && tab !== 'treeDetection') {
      setForecastResults(null);
      setStrawBurnResults(null);
      setTreeDetectionResults(null);
      setCrownSegmentationResults(null);
      setCropfieldDelineationResults(null);
      setFloodDamageSelectedData(null);
      setShowTreeDetectionModal(false);
      setShowCrownSegmentationModal(false);
      setShowCropfieldDelineationModal(false);
      if (layerControls?.handleLayerToggle) {
        layerControls.handleLayerToggle('riceForecast', false);
        layerControls.handleLayerToggle('strawBurn', false);
        layerControls.handleLayerToggle('treeDetection', false);
      }
      if (layerControls?.handleForecastDataUpdate) layerControls.handleForecastDataUpdate(null);
      if (layerControls?.handleStrawBurnDataClear) layerControls.handleStrawBurnDataClear();
      if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
      if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
      if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);
    } else if (tab === 'forecast') {
      setStrawBurnResults(null);
      setTreeDetectionResults(null);
      setCrownSegmentationResults(null);
      setCropfieldDelineationResults(null);
      setFloodDamageSelectedData(null);
      setShowTreeDetectionModal(false);
      setShowCrownSegmentationModal(false);
      setShowCropfieldDelineationModal(false);
      if (layerControls?.handleLayerToggle) {
        layerControls.handleLayerToggle('strawBurn', false);
        layerControls.handleLayerToggle('treeDetection', false);
      }
      if (layerControls?.handleStrawBurnDataClear) layerControls.handleStrawBurnDataClear();
      if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
      if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
      if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);
    } else if (tab === 'disasterLossDamage') {
      setForecastResults(null);
      setTreeDetectionResults(null);
      setCrownSegmentationResults(null);
      setCropfieldDelineationResults(null);
      setShowTreeDetectionModal(false);
      setShowCrownSegmentationModal(false);
      setShowCropfieldDelineationModal(false);
      if (layerControls?.handleLayerToggle) {
        layerControls.handleLayerToggle('riceForecast', false);
        layerControls.handleLayerToggle('treeDetection', false);
      }
      if (layerControls?.handleForecastDataUpdate) layerControls.handleForecastDataUpdate(null);
      if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
      if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
      if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);
    } else if (tab === 'treeDetection') {
      setForecastResults(null);
      setStrawBurnResults(null);
      setFloodDamageSelectedData(null);
      // Keep treeDetectionResults, crownSegmentationResults, cropfieldDelineationResults (same tab)
      if (layerControls?.handleLayerToggle) {
        layerControls.handleLayerToggle('riceForecast', false);
        layerControls.handleLayerToggle('strawBurn', false);
      }
      if (layerControls?.handleForecastDataUpdate) layerControls.handleForecastDataUpdate(null);
      if (layerControls?.handleStrawBurnDataClear) layerControls.handleStrawBurnDataClear();
    }
  }, [layerControls]);

  const handleFloodDamageSelect = useCallback(
    (data: FloodDamageData | null) => {
      setFloodDamageSelectedData(data);
      if (!data) {
        setAutoGptRequest(null);
        return;
      }
      setForecastResults(null);
      setStrawBurnResults(null);
      setTreeDetectionResults(null);
      setCrownSegmentationResults(null);
      setCropfieldDelineationResults(null);
      setShowTreeDetectionModal(false);
      setShowCrownSegmentationModal(false);
      setShowCropfieldDelineationModal(false);
      if (layerControls?.handleLayerToggle) {
        layerControls.handleLayerToggle('riceForecast', false);
        layerControls.handleLayerToggle('strawBurn', false);
        layerControls.handleLayerToggle('treeDetection', false);
      }
      if (layerControls?.handleForecastDataUpdate) layerControls.handleForecastDataUpdate(null);
      if (layerControls?.handleStrawBurnDataClear) layerControls.handleStrawBurnDataClear();
      if (layerControls?.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
      if (layerControls?.handleCropfieldDelineationUpdate) layerControls.handleCropfieldDelineationUpdate(null);
      if (layerControls?.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);

      // Trigger Digisaka GPT auto analysis for typhoon & drought Loss & Damage
      setAutoGptRequest({
        source: 'other',
        payload: data,
      });
    },
    [layerControls]
  );

  // Multi-field analysis state
  const [isMultiFieldAnalysis, setIsMultiFieldAnalysis] = useState(false);
  const [multiFieldSelection, setMultiFieldSelection] = useState<any[] | null>(null);
  const [isMultiFieldProcessing, setIsMultiFieldProcessing] = useState(false);
  /** Opened farm group polygons on the map (FeatureCollection) */
  const [farmGroupGeoJson, setFarmGroupGeoJson] = useState<any | null>(null);
  /** All saved farms as labeled blocks on the map (no auto-fit). */
  const [farmsOverviewGeoJson, setFarmsOverviewGeoJson] = useState<any | null>(null);
  /** Selected farm's full block set — used when LGU hides the all-boundaries layer. */
  const [selectedFarmBlocksGeoJson, setSelectedFarmBlocksGeoJson] = useState<any | null>(null);
  /** One-shot fit for overview/group layer (refetch, location filters, open group). */
  const [farmGroupFit, setFarmGroupFit] = useState(false);
  const savedFarmsByIdRef = useRef<Map<string, any>>(new Map());
  const farmRiskByIdRef = useRef<Record<string, { risk_level?: string | null }>>({});

  const farmGroupMapLoadGenRef = useRef(0);

  // Digisaka GPT auto-request (farm-level tools -> GPT)
  const mapScreenshotRef = useRef<HTMLDivElement | null>(null);
  const dashboardScreenshotRef = useRef<HTMLDivElement | null>(null);
  const [largeScaleLeftSlotEl, setLargeScaleLeftSlotEl] = useState<HTMLDivElement | null>(null);
  const [farmAnalysisSlotEl, setFarmAnalysisSlotEl] = useState<HTMLDivElement | null>(null);
  const [autoGptRequest, setAutoGptRequest] = useState<{
    source:
      | 'riceForecast'
      | 'heatMap'
      | 'farmWalk'
      | 'timeSeries'
      | 'cropCalendar'
      | 'climate'
      | 'strawBurn'
      | 'treeDetection'
      | 'crownSegmentation'
      | 'cropfieldDelineation'
      | 'bsri'
      | 'other';
    payload?: any;
    pasteText?: string;
  } | null>(null);

  /** Leaving a farm tool drops that tool's map result so the next tool starts clean. */
  const farmToolPrevRef = useRef(farmAnalysisTab);
  useEffect(() => {
    const previous = farmToolPrevRef.current;
    farmToolPrevRef.current = farmAnalysisTab;
    if (previous === farmAnalysisTab) return;

    if (previous === 'timeseries') {
      setTimeSeriesData(null);
      setShowCloudFilter(false);
      setIsChangeDetectionMode(false);
      setChangeDetectionData(null);
      setAnalysisResults(null);
      setAutoGptRequest((current) => (current?.source === 'timeSeries' ? null : current));
    }
    if (previous === 'heatmap') {
      setIndexHeatMapData(null);
      setAnalysisResults(null);
      setAutoGptRequest((current) => (current?.source === 'heatMap' ? null : current));
    }
    if (previous === 'climate') {
      setAutoGptRequest((current) => (current?.source === 'climate' ? null : current));
    }
    if (previous === 'cropcalendar') {
      setAutoGptRequest((current) => (current?.source === 'cropCalendar' ? null : current));
    }
  }, [farmAnalysisTab]);

  // Drawing/Upload confirmation dialog state
  const [showFarmLevelActionDialog, setShowFarmLevelActionDialog] = useState(false);
  const [pendingFarmLevelAction, setPendingFarmLevelAction] = useState<{
    type: 'draw' | 'upload' | 'aiDelineation';
    data?: any;
  } | null>(null);

  /** Field Tools — same viewport pipeline as large-scale “Field boundary delineation” */
  const [aiFieldDelineationStep, setAiFieldDelineationStep] = useState<
    'idle' | 'instruction' | 'picking' | 'editing'
  >('idle');
  const [aiFieldDelineationLoading, setAiFieldDelineationLoading] = useState(false);
  const [aiFieldDelineationError, setAiFieldDelineationError] = useState('');
  const [aiFieldDelineationData, setAiFieldDelineationData] = useState<any>(null);
  const [aiDelineationSelectedIndex, setAiDelineationSelectedIndex] = useState<number | null>(null);
  /** When true, list uses checkboxes to pick multiple blocks for one farm save. */
  const [aiDelineationMultiSelect, setAiDelineationMultiSelect] = useState(false);
  const [aiDelineationCheckedIndexes, setAiDelineationCheckedIndexes] = useState<number[]>([]);
  const [aiFieldDelineationInputMode, setAiFieldDelineationInputMode] = useState<'mapView' | 'file'>(
    'mapView'
  );
  const [aiFieldDelineationModel, setAiFieldDelineationModel] = useState<CropfieldDelineationModel>('dino');
  const [aiFieldDelineationGeoTiff, setAiFieldDelineationGeoTiff] = useState<File | null>(null);
  const [aiFieldDelineationOrigin, setAiFieldDelineationOrigin] = useState<'ai' | 'upload'>('ai');
  const [aiFieldDelineationFieldLabels, setAiFieldDelineationFieldLabels] = useState<string[]>([]);
  /** Block indices pending delete confirmation (sidebar button, Delete key, or overlap cleanup). */
  const [delineationDeleteConfirm, setDelineationDeleteConfirm] = useState<{
    indices: number[];
    title?: string;
    body?: string;
  } | null>(null);
  /** Hide saved farm polygons on the map while picking AI blocks (reduces overlap clutter). */
  const [showSavedFieldsDuringDelineation, setShowSavedFieldsDuringDelineation] = useState(false);
  const aiFieldDelineationStepRef = useRef(aiFieldDelineationStep);
  aiFieldDelineationStepRef.current = aiFieldDelineationStep;

  // Crop calendar: farmer input before fetch (triggered by right-click on farm polygon)
  const [showCropCalendarInputModal, setShowCropCalendarInputModal] = useState(false);
  const [cropCalendarForm, setCropCalendarForm] = useState<CropCalendarFormValues>(EMPTY_CROP_CALENDAR_FORM);
  const [cropCalendarFarmerInput, setCropCalendarFarmerInput] = useState<CropCalendarFormValues | null>(null);
  const [cropCalendarOpenRequest, setCropCalendarOpenRequest] = useState(0);
  const [cropCalendarFieldInfo, setCropCalendarFieldInfo] = useState<FieldCropInformation | null>(null);
  const [cropCalendarFieldInfoLoading, setCropCalendarFieldInfoLoading] = useState(false);
  const cropCalendarLookupGenRef = useRef(0);
  const [droneDataOpenRequest, setDroneDataOpenRequest] = useState(0);

  // WMS Layer Management Hook
  const {
    layerStates: wmsLayerStates,
    toggleLayerVisibility: toggleWMSLayerVisibility,
    resetLayerState,
    setLayerDateByIndex,
    setLayerDate,
    toggleLayerPlayback,
    stopAllPlayback,
    hideAllLayers: hideAllWMSLayers,
    getVisibleLayers,
    getPlayingLayers,
  } = useWMSLayers({
    dateArrays: wmsDateArrays,
    onDateChange: (date, layerType) => {
      // Date change handled by handleWMSDateChange
    },
    playbackSpeed: 1000,
  });

  // Apply cloud filtering when timeSeriesData or filtering settings change
  useEffect(() => {
    if (!timeSeriesData?.results) {
      setFilteredTimeSeriesData(timeSeriesData);
      return;
    }

    if (!showCloudFilter) {
      setFilteredTimeSeriesData(timeSeriesData);
      return;
    }

    // Filter results based on cloud coverage. A scene with no index value
    // (no pixels inside the farm) is not "clear" — drop it even if cloud is 0.
    const filtered = {
      ...timeSeriesData,
      results: timeSeriesData.results.filter((result) => {
        const hasIndex =
          result.mean_index_value != null || result.median_backscatter != null;
        if (!hasIndex) return false;

        const cloudValue =
          result.cloud_percentage !== undefined ? result.cloud_percentage : result.cloud_cover;

        // Sentinel-1 has no optical cloud cover.
        if (cloudValue === undefined || cloudValue === null) return true;
        return Number(cloudValue) <= maxCloudCoverage;
      })
    };

    setFilteredTimeSeriesData(filtered);
  }, [timeSeriesData, maxCloudCoverage, showCloudFilter]);

  // Handle cloud filter settings change from AnalysisPanel
  const handleCloudFilterChange = (filterSettings) => {
    setShowCloudFilter(filterSettings.showCloudFilter);
    setMaxCloudCoverage(filterSettings.maxCloudCoverage ?? DEFAULT_MAX_CLOUD_COVERAGE_PERCENT);
  };

  // Resolve GeoJSON from webgis farms row; admin summary rows load detail via FastAPI.
  const fetchFieldGeoJson = async (fieldId, fieldRow?: any) => {
    setIsLoadingGeoJson(true);
    try {
      const catalog = savedFarmsByIdRef.current.get(String(fieldId));
      const clickedCount = Array.isArray(fieldRow?.polygons) ? fieldRow.polygons.length : 0;
      const catalogCount = Array.isArray(catalog?.polygons) ? catalog.polygons.length : 0;
      const singleBlock =
        fieldRow?.block_index != null || fieldRow?.block_number != null;
      const row =
        !singleBlock && catalogCount > clickedCount
          ? catalog
          : fieldRow ?? catalog ?? ({ farm_id: fieldId } as Record<string, unknown>);
      const feature = await resolveFarmGeoJsonWithDetail(row);
      if (feature?.geometry) {
        console.log('Resolved farm GeoJSON:', feature.geometry?.type, 'plots', feature.properties?.plot_count);
        setFieldGeoJson(feature);
        return feature;
      }
      setFieldGeoJson(null);
      return null;
    } catch (error) {
      console.error('Error resolving field GeoJSON:', error);
      setFieldGeoJson(null);
      return null;
    } finally {
      setIsLoadingGeoJson(false);
    }
  };

  // Handle file upload from Sidebar
  const handleFileUpload = (geoJsonFeature: any) => {
    // Check if there's an error
    if (geoJsonFeature.error) {
      setToast({
        message: geoJsonFeature.error,
        type: 'error'
      });
      return;
    }

    // Check if we need confirmation before processing upload
    // Always show confirmation when in large-scale mode to inform user they're switching to farm-level
    if (analysisMode === 'large-scale') {
      // Show confirmation dialog before processing upload
      setPendingFarmLevelAction({ type: 'upload', data: geoJsonFeature });
      setShowFarmLevelActionDialog(true);
    } else {
      // Already in field-level mode, process upload immediately
      performFileUpload(geoJsonFeature);
    }
  };

  // Perform the actual file upload processing
  const performFileUpload = (geoJsonFeature: any) => {
    const features: Array<{ type?: string; geometry?: unknown; properties?: Record<string, unknown> }> =
      geoJsonFeature?.type === 'FeatureCollection' && Array.isArray(geoJsonFeature.features)
        ? geoJsonFeature.features
        : geoJsonFeature?.type === 'Feature' || geoJsonFeature?.geometry
          ? [geoJsonFeature]
          : [];

    if (features.length === 0) {
      setToast({
        message: 'No polygon features found in the uploaded file.',
        type: 'error',
      });
      return;
    }

    const reprojected = Boolean(geoJsonFeature?.reprojected);
    const sourceCrs = typeof geoJsonFeature?.source_crs === 'string' ? geoJsonFeature.source_crs : null;
    const collection = {
      type: 'FeatureCollection',
      features: features.map((feature) => ({
        type: 'Feature',
        geometry: feature.geometry ?? feature,
        properties: feature.properties || {},
      })),
    };

    if (analysisMode === 'large-scale') {
      clearAllLargeScaleLayers();
      setAnalysisMode('field-level');
    }

    if (features.length > 1) {
      if (layerControls?.isCropfieldVertexEditActive?.()) {
        layerControls.cancelCropfieldVertexEdit();
      }
      const labels = collection.features.map((feature, index) => polygonFeatureLabel(feature, index));
      const delineationPayload = {
        success: true,
        geojson_wgs84: collection,
        geojson: collection,
        crs: 'EPSG:4326',
        field_count: collection.features.length,
      };
      setAiFieldDelineationOrigin('upload');
      setAiFieldDelineationFieldLabels(labels);
      setAiFieldDelineationData(delineationPayload);
      setAiDelineationSelectedIndex(null);
      setAiDelineationMultiSelect(collection.features.length > 1);
      setAiDelineationCheckedIndexes([]);
      setAiFieldDelineationError('');
      setAiFieldDelineationStep('picking');
      collapseNavForAiFields();
      layerControls?.handleCropfieldDelineationUpdate?.(delineationPayload);
      const crsNote = reprojected && sourceCrs ? ` Converted from ${sourceCrs} to WGS84.` : '';
      setToast({
        message: `${collection.features.length} polygons loaded.${crsNote} Tick blocks or Ctrl+click on the map, save in batches — the rest stay on the map.`,
        type: 'success',
      });
      return;
    }

    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const singleFeature = collection.features[0];
    const uploadedName = polygonFeatureLabel(singleFeature, 0);
    const uploadedField = {
      farm_id: `uploaded_field_${Date.now()}`,
      farm_name: /^\d+$/.test(uploadedName) ? `Uploaded Field (${timestamp})` : uploadedName,
      isDemo: true,
      isUnsaved: true,
      geometry: singleFeature,
    };

    setDrawnField(uploadedField as any);
    setSelectedField(uploadedField as any);
    setFieldGeoJson(singleFeature);

    setAnalysisPanelOpen(true);
    setAnalysisPanelReady(true);

    setAnalysisResults(null);
    setIndexData(null);
    setVhiData(null);
    setIndexHeatMapData(null);
    setTimeSeriesData(null);
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);
    setAutoGptRequest(null);

    const crsNote = reprojected && sourceCrs ? ` Converted from ${sourceCrs} to WGS84.` : '';
    setToast({
      message: `Field uploaded successfully! Ready for analysis.${crsNote}`,
      type: 'success',
    });
  };

  // Handlers from Sidebar
  const handleFieldSelect = async (field) => {
    if (aiFieldDelineationStepRef.current === 'picking') {
      setToast({
        message:
          'Finish or cancel AI field selection first. Opening a saved field clears detected blocks on the map.',
        type: 'info',
      });
      return;
    }
    console.log('Field selected:', field);

    // Show confirmation when large-scale layers are active (regardless of current analysisMode)
    // or when explicitly in large-scale mode with active layers
    await performSwitchToFieldLevel(field);
  };
  const handleFieldSelectRef = useRef(handleFieldSelect);
  handleFieldSelectRef.current = handleFieldSelect;

  const getCurrentMapZoom = useCallback((): number | null => {
    const fromControls = layerControls?.zoomLevel;
    if (typeof fromControls === 'number') return fromControls;
    return getOlMapZoom(layerControls?.getOlMap?.() ?? null);
  }, [layerControls]);

  const resetManualDrawState = useCallback(() => {
    setManualDrawEditing(false);
    setManualDrawRecoverable(false);
    layerControls?.clearManualDraw?.();
  }, [layerControls]);

  // Drawing mode handlers
  const handleDrawingModeToggle = (isDrawing) => {
    if (!isDrawing) {
      const hasPolygon = layerControls?.hasManualDrawPolygon?.() ?? false;
      if (manualDrawEditing || hasPolygon) {
        setIsDrawingMode(false);
        setManualDrawEditing(false);
        setManualDrawRecoverable(hasPolygon);
        console.log('Drawing paused — boundary kept on map for edit');
        return;
      }
      setIsDrawingMode(false);
      setDrawnField(null);
      agLandCheckGenRef.current += 1;
      setAgLandWarning(null);
      resetManualDrawState();
      console.log('Drawing mode ended');
      return;
    }

    const zoomCheck = assertFarmLevelMapZoom(getCurrentMapZoom());
    if (!zoomCheck.ok) {
      setToast({ message: zoomCheck.message, type: 'info' });
      return;
    }

    // If starting drawing mode, check if we need confirmation
    // Always show confirmation when in large-scale mode to inform user they're switching to farm-level
    if (analysisMode === 'large-scale') {
      // Show confirmation dialog before starting drawing
      setPendingFarmLevelAction({ type: 'draw' });
      setShowFarmLevelActionDialog(true);
    } else {
      // Already in field-level mode, start drawing immediately
      performStartDrawingMode();
    }
  };

  // Perform the actual start of drawing mode
  const performStartDrawingMode = () => {
    const zoomCheck = assertFarmLevelMapZoom(getCurrentMapZoom());
    if (!zoomCheck.ok) {
      setToast({ message: zoomCheck.message, type: 'info' });
      return;
    }
    setIsDrawingMode(true);
    setManualDrawEditing(false);
    setManualDrawRecoverable(false);
    layerControls?.clearManualDraw?.();
    // Starting drawing mode — close analysis panel and clear field context until polygon is closed
    setAnalysisPanelOpen(false);
    setAnalysisPanelReady(false);
    setActiveRailIcon(null);
    setSelectedField(null);
    setFieldGeoJson(null);
    setDrawnField(null);
    agLandCheckGenRef.current += 1;
    setAgLandWarning(null);
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);
    stopAllPlayback();
    console.log('Drawing mode started - analysis panel disabled until polygon is closed');
  };

  const performStartAiFieldDelineation = () => {
    setAnalysisPanelOpen(false);
    setAnalysisPanelReady(false);
    stopAllPlayback();
    setAiFieldDelineationStep('instruction');
    setAiFieldDelineationError('');
    setAiFieldDelineationData(null);
    setAiDelineationSelectedIndex(null);
    setAiFieldDelineationInputMode('mapView');
    setAiFieldDelineationGeoTiff(null);
  };

  const resetAiFieldDelineation = useCallback(() => {
    restoreNavAfterAiFields();
    setAiFieldDelineationStep('idle');
    setAiFieldDelineationLoading(false);
    setAiFieldDelineationError('');
    setAiFieldDelineationData(null);
    setAiDelineationSelectedIndex(null);
    setAiDelineationMultiSelect(false);
    setAiDelineationCheckedIndexes([]);
    setAiFieldDelineationInputMode('mapView');
    setAiFieldDelineationGeoTiff(null);
    setAiFieldDelineationOrigin('ai');
    setAiFieldDelineationFieldLabels([]);
  }, [restoreNavAfterAiFields]);

  /** Recover when map layer and sidebar list drift (e.g. saved-field click cleared polygons). */
  useEffect(() => {
    if (aiFieldDelineationStep !== 'picking' || !layerControls) return;
    const live = layerControls.getCropfieldDelineationCount?.() ?? 0;
    const stale = aiFieldDelineationData?.field_count ?? 0;
    if (stale > 0 && live === 0) {
      setToast({
        message:
          'Detected fields were removed from the map. Close AI delineation and run detection again.',
        type: 'warning',
      });
      resetAiFieldDelineation();
      return;
    }
    if (live > 0 && live !== stale) {
      setAiFieldDelineationData((prev: any) => (prev ? { ...prev, field_count: live } : prev));
      setAiDelineationCheckedIndexes((prev) => prev.filter((i) => i >= 0 && i < live));
      if (aiDelineationSelectedIndex != null && aiDelineationSelectedIndex >= live) {
        setAiDelineationSelectedIndex(null);
        layerControls.setCropfieldDelineationSelectedIndex?.(null);
      }
    }
  }, [
    aiFieldDelineationStep,
    aiFieldDelineationData?.field_count,
    aiDelineationSelectedIndex,
    layerControls,
    resetAiFieldDelineation,
    analysisPanelOpen,
  ]);

  const handleAiFieldDelineationOpen = () => {
    if (isDrawingMode || manualDrawEditing || manualDrawRecoverable) {
      setIsDrawingMode(false);
      setDrawnField(null);
      resetManualDrawState();
    }
    if (analysisMode === 'large-scale') {
      setPendingFarmLevelAction({ type: 'aiDelineation' });
      setShowFarmLevelActionDialog(true);
      return;
    }
    performStartAiFieldDelineation();
  };

  const handleAiFieldDelineationClose = () => {
    if (layerControls?.isCropfieldVertexEditActive?.()) {
      layerControls.cancelCropfieldVertexEdit();
    }
    layerControls?.handleCropfieldDelineationUpdate?.(null);
    resetAiFieldDelineation();
  };

  const handleAiFieldDelineationProcess = async () => {
    setAiFieldDelineationLoading(true);
    setAiFieldDelineationError('');
    try {
      let data: { success?: boolean; field_count?: number };

      if (aiFieldDelineationInputMode === 'mapView') {
        const map = layerControls?.getOlMap?.();
        if (!map) {
          setAiFieldDelineationError('Map is not ready. Pan or zoom once and try again.');
          setAiFieldDelineationLoading(false);
          return;
        }
        const zoomCheck = assertFarmLevelMapZoom(getOlMapZoom(map));
        if (!zoomCheck.ok) {
          setAiFieldDelineationError(zoomCheck.message);
          setAiFieldDelineationLoading(false);
          return;
        }
        data = (await cropfieldDelineateFromViewport(map, {
          ...AI_FIELD_YOLO_VIEWPORT_PARAMS,
          model: aiFieldDelineationModel,
        })) as {
          success?: boolean;
          field_count?: number;
        };
      } else {
        if (!aiFieldDelineationGeoTiff) {
          setAiFieldDelineationError('Please select a GeoTIFF file.');
          setAiFieldDelineationLoading(false);
          return;
        }
        const formData = new FormData();
        formData.append('image', aiFieldDelineationGeoTiff);
        formData.append('include_preview', 'false');
        formData.append('tta', 'false');
        formData.append('smooth_epsilon', String(AI_FIELD_YOLO_VIEWPORT_PARAMS.smooth_epsilon));
        formData.append(
          'max_polygon_vertices',
          String(AI_FIELD_YOLO_VIEWPORT_PARAMS.max_polygon_vertices)
        );
        formData.append('model', aiFieldDelineationModel);
        formData.append('tile_size', String(AI_FIELD_YOLO_VIEWPORT_PARAMS.tile_size));
        formData.append('overlap', String(AI_FIELD_YOLO_VIEWPORT_PARAMS.overlap));
        formData.append('resolution', String(AI_FIELD_YOLO_VIEWPORT_PARAMS.resolution));
        formData.append('conf_threshold', String(AI_FIELD_YOLO_VIEWPORT_PARAMS.conf_threshold));

        const response = await fetch(
          DIGISAKA_API_FIELDDELINEATION,
          digisakaAuthRequestInit({
            method: 'POST',
            headers: gptInferenceMultipartAuthHeaders(),
            body: formData,
          })
        );
        notifyInferenceComputeUsage(response);
        if (!response.ok) {
          let msg = `API error: ${response.status}`;
          try {
            const errBody = await response.json();
            if (errBody?.detail) {
              msg =
                typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
            }
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }
        data = await response.json();
      }

      if (data.success) {
        setAiFieldDelineationOrigin('ai');
        setAiFieldDelineationFieldLabels([]);
        setAiFieldDelineationData(data);
        layerControls?.handleCropfieldDelineationUpdate?.(data);
        const count = data.field_count ?? 0;
        setAiDelineationMultiSelect(count > 1);
        setAiDelineationCheckedIndexes([]);
        setAiDelineationSelectedIndex(null);
        setAiFieldDelineationStep('picking');
        collapseNavForAiFields();
        // Auto-trigger Digisaka GPT analysis offer
        setAutoGptRequest({ source: 'cropfieldDelineation', payload: data });
        if (count === 0) {
          setAiFieldDelineationError(
            'No fields detected. Try another map area or a different GeoTIFF.'
          );
        }
      } else {
        setAiFieldDelineationError('Delineation failed. Please try again.');
      }
    } catch (e: unknown) {
      setAiFieldDelineationError(
        e instanceof Error ? e.message : 'Failed to run boundary delineation.'
      );
    } finally {
      setAiFieldDelineationLoading(false);
    }
  };

  const handleSelectDelineationField = (
    index: number,
    modifiers?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }
  ) => {
    if (farmSplitReview && splitReviewStep === 'picking') {
      setSplitSelectedIndex(index);
      layerControls?.setCropfieldDelineationSelectedIndex?.(index);
      return;
    }

    const additive = Boolean(modifiers?.ctrlKey || modifiers?.metaKey);
    const rangeSelect = Boolean(modifiers?.shiftKey);

    if (additive || aiDelineationMultiSelect) {
      if (additive && !aiDelineationMultiSelect) {
        setAiDelineationMultiSelect(true);
        setAiDelineationCheckedIndexes((prev) => {
          const seed =
            aiDelineationSelectedIndex != null && !prev.includes(aiDelineationSelectedIndex)
              ? [aiDelineationSelectedIndex]
              : [...prev];
          if (seed.includes(index)) {
            return seed.filter((i) => i !== index);
          }
          return [...seed, index].sort((a, b) => a - b);
        });
      } else if (rangeSelect && aiDelineationSelectedIndex != null) {
        const start = Math.min(aiDelineationSelectedIndex, index);
        const end = Math.max(aiDelineationSelectedIndex, index);
        const range = Array.from({ length: end - start + 1 }, (_, i) => start + i);
        setAiDelineationMultiSelect(true);
        setAiDelineationCheckedIndexes((prev) =>
          [...new Set([...prev, ...range])].sort((a, b) => a - b)
        );
      } else {
        setAiDelineationCheckedIndexes((prev) => {
          const has = prev.includes(index);
          return has
            ? prev.filter((i) => i !== index)
            : [...prev, index].sort((a, b) => a - b);
        });
      }
      setAiDelineationSelectedIndex(index);
      return;
    }

    if (aiDelineationSelectedIndex === index) {
      setAiDelineationSelectedIndex(null);
      layerControls?.setCropfieldDelineationSelectedIndex?.(null);
      return;
    }

    setAiDelineationSelectedIndex(index);
    layerControls?.setCropfieldDelineationSelectedIndex?.(index);
  };

  const handleClearDelineationSelection = useCallback(() => {
    setAiDelineationCheckedIndexes([]);
    setAiDelineationSelectedIndex(null);
    layerControls?.setCropfieldDelineationSelectedIndex?.(null);
  }, [layerControls]);

  useEffect(() => {
    if (aiFieldDelineationStep !== 'picking') {
      layerControls?.setCropfieldDelineationCheckedIndexes?.([]);
      layerControls?.setCropfieldDelineationMultiSelectMode?.(false);
      return;
    }
    layerControls?.setCropfieldDelineationMultiSelectMode?.(aiDelineationMultiSelect);
    layerControls?.setCropfieldDelineationCheckedIndexes?.(
      aiDelineationMultiSelect ? aiDelineationCheckedIndexes : []
    );
    if (!aiDelineationMultiSelect) {
      layerControls?.setCropfieldDelineationSelectedIndex?.(aiDelineationSelectedIndex);
    } else {
      layerControls?.setCropfieldDelineationSelectedIndex?.(null);
    }
  }, [
    aiFieldDelineationStep,
    aiDelineationMultiSelect,
    aiDelineationCheckedIndexes,
    aiDelineationSelectedIndex,
    layerControls,
  ]);

  const syncDelineationRemainingState = (remainingCount: number) => {
    if (remainingCount <= 0) return;
    setAiDelineationSelectedIndex(null);
    setAiDelineationCheckedIndexes([]);
    setAiDelineationMultiSelect(remainingCount > 1);
    layerControls?.setCropfieldDelineationSelectedIndex?.(null);
    setAiFieldDelineationStep('picking');
    const remaining = layerControls?.getCropfieldDelineationFeatureCollectionGeoJson?.();
    if (remaining?.features?.length) {
      setAiFieldDelineationFieldLabels(
        remaining.features.map((feature, index) => polygonFeatureLabel(feature, index))
      );
    }
    setAiFieldDelineationData((prev: any) =>
      prev
        ? {
            ...prev,
            field_count: remainingCount,
          }
        : prev
    );
  };

  const resolveLiveDelineationCount = () =>
    layerControls?.getCropfieldDelineationCount?.() ?? aiFieldDelineationData?.field_count ?? 0;

  const finalizeDelineationFieldUse = (
    result: { geojson: Record<string, unknown>; remainingCount: number } | null,
    fieldLabel?: string,
    opts?: { openSaveModal?: boolean }
  ) => {
    if (!result) return;
    handleFieldDrawn(result.geojson, fieldLabel, {
      selectOnMap: result.remainingCount === 0,
    });
    if (result.remainingCount > 0) {
      syncDelineationRemainingState(result.remainingCount);
    } else {
      resetAiFieldDelineation();
    }
    if (opts?.openSaveModal) {
      window.setTimeout(() => setShowSaveModal(true), 120);
    }
  };

  const [vertexEditHistory, setVertexEditHistory] = useState({ canUndo: false, canRedo: false });

  useEffect(() => {
    layerControls?.setCropfieldVertexHistoryListener?.((state) => {
      setVertexEditHistory(state);
    });
    return () => layerControls?.setCropfieldVertexHistoryListener?.(null);
  }, [layerControls]);

  const handleStartAdjustDelineationVertices = () => {
    const index =
      aiDelineationSelectedIndex ??
      (aiDelineationCheckedIndexes.length === 1 ? aiDelineationCheckedIndexes[0] : null);
    if (index === null) return;
    const ok = layerControls?.startCropfieldVertexEdit?.(index);
    if (ok) {
      setVertexEditHistory({ canUndo: false, canRedo: false });
      setAiFieldDelineationStep('editing');
    }
  };

  const handleCancelDelineationVertexEdit = () => {
    layerControls?.cancelCropfieldVertexEdit?.();
    setVertexEditHistory({ canUndo: false, canRedo: false });
    setAiFieldDelineationStep('picking');
  };

  const handleUndoVertexEdit = useCallback(() => {
    if (!layerControls?.undoCropfieldVertexEdit?.()) return;
    setVertexEditHistory({
      canUndo: layerControls.canUndoCropfieldVertexEdit?.() ?? false,
      canRedo: layerControls.canRedoCropfieldVertexEdit?.() ?? false,
    });
  }, [layerControls]);

  const handleRedoVertexEdit = useCallback(() => {
    if (!layerControls?.redoCropfieldVertexEdit?.()) return;
    setVertexEditHistory({
      canUndo: layerControls.canUndoCropfieldVertexEdit?.() ?? false,
      canRedo: layerControls.canRedoCropfieldVertexEdit?.() ?? false,
    });
  }, [layerControls]);

  const handleDrawPolygonReady = () => {
    setManualDrawEditing(true);
    setManualDrawRecoverable(false);
  };

  const handleResumeManualDrawEdit = () => {
    if (!layerControls?.hasManualDrawPolygon?.()) return;
    setManualDrawRecoverable(false);
    setManualDrawEditing(true);
    setIsDrawingMode(true);
  };

  const handleDiscardManualDraw = () => {
    resetManualDrawState();
    setIsDrawingMode(false);
    setDrawnField(null);
    // Also clean up update mode if active
    if (isUpdatingMode) {
      setIsUpdatingMode(false);
      setFieldToUpdate(null);
      setConfirmedUpdatedGeometry(null);
    }
  };

  const handleRedrawManualDraw = () => {
    layerControls?.restartManualDraw?.();
    setManualDrawEditing(false);
    setManualDrawRecoverable(false);
    setIsDrawingMode(true);
  };

  const handleDeleteManualDrawBlock = () => {
    const removed = layerControls?.removeSelectedManualDrawBlock?.();
    if (!removed) {
      setToast({ message: 'No block to delete. Shift+click a block first, or draw one.', type: 'info' });
      return;
    }
    const remaining = layerControls?.getManualDrawBlockCount?.() ?? 0;
    if (remaining === 0) {
      setManualDrawEditing(false);
      setManualDrawRecoverable(false);
      setIsDrawingMode(true);
      setToast({ message: 'Block removed. Draw a new block to continue.', type: 'info' });
      return;
    }
    setManualDrawEditing(true);
    setToast({
      message: `Block removed · ${remaining} block${remaining === 1 ? '' : 's'} left`,
      type: 'success',
    });
  };

  const handleConfirmManualDraw = () => {
    // If we are in update-boundary mode, use a dedicated flow
    if (isUpdatingMode && fieldToUpdate) {
      handleConfirmUpdateBoundary();
      return;
    }
    const geoJsonFeature = layerControls?.getManualDrawGeoJson?.();
    if (!geoJsonFeature) return;
    resetManualDrawState();
    handleFieldDrawn(geoJsonFeature);
  };

  /** Dedicated confirm handler for the Update Boundary flow — shows the update modal directly */
  const handleConfirmUpdateBoundary = () => {
    const geoJsonFeature = layerControls?.getManualDrawGeoJson?.();
    if (!geoJsonFeature?.geometry) return;

    console.log('Confirming boundary update with geometry:', geoJsonFeature);

    // Exit drawing/editing mode (keep polygon visible on map)
    setIsDrawingMode(false);
    setManualDrawEditing(false);
    setManualDrawRecoverable(true); // keeps polygon on map

    // Store the confirmed geometry and show the update modal
    setConfirmedUpdatedGeometry(geoJsonFeature);
    setShowUpdateModal(true);
  };

  const handleCancelManualDrawEdit = () => {
    setManualDrawEditing(false);
    setManualDrawRecoverable(layerControls?.hasManualDrawPolygon?.() ?? false);
    setIsDrawingMode(false);
  };

  // Handle field drawn on map (only after user confirms adjusted boundary)
  const handleFieldDrawn = (
    geoJsonFeature: any,
    suggestedName?: string,
    opts?: { selectOnMap?: boolean }
  ) => {
    if (!geoJsonFeature?.geometry) return;

    console.log('Field drawn with geometry:', geoJsonFeature);

    // Generate a more descriptive name with timestamp
    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });

    const demoField = {
      farm_id: 'demo_field',
      farm_name: suggestedName ?? `Drawn Field (${timestamp})`,
      isDemo: true,
      isUnsaved: true,
      geometry: geoJsonFeature
    };

    // Set drawn field state
    setDrawnField(demoField as any);
    setIsDrawingMode(false);
    setAgLandWarning(null);
    const agCheckGen = ++agLandCheckGenRef.current;

    // Draw-time only: soft GEE check (does not block save)
    void (async () => {
      const result = await checkAgriculturalLand(geoJsonFeature);
      if (agCheckGen !== agLandCheckGenRef.current) return;
      if (!result || result.is_agricultural) return;
      const msg =
        result.message ||
        'Outside agricultural area — you can still save this as agricultural area; later you can delete it.';
      setAgLandWarning(msg);
      // After the draw-success toast so it is not overwritten
      window.setTimeout(() => {
        if (agCheckGen !== agLandCheckGenRef.current) return;
        setToast({ message: msg, type: 'info' });
      }, 1600);
    })();

    // Keep the demo field in Field Tools for Save. Skip the map highlight when
    // more AI detections are still being picked — otherwise two shapes light up.
    const selectOnMap = opts?.selectOnMap !== false;
    setTimeout(() => {
      if (selectOnMap) {
        setSelectedField(demoField as any);
        setFieldGeoJson(geoJsonFeature);
      }
      setAnalysisPanelReady(true);
      setAnalysisPanelOpen(false);
      setActiveRailIcon(null);

      setAnalysisResults(null);
      setIndexData(null);
      setVhiData(null);
      setIndexHeatMapData(null);
      setTimeSeriesData(null);
      setIsChangeDetectionMode(false);
      setChangeDetectionData(null);
      setAutoGptRequest(null);

      const blocks = geoJsonFeature?.geometry?.type === 'MultiPolygon'
        ? (geoJsonFeature.geometry.coordinates?.length ?? 0)
        : 1;
      setToast({
        message:
          blocks > 1
            ? `Field ready (${blocks} blocks). Save it from Field Tools, or click it later to open analysis.`
            : 'Field ready. Save it from Field Tools, or click it later to open analysis.',
        type: 'success',
      });
    }, 100);
  };

  const handleUseSelectedDelineationField = () => {
    if (aiDelineationSelectedIndex === null) return;
    const liveCount = resolveLiveDelineationCount();
    if (liveCount <= 0 || aiDelineationSelectedIndex >= liveCount) {
      setToast({
        message: 'That block is no longer on the map. Select it again or re-run AI delineation.',
        type: 'error',
      });
      setAiDelineationSelectedIndex(null);
      layerControls?.setCropfieldDelineationSelectedIndex?.(null);
      return;
    }
    const fieldLabel =
      aiFieldDelineationOrigin === 'upload'
        ? aiFieldDelineationFieldLabels[aiDelineationSelectedIndex] ||
          `Uploaded Field ${aiDelineationSelectedIndex + 1}`
        : `AI Field ${aiDelineationSelectedIndex + 1}`;
    const result = layerControls?.confirmCropfieldDelineationFeature?.(aiDelineationSelectedIndex);
    if (!result?.geojson) {
      setToast({
        message: 'Could not read the selected block. Try selecting it again.',
        type: 'error',
      });
      return;
    }
    finalizeDelineationFieldUse(result, fieldLabel);
  };

  const handleToggleDelineationMultiSelect = () => {
    setAiDelineationMultiSelect((prev) => {
      const next = !prev;
      if (!next) setAiDelineationCheckedIndexes([]);
      else if (aiDelineationSelectedIndex != null) {
        setAiDelineationCheckedIndexes([aiDelineationSelectedIndex]);
      }
      return next;
    });
  };

  const handleSaveAllDelineationAsOneField = () => {
    const count = resolveLiveDelineationCount();
    if (count <= 0) {
      setToast({
        message: 'No detected blocks remain on the map. Run AI delineation again.',
        type: 'error',
      });
      resetAiFieldDelineation();
      return;
    }
    const indices = Array.from({ length: count }, (_, i) => i);
    const result = layerControls?.confirmCropfieldDelineationFeatures?.(indices);
    if (!result?.geojson) {
      setToast({ message: 'Could not collect field polygons to save.', type: 'error' });
      return;
    }
    const rings =
      (result.geojson as { geometry?: { type?: string; coordinates?: unknown[] } }).geometry
        ?.type === 'MultiPolygon'
        ? (
            (result.geojson as { geometry: { coordinates: unknown[] } }).geometry.coordinates ||
            []
          ).length
        : 1;
    const label =
      rings === 1
        ? aiFieldDelineationOrigin === 'upload'
          ? 'Uploaded field'
          : 'AI field'
        : `${aiFieldDelineationOrigin === 'upload' ? 'Uploaded' : 'AI'} field (${rings} blocks)`;
    finalizeDelineationFieldUse(result, label, { openSaveModal: true });
  };

  const handleSaveCheckedDelineationAsOneField = () => {
    const liveCount = resolveLiveDelineationCount();
    if (liveCount <= 0) {
      setToast({
        message: 'No detected blocks remain on the map. Run AI delineation again.',
        type: 'error',
      });
      resetAiFieldDelineation();
      return;
    }
    const checked = [...aiDelineationCheckedIndexes]
      .filter((i) => i >= 0 && i < liveCount)
      .sort((a, b) => a - b);
    if (checked.length === 0) {
      setToast({
        message: 'Selected blocks are no longer on the map. Tick them again in the list or on the map.',
        type: 'error',
      });
      setAiDelineationCheckedIndexes([]);
      return;
    }
    if (checked.length !== aiDelineationCheckedIndexes.length) {
      setAiDelineationCheckedIndexes(checked);
    }
    const result = layerControls?.confirmCropfieldDelineationFeatures?.(checked);
    if (!result?.geojson) {
      setToast({
        message: 'Could not save the selected blocks. Try selecting them again.',
        type: 'error',
      });
      return;
    }
    const geom = (result.geojson as { geometry?: { type?: string; coordinates?: unknown[] } })
      .geometry;
    const rings =
      geom?.type === 'MultiPolygon'
        ? (geom.coordinates || []).length
        : geom?.type === 'Polygon'
          ? 1
          : 0;
    if (rings !== checked.length) {
      setToast({
        message: `Saved ${rings} of ${checked.length} selected blocks — some polygons could not be read.`,
        type: 'info',
      });
    }
    const label =
      rings <= 1
        ? aiFieldDelineationOrigin === 'upload'
          ? 'Uploaded field'
          : 'AI field'
        : `${aiFieldDelineationOrigin === 'upload' ? 'Uploaded' : 'AI'} field (${rings} blocks)`;
    setAiDelineationCheckedIndexes([]);
    finalizeDelineationFieldUse(result, label, { openSaveModal: true });
  };

  const handleSelectAllDelineationBlocks = () => {
    const count =
      aiFieldDelineationData?.field_count ??
      layerControls?.getCropfieldDelineationCount?.() ??
      0;
    if (count <= 0) return;
    setAiDelineationMultiSelect(true);
    setAiDelineationCheckedIndexes(Array.from({ length: count }, (_, i) => i));
  };

  const handleDismissOverlappingSavedDelineations = () => {
    const fc = layerControls?.getCropfieldDelineationFeatureCollectionGeoJson?.();
    const saved = farmsOverviewGeoJson ?? farmGroupGeoJson;
    if (!fc?.features?.length || !saved?.features?.length) {
      setToast({
        message: 'Need both detected blocks and saved fields on this map view.',
        type: 'info',
      });
      return;
    }
    const indices = findDelineationIndicesOverlappingSavedFields(fc, saved);
    if (indices.length === 0) {
      setToast({ message: 'No detections sit on top of your saved fields.', type: 'info' });
      return;
    }
    setDelineationDeleteConfirm({
      indices,
      title: `Remove ${indices.length} detection${indices.length === 1 ? '' : 's'} over saved fields?`,
      body: 'These orange blocks overlap farms you already saved. Removing them keeps only new areas to digitize.',
    });
  };

  const resolveDelineationDeleteTargets = useCallback(
    (explicitIndex?: number): number[] => {
      if (typeof explicitIndex === 'number' && Number.isFinite(explicitIndex) && explicitIndex >= 0) {
        return [explicitIndex];
      }
      if (aiDelineationMultiSelect && aiDelineationCheckedIndexes.length > 0) {
        return [...aiDelineationCheckedIndexes].sort((a, b) => a - b);
      }
      if (aiDelineationSelectedIndex != null && aiDelineationSelectedIndex >= 0) {
        return [aiDelineationSelectedIndex];
      }
      return [];
    },
    [aiDelineationMultiSelect, aiDelineationCheckedIndexes, aiDelineationSelectedIndex]
  );

  const executeDeleteDelineationBlocks = useCallback(
    (indices: number[]) => {
      const unique = [...new Set(indices.filter((i) => Number.isFinite(i) && i >= 0))].sort(
        (a, b) => a - b
      );
      if (unique.length === 0) return;
      const remaining =
        layerControls?.removeCropfieldDelineationFeaturesAt?.(unique) ??
        layerControls?.getCropfieldDelineationCount?.() ??
        0;
      if (remaining <= 0) {
        resetAiFieldDelineation();
        setToast({ message: 'All blocks removed.', type: 'info' });
        return;
      }
      syncDelineationRemainingState(remaining);
      setToast({
        message: `Removed ${unique.length} block${unique.length === 1 ? '' : 's'} · ${remaining} remaining`,
        type: 'success',
      });
    },
    [layerControls, resetAiFieldDelineation]
  );

  const requestDeleteDelineationBlocks = useCallback(
    (explicitIndex?: number) => {
      if (aiFieldDelineationStep !== 'picking') return;
      const targets = resolveDelineationDeleteTargets(explicitIndex);
      if (targets.length === 0) {
        setToast({ message: 'Select one or more blocks to delete.', type: 'info' });
        return;
      }
      setDelineationDeleteConfirm({ indices: targets });
    },
    [aiFieldDelineationStep, resolveDelineationDeleteTargets]
  );

  const confirmDeleteDelineationBlocks = useCallback(() => {
    if (!delineationDeleteConfirm?.indices?.length) {
      setDelineationDeleteConfirm(null);
      return;
    }
    executeDeleteDelineationBlocks(delineationDeleteConfirm.indices);
    setDelineationDeleteConfirm(null);
  }, [delineationDeleteConfirm, executeDeleteDelineationBlocks]);

  useEffect(() => {
    if (!delineationDeleteConfirm) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDelineationDeleteConfirm(null);
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [delineationDeleteConfirm]);

  useEffect(() => {
    const editingBoundary =
      aiFieldDelineationStep === 'editing' ||
      (!!farmSplitReview && splitReviewStep === 'editing');
    if (!editingBoundary) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier) return;
      const key = event.key.toLowerCase();
      if (key === 'z') {
        event.preventDefault();
        if (event.shiftKey) handleRedoVertexEdit();
        else handleUndoVertexEdit();
        return;
      }
      if (key === 'y' && !event.shiftKey) {
        event.preventDefault();
        handleRedoVertexEdit();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    aiFieldDelineationStep,
    farmSplitReview,
    splitReviewStep,
    handleUndoVertexEdit,
    handleRedoVertexEdit,
  ]);

  useEffect(() => {
    if (aiFieldDelineationStep !== 'picking' || delineationDeleteConfirm) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target as HTMLElement | null;
      if (
        target?.closest('input, textarea, select, button, [contenteditable="true"], [role="dialog"]')
      ) {
        return;
      }
      event.preventDefault();
      requestDeleteDelineationBlocks();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [aiFieldDelineationStep, delineationDeleteConfirm, requestDeleteDelineationBlocks]);

  const handleUseCheckedDelineationField = () => {
    if (aiDelineationCheckedIndexes.length !== 1) return;
    const index = aiDelineationCheckedIndexes[0];
    setAiDelineationSelectedIndex(index);
    layerControls?.setCropfieldDelineationSelectedIndex?.(index);
    const fieldLabel =
      aiFieldDelineationOrigin === 'upload'
        ? aiFieldDelineationFieldLabels[index] || `Uploaded Field ${index + 1}`
        : `AI Field ${index + 1}`;
    const result = layerControls?.confirmCropfieldDelineationFeature?.(index);
    setAiDelineationCheckedIndexes([]);
    finalizeDelineationFieldUse(result, fieldLabel);
  };

  const handleRemoveDelineationBlock = (index?: number) => {
    requestDeleteDelineationBlocks(
      typeof index === 'number' && Number.isFinite(index) ? index : undefined
    );
  };

  const handleRemoveCheckedDelineationBlocks = () => {
    requestDeleteDelineationBlocks();
  };

  const handleConfirmDelineationAsField = () => {
    const result = layerControls?.applyCropfieldVertexEditInPlace?.();
    if (!result) {
      setToast({
        message: 'Could not keep the adjusted boundary. Try again.',
        type: 'error',
      });
      return;
    }
    setVertexEditHistory({ canUndo: false, canRedo: false });
    setAiDelineationSelectedIndex(result.featureIndex);
    if (aiDelineationMultiSelect) {
      setAiDelineationCheckedIndexes((prev) =>
        prev.includes(result.featureIndex)
          ? prev
          : [...prev, result.featureIndex].sort((a, b) => a - b)
      );
    }
    setAiFieldDelineationStep('picking');
    setToast({ message: 'Boundary updated.', type: 'success' });
  };

  // Handle analysis panel toggle with validation
  const handleAnalysisPanelToggle = () => {
    if (!analysisPanelReady) {
      console.log('Analysis panel not ready');
      return;
    }
    // Allow toggle even without selected field (for large-scale mode)
    if (analysisMode === 'large-scale' || selectedField) {
      setAnalysisPanelOpen(!analysisPanelOpen);
    }
  };

  /**
   * Sync the legacy `analysisPanelOpen` flag and `analysisMode` → rail's `activeRailIcon`.
   *
   * The many existing handlers that call `setAnalysisPanelOpen(true/false)` (field selection,
   * multi-field analysis, mode switches, etc.) keep working unchanged. Rules:
   *   - Panel closed → clear the rail.
   *   - Panel open in field-level mode → 'farm' (the only valid icon for that mode).
   *   - Panel open in large-scale mode → keep the prior tab if it is still a large-scale tab,
   *     otherwise apply a one-shot remembered tab (set by `handleRailIconClick` when switching
   *     from farm-level), otherwise default to 'forecast'.
   */
  useEffect(() => {
    if (!analysisPanelOpen) {
      setActiveRailIcon(null);
      return;
    }
    setActiveRailIcon((prev) => {
      if (analysisMode === 'large-scale') {
        if (prev && prev !== 'farm') return prev;
        if (pendingRailIconAfterSwitch) {
          const next = pendingRailIconAfterSwitch;
          setPendingRailIconAfterSwitch(null);
          return next;
        }
        return 'forecast';
      }
      // field-level: only 'farm' is valid.
      return 'farm';
    });
  }, [analysisPanelOpen, analysisMode, pendingRailIconAfterSwitch]);

  /**
   * Rail icon click → opens the corresponding floating tool card (and may trigger a mode switch).
   *
   * - Farm icon requires a selected field (single or multi) when already in field-level mode;
   *   from large-scale mode it switches back to field-level and opens the fields sidebar.
   * - Large-scale icon in farm-level mode triggers the existing mode-switch confirmation; the
   *   chosen tab is remembered via `pendingRailIconAfterSwitch` and applied post-switch.
   * - Clicking the currently-active icon closes the panel.
   */
  const openFarmAnalysisFromRail = () => {
    setSidebarOpen(true);
    setActiveRailIcon('farm');

    const hasFieldContext = !!selectedField || isMultiFieldAnalysis;
    if (!hasFieldContext) {
      setToast({
        message: 'Select a field from the left sidebar to open Farm Analysis.',
        type: 'info',
      });
      setAnalysisPanelOpen(false);
      setAnalysisPanelReady(false);
      return;
    }

    setAnalysisPanelOpen(true);
    setAnalysisPanelReady(true);
  };

  const handleRailIconClick = (icon: MapToolRailIcon) => {
    if (activeRailIcon === icon) {
      closeHeaderTool();
      return;
    }

    if (icon === 'farm') {
      if (analysisMode === 'large-scale') {
        clearAllLargeScaleLayers();
        setAnalysisMode('field-level');
        openFarmAnalysisFromRail();
        return;
      }

      openFarmAnalysisFromRail();
      return;
    }

    // Large-scale tab icon
    if (analysisMode === 'large-scale') {
      setActiveRailIcon(icon);
      setAnalysisPanelOpen(true);
      setAnalysisPanelReady(true);
    } else {
      // Currently in farm-level — switch immediately and remember the desired tab.
      setPendingRailIconAfterSwitch(icon);
      handleSwitchToLargeScaleMode();
    }
  };

  /**
   * When the embedded `LayerTogglePanel` programmatically requests a tab change in inline-card
   * mode (e.g. entitlement effect picking the next allowed tab), keep the rail in sync.
   * `null` means "no tab" — we close the panel.
   */
  const handleLargeScaleTabRequestChange = (tab: LayerTogglePanelTabId | null) => {
    if (tab === null) {
      setActiveRailIcon(null);
      setAnalysisPanelOpen(false);
    } else {
      setActiveRailIcon(tab);
    }
  };

  useEffect(() => {
    setCropCalendarFarmerInput(null);
  }, [selectedField?.farm_id]);

  const handleFieldContextMenu = useCallback(
    (fieldProps: Record<string, unknown>) => {
      if (analysisMode !== 'field-level') {
        setToast({
          message: 'Switch to farm-level mode and select a field, then right-click the farm boundary.',
          type: 'info',
        });
        return;
      }
      const farmId = (fieldProps?.farm_id ?? (selectedField as { farm_id?: string } | null)?.farm_id) as string | undefined;
      const isDemo = Boolean(fieldProps?.isDemo ?? (selectedField as { isDemo?: boolean } | null)?.isDemo);
      const farmIdStr = farmId != null ? String(farmId) : '';
      if (
        !farmIdStr ||
        isDemo ||
        farmIdStr === 'demo_field' ||
        farmIdStr.startsWith('uploaded_field_')
      ) {
        setToast({
          message: 'Crop calendar needs a saved farm. Save your field first, then right-click the boundary.',
          type: 'info',
        });
        return;
      }
      setCropCalendarForm(EMPTY_CROP_CALENDAR_FORM);
      setCropCalendarFieldInfo(null);
      setShowCropCalendarInputModal(true);
      const lookupGen = ++cropCalendarLookupGenRef.current;
      setCropCalendarFieldInfoLoading(true);
      void fetchFieldCropInformation(farmIdStr)
        .then((info) => {
          if (lookupGen !== cropCalendarLookupGenRef.current) return;
          setCropCalendarFieldInfo(info);
          setCropCalendarForm(formValuesFromFieldCrop(info));
        })
        .catch((err: unknown) => {
          if (lookupGen !== cropCalendarLookupGenRef.current) return;
          console.error('Error fetching field crop information:', err);
          setCropCalendarFieldInfo(null);
        })
        .finally(() => {
          if (lookupGen !== cropCalendarLookupGenRef.current) return;
          setCropCalendarFieldInfoLoading(false);
        });
    },
    [analysisMode, selectedField]
  );

  const handleCropCalendarInputConfirm = () => {
    if (!isCropCalendarFormReady(cropCalendarForm)) {
      setToast({
        message: 'Please enter crop type, planting method, sowing date, and planting date if transplanted.',
        type: 'error',
      });
      return;
    }
    setCropCalendarFarmerInput(cropCalendarForm);
    setCropCalendarOpenRequest((n) => n + 1);
    setAnalysisPanelOpen(true);
    setAnalysisPanelReady(true);
    setShowCropCalendarInputModal(false);
  };

  const handleCropCalendarInputCancel = () => {
    setShowCropCalendarInputModal(false);
  };

  // Check if any large-scale layers are active
  const hasActiveLargeScaleLayers = (): boolean => {
    if (!layerControls || !layerControls.getCurrentLayerVisibility) return false;
    
    const layerVisibility = layerControls.getCurrentLayerVisibility();
    if (!layerVisibility) return false;

    // Check all large-scale layer categories
    const largeScaleLayers = [
      'yield', 'vhi', 'lst', 'bsri', 'droneNDVI',
      'riceForecast', 'maize', 'sugarCane', 'rice', 'coconut',
      'heatMap', 'timeSeries', 'strawBurn',
      'plantingMethodDry2025', 'plantingMethodDry2024', 'riceGrowthStage', 'irrigationType',
      'cadiDrought',
      'pestRisk',
      'isabelaPhilriceNDVI', 'isabelaPhilriceOrtho', 'caviteSilangNDVI', 'caviteSilangOrtho',
      'treeDetection'
    ];

    return (
      largeScaleLayers.some(layerKey => layerVisibility[layerKey] === true) ||
      !!layerControls.droneSiteActiveLayerId
    );
  };

  // Clear all large-scale layers
  const clearAllLargeScaleLayers = (options?: { keepCropfieldDelineation?: boolean }) => {
    if (!layerControls || !layerControls.handleLayerToggle) return;
    const keepDelineation =
      options?.keepCropfieldDelineation || aiFieldDelineationStepRef.current === 'picking';

    const largeScaleLayers = [
      'yield', 'vhi', 'lst', 'bsri', 'droneNDVI',
      'riceForecast', 'maize', 'sugarCane', 'rice', 'coconut',
      'heatMap', 'timeSeries', 'strawBurn',
      'plantingMethodDry2025', 'plantingMethodDry2024', 'riceGrowthStage', 'irrigationType',
      'cadiDrought',
      'pestRisk',
      'isabelaPhilriceNDVI', 'isabelaPhilriceOrtho', 'caviteSilangNDVI', 'caviteSilangOrtho',
      'treeDetection'
    ];

    largeScaleLayers.forEach(layerKey => {
      layerControls.handleLayerToggle(layerKey, false);
    });
    layerControls.clearDroneSiteLayers?.();

    // Also clear API-based multi-temporal tiles (LST/VHI Philippines)
    if (layerControls.clearMultiTemporalAPITiles) {
      layerControls.clearMultiTemporalAPITiles();
    }

    // Clear map layer data (forecast tiles, straw burn tiles, tree detection pins)
    if (layerControls.handleForecastDataUpdate) layerControls.handleForecastDataUpdate(null);
    if (layerControls.handleStrawBurnDataClear) layerControls.handleStrawBurnDataClear();
    if (layerControls.handleTreeDetectionDataUpdate) layerControls.handleTreeDetectionDataUpdate(null);
    if (!keepDelineation && layerControls.handleCropfieldDelineationUpdate) {
      layerControls.handleCropfieldDelineationUpdate(null);
    }
    if (layerControls.handleCrownSegmentationMapUpdate) layerControls.handleCrownSegmentationMapUpdate(null);

    // Clear bottom result panels
    setForecastResults(null);
    setStrawBurnResults(null);
    setTreeDetectionResults(null);
    setCrownSegmentationResults(null);
    if (!keepDelineation) {
      setCropfieldDelineationResults(null);
    }
    setFloodDamageSelectedData(null);
    setDronePrescriptionPanel(null);
    if (layerControls?.clearDronePrescriptionStats) {
      layerControls.clearDronePrescriptionStats();
    }
    setMultitemporalSelectedProvince('');
    setMultitemporalZonalPanel(null);
    if (layerControls?.clearMultitemporalZonalStats) {
      layerControls.clearMultitemporalZonalStats();
    }
    setShowTreeDetectionModal(false);
    setShowCrownSegmentationModal(false);
    setShowCropfieldDelineationModal(false);

    // Also hide all WMS layers
    hideAllWMSLayers();

    setAutoGptRequest(null);
  };

  function clearFarmNavSection(sectionId: string) {
    cancelInFlightFarmWork();
    if (sectionId === 'heatmap') {
      setIndexHeatMapData(null);
      setIsChangeDetectionMode(false);
      setChangeDetectionData(null);
    }
    if (sectionId === 'timeseries') {
      setTimeSeriesData(null);
      setFilteredTimeSeriesData(null);
    }
    if (sectionId === 'droneData') {
      setDronePrescriptionPanel(null);
      layerControls?.clearDronePrescriptionStats?.();
      layerControls?.clearDroneOrthoIndexMap?.();
    }
  }

  const MULTI_TEMPORAL_NAV_SECTION_IDS = new Set([
    'cadi', 'pest', 'planting', 'growthStage', 'irrigation', 'lst', 'vhi', 'bsri',
  ]);

  function clearLargeScaleNavSection(sectionId: string, layerKeys: string[]) {
    layerKeys.forEach((key) => {
      layerControls?.handleLayerToggle?.(key, false);
    });
    if (sectionId === 'flood') setFloodDamageSelectedData(null);
    if (sectionId === 'straw') layerControls?.handleStrawBurnDataClear?.();
    if (sectionId === 'riceForecast') {
      layerControls?.handleForecastDataUpdate?.(null);
      setForecastResults(null);
    }
    if (sectionId === 'trees') {
      layerControls?.handleTreeDetectionDataUpdate?.(null);
      setTreeDetectionResults(null);
      setShowTreeDetectionModal(false);
    }
    if (sectionId === 'crowns') {
      layerControls?.handleCrownSegmentationMapUpdate?.(null);
      setCrownSegmentationResults(null);
      setShowCrownSegmentationModal(false);
    }
    if (sectionId === 'fields') {
      layerControls?.handleCropfieldDelineationUpdate?.(null);
      setCropfieldDelineationResults(null);
      setShowCropfieldDelineationModal(false);
    }
    if (isDroneProvinceNavId(sectionId)) {
      // Only drop the active drone layer when it belongs to this province's section.
      const activeId: string | null = layerControls?.droneSiteActiveLayerId ?? null;
      const active = activeId ? findDroneLayer(activeId) : null;
      if (active && droneProvinceNavId(active.site.province) === sectionId) {
        layerControls?.clearDroneSiteLayers?.();
      }
    }
    if (sectionId === 'isabela' || sectionId === 'cavite') {
      setDronePrescriptionPanel(null);
      layerControls?.clearDronePrescriptionStats?.();
      layerControls?.clearDroneOrthoIndexMap?.();
    }
    // Multi-temporal layers share API tiles, the province pick and the zonal stats panel.
    if (MULTI_TEMPORAL_NAV_SECTION_IDS.has(sectionId)) {
      layerControls?.clearMultiTemporalAPITiles?.();
      setMultitemporalSelectedProvince('');
      setMultitemporalZonalPanel(null);
      layerControls?.clearMultitemporalZonalStats?.();
    }
  }

  function closeHeaderTool() {
    setPinnedNavSection(null);
    clearAllLargeScaleLayers();
    layerControls?.clearDroneOrthoIndexMap?.();
    clearAllFarmLevelData();
    setAnalysisMode('field-level');
    setSidebarOpen(true);
    setActiveRailIcon(null);
    setAnalysisPanelOpen(false);
    setAnalysisPanelReady(false);
  }

  // Clear all farm-level data
  const clearAllFarmLevelData = () => {
    cancelInFlightFarmWork();
    setAnalysisResults(null);
    setIndexData(null);
    setVhiData(null);
    setIndexHeatMapData(null);
    setTimeSeriesData(null);
    setFilteredTimeSeriesData(null);
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);
    setAutoGptRequest(null);
  };

  const largeScaleNavSections = useMemo(
    () => visibleLargeScaleNavSections(activeRailIcon, layerAllowedEntitlementSet),
    [activeRailIcon, layerAllowedEntitlementSet],
  );
  const droneSampleNavSections = useMemo(
    () =>
      visibleLargeScaleNavSections('droneData', layerAllowedEntitlementSet).map((section) => ({
        id: section.id,
        label: section.label,
        hasInfo: true,
      })),
    [layerAllowedEntitlementSet],
  );
  const headerNavSections: AnalysisHeaderSection[] = droneProcessingOpen
    ? droneSampleNavSections
    : activeRailIcon === 'farm'
      ? farmNavSections
      : largeScaleNavSections.map((section) => ({ id: section.id, label: section.label, hasInfo: true }));
  const headerToolLabel = activeRailIcon
    ? RAIL_TAB_META[activeRailIcon].fullLabel.replace(/\n/g, ' ')
    : '';

  useEffect(() => {
    if (!analysisPanelOpen || !activeRailIcon || activeRailIcon === 'farm') {
      setPinnedNavSection(null);
      return;
    }
    const ids = largeScaleNavSections.map((section) => section.id);
    setPinnedNavSection((current) => (current && ids.includes(current) ? current : (ids[0] ?? null)));
  }, [activeRailIcon, analysisPanelOpen, largeScaleNavSections]);

  const MULTI_TEMPORAL_MAP_KEYS = [
    'yield', 'vhi', 'lst', 'bsri',
    'cadiDrought', 'pestRisk',
    'plantingMethodDry2025', 'plantingMethodDry2024',
    'riceGrowthStage', 'irrigationType',
  ] as const;
  const multiTemporalOpenRef = useRef(false);
  useEffect(() => {
    const open = analysisPanelOpen && activeRailIcon === 'multiTemporal';
    if (open) {
      multiTemporalOpenRef.current = true;
      return;
    }
    if (!multiTemporalOpenRef.current) return;
    const toggle = layerControls?.handleLayerToggle;
    const visibility = layerControls?.getCurrentLayerVisibility?.();
    if (!toggle || !visibility) return;
    multiTemporalOpenRef.current = false;
    MULTI_TEMPORAL_MAP_KEYS.forEach((key) => {
      if (visibility[key]) toggle(key, false);
    });
    layerControls.clearMultiTemporalAPITiles?.();
    layerControls.clearMultitemporalZonalStats?.();
    setMultitemporalSelectedProvince('');
    setMultitemporalZonalPanel(null);
    setAutoGptRequest((current) => (current?.source === 'bsri' ? null : current));
  }, [activeRailIcon, analysisPanelOpen, layerControls]);

  const cropAreaAutoloadRef = useRef(false);
  useEffect(() => {
    if (!analysisPanelOpen || activeRailIcon !== 'cropArea') {
      // This tool does not mount the panel that clears layers on tab change,
      // so the crop raster has to be removed when the tool closes.
      const toggle = layerControls?.handleLayerToggle;
      const visibility = layerControls?.getCurrentLayerVisibility?.();
      if (toggle && visibility) {
        (['maize', 'sugarCane', 'rice', 'coconut'] as const).forEach((key) => {
          if (visibility[key]) toggle(key, false);
        });
      }
      cropAreaAutoloadRef.current = false;
      return;
    }
    if (cropAreaAutoloadRef.current || !layerControls?.handleLayerToggle) return;
    const section = largeScaleNavSections.find((item) => item.id === pinnedNavSection)
      ?? largeScaleNavSections[0];
    if (!section) return;
    cropAreaAutoloadRef.current = true;
    const enable = new Set(layerKeysToEnable(section, layerAllowedEntitlementSet));
    const allKeys = new Set(largeScaleNavSections.flatMap((item) => item.layerKeys));
    allKeys.forEach((key) => {
      layerControls.handleLayerToggle(key, enable.has(key));
    });
  }, [
    activeRailIcon,
    analysisPanelOpen,
    pinnedNavSection,
    layerControls,
    largeScaleNavSections,
    layerAllowedEntitlementSet,
  ]);

  /** The open tool is showing its layers/tabs, which are listed under it in the left menu. */
  const navToolFocused =
    analysisPanelOpen &&
    !!activeRailIcon &&
    (analysisMode === 'large-scale' || !!selectedField || isMultiFieldAnalysis);

  // Layers are listed under the tool in the left menu. If the menu is shrunk to icons, open it
  // (without changing the saved preference) so those layers stay reachable.
  const navHasSubItems = navToolFocused && headerNavSections.length > 0;
  useEffect(() => {
    if (navHasSubItems) setNavMenuExpanded(true);
  }, [navHasSubItems, activeRailIcon]);

  const handleFarmHeaderTabsChange = useCallback((tabs: AnalysisHeaderSection[]) => {
    setFarmNavSections((prev) => {
      if (
        prev.length === tabs.length &&
        prev.every((item, index) => item.id === tabs[index]?.id && item.label === tabs[index]?.label)
      ) {
        return prev;
      }
      return tabs;
    });
  }, []);

  function handleHeaderSectionClick(id: string) {
    setPinnedNavSection(id);
    if (activeRailIcon === 'farm') {
      setFarmTabRequest({ id, n: Date.now() });
      return;
    }
    const section = largeScaleNavSections.find((item) => item.id === id);
    if (!section) return;
    // Opening another layer clears every other layer of this tool first: map layers, result
    // panels and any extra info (zonal stats, drone panel, ...), so nothing stays behind.
    largeScaleNavSections.forEach((item) => {
      if (item.id !== id) clearLargeScaleNavSection(item.id, item.layerKeys);
    });
    const enable = new Set(layerKeysToEnable(section, layerAllowedEntitlementSet));
    const allKeys = new Set(largeScaleNavSections.flatMap((item) => item.layerKeys));
    allKeys.forEach((key) => {
      layerControls?.handleLayerToggle?.(key, enable.has(key));
    });
  }

  function handleHeaderSectionClose() {
    const id = pinnedNavSection;
    setPinnedNavSection(null);
    if (!id) return;
    if (activeRailIcon === 'farm') {
      clearFarmNavSection(id);
      return;
    }
    const section = largeScaleNavSections.find((item) => item.id === id);
    if (!section) return;
    clearLargeScaleNavSection(section.id, section.layerKeys);
  }

  // Handler to switch to large-scale analysis mode
  const handleSwitchToLargeScaleMode = () => {
    performSwitchToLargeScale();
  };

  // Perform the actual switch to large-scale mode
  const performSwitchToLargeScale = () => {
    clearAllFarmLevelData();
    // Clear selected field to deselect it in the sidebar
    setSelectedField(null);
    setFieldGeoJson(null);
    setAnalysisMode('large-scale');
    setAnalysisPanelOpen(true);
    setAnalysisPanelReady(true);
  };

  const handleOpenDroneDataFromField = async (field: any) => {
    setSidebarOpen(true);

    const fieldReady =
      selectedField?.farm_id === field.farm_id && !!fieldGeoJson;

    if (!fieldReady) {
      if (analysisMode === 'large-scale' || hasActiveLargeScaleLayers() || selectedField?.farm_id !== field.farm_id) {
        await performSwitchToFieldLevel(field);
      }
    } else if (analysisMode === 'large-scale') {
      clearAllLargeScaleLayers();
      setAnalysisMode('field-level');
    }

    setActiveRailIcon('farm');
    setAnalysisPanelOpen(true);
    setAnalysisPanelReady(true);
    setDroneDataOpenRequest((n) => n + 1);
  };

  // Focus farm from sidebar: zoom + Update field, but do not open analysis (block click does that).
  const handleFarmFocus = async (field: any) => {
    setIsMultiFieldAnalysis(false);
    setMultiFieldSelection(null);
    clearAllLargeScaleLayers();

    // Whole-farm focus — strip any block selection so only Update/Split show until a block is clicked
    const focusField = {
      ...field,
      block_index: undefined,
      block_number: undefined,
      block_label: undefined,
    };
    cancelInFlightFarmWork();
    setSelectedField(focusField);
    setAnalysisMode('field-level');
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);

    // Keep analysis closed until a block is chosen
    setAnalysisPanelOpen(false);
    setAnalysisPanelReady(true);
    setAnalysisResults(null);
    setIndexData(null);
    setVhiData(null);
    setIndexHeatMapData(null);
    setTimeSeriesData(null);
    setAutoGptRequest(null);
    hideAllWMSLayers();

    if (focusField?.isDemo && focusField.geometry) {
      setFieldGeoJson(focusField.geometry);
    } else if (focusField?.farm_id && !focusField.isDemo) {
      setFieldGeoJson(null);
      await fetchFieldGeoJson(focusField.farm_id, focusField);
    } else {
      setFieldGeoJson(null);
    }
  };

  // Handler to switch to field-level mode (called from field click)
  const performSwitchToFieldLevel = async (field: any) => {
    // Focus-only (sidebar farm click after large-scale confirm)
    if (field?._focusOnly) {
      const { _focusOnly: _, ...rest } = field;
      await handleFarmFocus(rest);
      return;
    }

    // Exit multi-field analysis mode when switching to a single field
    setIsMultiFieldAnalysis(false);
    setMultiFieldSelection(null);
    // Clear large-scale layers first
    clearAllLargeScaleLayers();

    cancelInFlightFarmWork();
    setSelectedField(field);
    setAnalysisMode('field-level');

    // Exit change detection mode when selecting new field
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);

    // Open analysis when the user clicks a block (or map polygon).
    setAnalysisPanelOpen(true);
    setAnalysisPanelReady(true);

    // Clear previous analysis results when selecting new field
    setAnalysisResults(null);
    setIndexData(null);
    setVhiData(null);
    setIndexHeatMapData(null);
    setTimeSeriesData(null);
    setAutoGptRequest(null);

    // Hide all WMS layers when selecting a new field to avoid confusion
    hideAllWMSLayers();

    // Handle geometry for different field types
    if (field && field.isDemo && field.geometry) {
      // For demo/drawn/uploaded fields, use the geometry directly
      console.log('Setting geometry for demo field:', field.geometry);
      setFieldGeoJson(field.geometry);
    } else if (field && field.farm_id && !field.isDemo) {
      // Prefer polygons from webgis farms list (multi-block aware)
      setFieldGeoJson(null);
      await fetchFieldGeoJson(field.farm_id, field);
    } else {
      setFieldGeoJson(null);
    }
  };

  // Handle farm-level action (draw/upload) confirmation
  const handleFarmLevelActionConfirm = () => {
    if (!pendingFarmLevelAction) return;

    setShowFarmLevelActionDialog(false);

    if (pendingFarmLevelAction.type === 'draw') {
      const zoomCheck = assertFarmLevelMapZoom(getCurrentMapZoom());
      if (!zoomCheck.ok) {
        setToast({ message: zoomCheck.message, type: 'info' });
        setPendingFarmLevelAction(null);
        return;
      }
      // Clear large-scale layers and start drawing mode
      if (analysisMode === 'large-scale') {
        clearAllLargeScaleLayers();
        setAnalysisMode('field-level');
      }
      performStartDrawingMode();
    } else if (pendingFarmLevelAction.type === 'upload' && pendingFarmLevelAction.data) {
      // Process the file upload
      performFileUpload(pendingFarmLevelAction.data);
    } else if (pendingFarmLevelAction.type === 'aiDelineation') {
      if (analysisMode === 'large-scale') {
        clearAllLargeScaleLayers();
        setAnalysisMode('field-level');
      }
      performStartAiFieldDelineation();
    }

    setPendingFarmLevelAction(null);
  };

  // Handle farm-level action cancellation
  const handleFarmLevelActionCancel = () => {
    setShowFarmLevelActionDialog(false);
    setPendingFarmLevelAction(null);
  };

  // Handle save demo field
  const handleSaveDemoField = () => {
    setShowSaveModal(true);
  };

  // Handle demo field deletion
  const handleDeleteDemoField = () => {
    cancelInFlightFarmWork();
    setDrawnField(null);
    agLandCheckGenRef.current += 1;
    setAgLandWarning(null);
    setSelectedField(null);
    setFieldGeoJson(null);
    setAnalysisPanelOpen(false);
    setAnalysisPanelReady(false);

    // Clear analysis data and change detection
    setAnalysisResults(null);
    setIndexData(null);
    setVhiData(null);
    setIndexHeatMapData(null);
    setTimeSeriesData(null);
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);
    setAutoGptRequest(null);
  };

  // UPDATED: WMS Layer control handlers - Now ensures only one layer is visible at a time
  // When switching between layers, completely clear the previous layer (reset to first date, stop playback)
  const handleWMSLayerToggle = (layerType: 'yield' | 'vhi' | 'lst') => {
    const currentLayerState = wmsLayerStates[layerType];
    const isCurrentlyVisible = currentLayerState?.visible || false;

    if (isCurrentlyVisible) {
      resetLayerState(layerType);
    } else {
      // ALWAYS clear all other layers (reset to first date, stop playback)
      const otherLayers: ('yield' | 'vhi' | 'lst')[] = ['yield', 'vhi', 'lst'].filter(l => l !== layerType);
      otherLayers.forEach(otherLayerType => {
        resetLayerState(otherLayerType);
      });

      // Then show the target layer
      toggleWMSLayerVisibility(layerType);
    }
  };

  // UPDATED: Handle WMS date change - Now receives date string and converts to index
  const handleWMSDateChange = (date: string, layerType: 'yield' | 'vhi' | 'lst') => {
    // Find the index of this date in our date arrays
    const dateArray = wmsDateArrays[layerType] || [];
    const dateIndex = dateArray.indexOf(date);

    if (dateIndex !== -1) {
      setLayerDateByIndex(layerType, dateIndex);
    }
  };

  const handleWMSPlayToggle = (layerType: 'yield' | 'vhi' | 'lst') => {
    toggleLayerPlayback(layerType);
  };

  // Handle change detection mode toggle
  const handleChangeDetectionToggle = (
    enabled: boolean,
    beforeIndex?: number | null,
    afterIndex?: number | null
  ) => {
    setIsChangeDetectionMode(enabled);

    if (
      enabled &&
      beforeIndex != null &&
      afterIndex != null &&
      beforeIndex !== afterIndex &&
      timeSeriesData?.results
    ) {
      const changeData = {
        before: timeSeriesData.results[beforeIndex],
        after: timeSeriesData.results[afterIndex],
        beforeIndex,
        afterIndex
      };
      setChangeDetectionData(changeData);

      // Stop any playing animations when entering change detection
      if (getPlayingLayers().length > 0) {
        stopAllPlayback();
      }

      console.log('Change detection enabled:', changeData);
    } else {
      setChangeDetectionData(null);
      console.log('Change detection disabled');
    }
  };

  // Updated handler to receive analysis data from AnalysisPanel
  const handleAnalysisRun = async (analysisType, params) => {
    if (!selectedField) {
      console.error('No field selected for analysis');
      return;
    }

    if (!fieldGeoJson) {
      console.error('No geometry available for analysis');
      return;
    }

    console.log('Running analysis:', analysisType, 'with params:', params);
    console.log('Using geometry:', fieldGeoJson);

    setProcessing(true);
    setProcessingType(getProcessingMessage(analysisType, params));

    // Exit change detection mode when running new analysis
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);

    try {
      // Stop any WMS layer playback during analysis
      stopAllPlayback();

      switch (analysisType) {
        case 'heatmap':
          setTimeSeriesData(null);
          if (params?.tileUrl) {
            setIndexHeatMapData(params);
            setAnalysisResults(params);
            setAutoGptRequest({ source: 'heatMap', payload: params });
            console.log('Heat map data set:', params);
          } else {
            // Failed / fully cloud-masked — clear blank layer so users are not misled
            setIndexHeatMapData(null);
            setAnalysisResults(null);
          }
          break;

        case 'timeseries':
          setIndexHeatMapData(null);
          setTimeSeriesData(params);
          setAnalysisResults(params);
          // Final time series payload only: default cloud filter ON at 50% (skip streaming partials)
          if (params.interventionCompare) {
            setShowCloudFilter(false);
            setAutoGptRequest({ source: 'timeSeries', payload: { ...params, farmId: selectedField?.farm_id } });
          } else if (!params._streaming) {
            setMaxCloudCoverage(DEFAULT_MAX_CLOUD_COVERAGE_PERCENT);
            setShowCloudFilter(true);
            setAutoGptRequest({ source: 'timeSeries', payload: { ...params, farmId: selectedField?.farm_id } });
          }
          console.log('Time series data set:', params);
          break;

        case 'burndetection':
          setTimeSeriesData(null);
          setIndexHeatMapData(params);
          setAnalysisResults(params);
          setAutoGptRequest({ source: 'other', payload: params });
          console.log('Burn detection data set:', params);
          break;

        case 'report':
          await handleReportGeneration(params);
          break;

        case 'climate':
          // Field-level climate analysis (ECMWF ERA5) – results live in AnalysisPanel UI,
          // but we still want Digisaka GPT to comment on the numeric climate data + map.
          setAnalysisResults(params);
          setAutoGptRequest({ source: 'climate', payload: { ...params, farmId: selectedField?.farm_id } });
          console.log('Climate data set for GPT analysis:', params);
          break;

        case 'wms_yield':
          handleWMSLayerToggle('yield');
          setAnalysisResults({ type: 'wms_yield', ...params });
          setAutoGptRequest({ source: 'timeSeries', payload: { type: 'wms_yield', ...params, farmId: selectedField?.farm_id } });
          break;

        case 'wms_vhi':
          handleWMSLayerToggle('vhi');
          setAnalysisResults({ type: 'wms_vhi', ...params });
          setAutoGptRequest({ source: 'timeSeries', payload: { type: 'wms_vhi', ...params, farmId: selectedField?.farm_id } });
          break;

        case 'wms_lst':
          handleWMSLayerToggle('lst');
          setAnalysisResults({ type: 'wms_lst', ...params });
          setAutoGptRequest({ source: 'timeSeries', payload: { type: 'wms_lst', ...params, farmId: selectedField?.farm_id } });
          break;

        case 'adjust-heatmap-opacity':
          // This will be handled by MapView component
          setAnalysisResults({ type: 'adjust-heatmap-opacity', ...params });
          break;

        case 'adjust-timeseries-opacity':
          // This will be handled by MapView component
          setAnalysisResults({ type: 'adjust-timeseries-opacity', ...params });
          break;

        default:
          console.warn('Unknown analysis type:', analysisType);
          break;
      }
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setProcessing(false);
      setProcessingType(null);
    }
  };

  // Helper function to get processing message
  const getProcessingMessage = (analysisType, params) => {
    switch (analysisType) {
      case 'heatmap':
        return `Generating ${params.index} Index Map...`;
      case 'timeseries':
        return `Generating ${params.index} Time Series...`;
      case 'report':
        return 'Generating Report...';
      case 'wms_yield':
        return 'Loading Yield Layer...';
      case 'wms_vhi':
        return 'Loading VHI Layer...';
      case 'wms_lst':
        return 'Loading LST Layer...';
      default:
        return `Running ${analysisType} analysis...`;
    }
  };

  const handleTimeSeriesAnalysis = async (params) => {
    try {
      console.log('Time series analysis params:', params);

      const [indexResponse, vhiResponse] = await Promise.all([
        fetch(`https://digisaka.app/api/outbound/field-index/${selectedField.farm_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getDigisakaAuthHeaders() },
          body: JSON.stringify({
            index_type: params.indexType,
            start_date: params.startDate,
            end_date: params.endDate
          })
        }),
        fetch(`https://digisaka.app/api/outbound/field-vhi/${selectedField.farm_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getDigisakaAuthHeaders() },
          body: JSON.stringify({
            start_date: params.startDate,
            end_date: params.endDate
          })
        })
      ]);

      const indexData = await indexResponse.json();
      const vhiData = await vhiResponse.json();

      setIndexData(indexData);
      setVhiData(vhiData);
      setAnalysisResults({ indexData, vhiData });
    } catch (error) {
      console.error('Time series analysis error:', error);
    }
  };

  const handleReportGeneration = async (params) => {
    try {
      const response = await fetch(`https://digisaka.app/api/outbound/fieldreport/${selectedField.farm_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getDigisakaAuthHeaders() },
        body: JSON.stringify({
          report_type: params.reportType,
          analysis_period: params.analysisPeriod
        })
      });
      const data = await response.json();

      if (data.success && data.results) {
        const reportData = {
          type: 'fieldreport',
          success: data.success,
          results: data.results,
          area_ha: data.area_ha,
          crop_name: data.crop_name,
          monitoring_period: data.monitoring_period,
          indices_returned: data.indices_returned,
          recommendations_and_insights: data.recommendations_and_insights,
          mean_ndvi: data.results.mean_ndvi || [],
          mean_evi: data.results.mean_evi || [],
          reportType: params.reportType,
          analysisPeriod: params.analysisPeriod
        };
        setAnalysisResults(reportData);
        setAutoGptRequest({ source: 'cropCalendar', payload: reportData });
      } else {
        setAnalysisResults(data);
        setAutoGptRequest({ source: 'other', payload: data });
      }
    } catch (error) {
      console.error('Report generation error:', error);
    }
  };

  const handleZoomToField = async (fieldId) => {
    if (fieldId) {
      console.log('Zooming to field:', fieldId);

      if (!fieldGeoJson || (selectedField && selectedField.farm_id !== fieldId)) {
        const row =
          selectedField && String(selectedField.farm_id) === String(fieldId)
            ? selectedField
            : undefined;
        const geoJson = await fetchFieldGeoJson(fieldId, row);
        if (geoJson) {
          console.log('GeoJSON available for zoom:', geoJson);
        }
      } else {
        console.log('Using existing GeoJSON for zoom:', fieldGeoJson);
      }
    }
  };

  const handleBackToFields = () => {
    clearAllFarmLevelData();
    setIsMultiFieldAnalysis(false);
    setMultiFieldSelection(null);
    setIsMultiFieldProcessing(false);
    setSelectedField(null);
    setFieldGeoJson(null);
    setAnalysisPanelMinimized(false);
    hideAllWMSLayers();
    setSidebarOpen(true);
    setAnalysisPanelOpen(false);
    setAnalysisPanelReady(false);
  };

  const handleCloseAnalysis = () => {
    cancelInFlightFarmWork();
    setAnalysisResults(null);
    setIndexData(null);
    setVhiData(null);
    setIndexHeatMapData(null);
    setTimeSeriesData(null);
    setIsMultiFieldAnalysis(false);
    setMultiFieldSelection(null);
    setIsMultiFieldProcessing(false);
    setSelectedField(null);
    setFieldGeoJson(null);
    setAnalysisPanelOpen(false);
    setAnalysisPanelReady(false);

    // Clear change detection and hide all WMS layers when closing analysis
    setIsChangeDetectionMode(false);
    setChangeDetectionData(null);
    hideAllWMSLayers();
    setAutoGptRequest(null);

    // Large-scale close: drop tiles + on-map legend (forecast and other overlays)
    if (analysisMode === 'large-scale') {
      clearAllLargeScaleLayers();
    }
  };

  const handleStartMultiFieldAnalysis = (fields: any[]) => {
    if (!fields || fields.length < 2) {
      if (isMultiFieldAnalysis) {
        setIsMultiFieldAnalysis(false);
        setMultiFieldSelection(null);
        setAnalysisPanelOpen(false);
        setAnalysisPanelReady(false);
      }
      return;
    }

    if (isMultiFieldAnalysis) {
      setMultiFieldSelection(fields);
      return;
    }

    // Clear any existing farm-level data
    clearAllFarmLevelData();
    hideAllWMSLayers();

    setIsMultiFieldAnalysis(true);
    setMultiFieldSelection(fields);
    setSelectedField(null);
    setFieldGeoJson(null);
    setAnalysisMode('field-level');
    setAnalysisPanelReady(true);
    setAnalysisPanelOpen(true);
  };

  const handleClearFarmGroupOnMap = useCallback(() => {
    farmGroupMapLoadGenRef.current += 1;
    setFarmGroupGeoJson(null);
  }, []);

  /** After Sidebar loads / filters farms — draw matching blocks on the map. */
  const handleFieldsLoaded = useCallback(
    (rows: any[], options?: { fit?: boolean; groupFocus?: boolean; omitBoundaries?: boolean }) => {
      const list = Array.isArray(rows) ? rows : [];
      const drawable = options?.omitBoundaries
        ? list.map((row) =>
            row && typeof row === 'object'
              ? { ...row, polygons: undefined, geometry: undefined, geojson: undefined, _geometry: undefined }
              : row
          )
        : list;
      const collection = farmRowsToBlockFeatureCollection(drawable);
      const riskById = farmRiskByIdRef.current;
      if (collection.features.length > 0 && Object.keys(riskById).length > 0) {
        collection.features = collection.features.map((f: any) => {
          const farmId = f?.properties?.farm_id;
          if (farmId == null) return f;
          const nextLevel = riskById[String(farmId)]?.risk_level ?? null;
          const level =
            nextLevel === 'high' || nextLevel === 'medium' || nextLevel === 'low'
              ? nextLevel
              : null;
          if (f.properties?.risk_level === level) return f;
          return {
            ...f,
            properties: { ...f.properties, risk_level: level },
          };
        });
      }
      const nextGeo = collection.features.length > 0 ? collection : null;

      if (options?.groupFocus) {
        // Opened group: narrow the group layer only — keep full overview for when group closes.
        setFarmGroupGeoJson(nextGeo ?? { type: 'FeatureCollection', features: [] });
      } else {
        const byId = new Map<string, any>();
        for (const row of list) {
          if (row?.farm_id == null) continue;
          byId.set(String(row.farm_id), row);
        }
        // Replace lookup on full sync (fit) so deleted farms drop; merge on filter-only.
        if (options?.fit) {
          savedFarmsByIdRef.current = byId;
        } else {
          for (const [id, row] of byId) {
            savedFarmsByIdRef.current.set(id, row);
          }
        }
        setFarmsOverviewGeoJson(nextGeo);
      }

      if (options?.fit) {
        setFarmGroupFit(true);
      }
    },
    []
  );

  // Clear one-shot fit after MapView has applied it.
  useEffect(() => {
    if (!farmGroupFit) return;
    const id = window.setTimeout(() => setFarmGroupFit(false), 0);
    return () => window.clearTimeout(id);
  }, [farmGroupFit, farmsOverviewGeoJson, farmGroupGeoJson]);

  // Keep every block of the selected farm on the map when all-boundaries is off.
  useEffect(() => {
    const farmId = selectedField?.farm_id;
    if (farmId == null || selectedField?.isDemo) {
      setSelectedFarmBlocksGeoJson(null);
      return;
    }
    let cancelled = false;
    const apply = (row: Record<string, unknown> | null | undefined) => {
      if (!row) return;
      const collection = farmRowsToBlockFeatureCollection([row]);
      if (!cancelled && collection.features.length > 0) {
        setSelectedFarmBlocksGeoJson(collection);
      }
    };
    const catalog = savedFarmsByIdRef.current.get(String(farmId));
    const row =
      catalog && Array.isArray(catalog.polygons) && catalog.polygons.length > 0
        ? catalog
        : selectedField;
    apply(row);
    const knownCount = Array.isArray(row?.polygons) ? row.polygons.length : 0;
    const listedCount = Number(row?.plot_count) || 0;
    if (knownCount > 1 || (listedCount <= 1 && knownCount === 1)) {
      return () => {
        cancelled = true;
      };
    }
    void resolveFarmGeoJsonWithDetail(row).then((feature) => {
      if (cancelled || !feature?.geometry) return;
      const rings: number[][][] = [];
      const g = feature.geometry as { type?: string; coordinates?: unknown };
      if (g.type === 'Polygon' && Array.isArray(g.coordinates)) {
        rings.push((g.coordinates as unknown[])[0] as number[][]);
      } else if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
        for (const poly of g.coordinates as unknown[]) {
          if (Array.isArray(poly) && poly[0]) rings.push(poly[0] as number[][]);
        }
      }
      const next = {
        ...(catalog || selectedField),
        ...(rings.length ? { polygons: rings } : {}),
      };
      savedFarmsByIdRef.current.set(String(farmId), next);
      apply(next);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedField?.farm_id, selectedField?.isDemo, selectedField?.plot_count]);

  /** Apply risk_level onto overview / group GeoJSON so map pins use risk colors. */
  const handleFarmRiskStatuses = useCallback(
    (byFarmId: Record<string, { risk_level?: string | null }>) => {
      farmRiskByIdRef.current = byFarmId || {};
      const applyRisk = (prev: any | null) => {
        if (!prev?.features?.length) return prev;
        let changed = false;
        const features = prev.features.map((f: any) => {
          const farmId = f?.properties?.farm_id;
          if (farmId == null) return f;
          const nextLevel = byFarmId[String(farmId)]?.risk_level ?? null;
          const level =
            nextLevel === 'high' || nextLevel === 'medium' || nextLevel === 'low'
              ? nextLevel
              : null;
          if (f.properties?.risk_level === level) return f;
          changed = true;
          return {
            ...f,
            properties: {
              ...f.properties,
              risk_level: level,
            },
          };
        });
        if (!changed) return prev;
        return { ...prev, features };
      };
      setFarmsOverviewGeoJson(applyRisk);
      setFarmGroupGeoJson(applyRisk);
    },
    []
  );

  /** Map polygon click → open analysis for that farm block (resolve full list row). */
  const handleMapFarmPolygonClick = useCallback((props: Record<string, unknown>) => {
    if (aiFieldDelineationStepRef.current === 'picking') {
      setToast({
        message: 'You are selecting AI-detected blocks — click those polygons, not saved fields.',
        type: 'info',
      });
      return;
    }
    const farmId = props?.farm_id ?? props?.farmId;
    if (farmId == null || farmId === '') return;
    const row = savedFarmsByIdRef.current.get(String(farmId));
    const rawIdx = props.block_index ?? (props.block_number != null ? Number(props.block_number) - 1 : null);
    const blockIndex =
      rawIdx != null && Number.isFinite(Number(rawIdx)) ? Math.floor(Number(rawIdx)) : null;

    if (row && blockIndex != null && Array.isArray(row.polygons) && row.polygons[blockIndex]) {
      const ring = row.polygons[blockIndex] as number[][];
      void handleFieldSelectRef.current({
        ...row,
        polygons: [ring],
        plot_count: 1,
        block_index: blockIndex,
        block_number: blockIndex + 1,
        block_label: displayBlockContextLabel(row as Record<string, unknown>, blockIndex + 1),
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
      return;
    }

    if (row) {
      void handleFieldSelectRef.current(row);
      return;
    }
    void handleFieldSelectRef.current({
      farm_id: farmId,
      farm_name: props.farm_name ?? props.name,
      farmer_id: props.farmer_id,
      polygons: props.polygons,
      plot_count: props.plot_count,
      block_index: blockIndex,
      block_number: props.block_number,
      block_label: props.block_label,
    });
  }, []);

  /** Load member geometries from webgis farms polygons (multi-block), detail on demand. */
  const handleOpenFarmGroupOnMap = useCallback(
    async (members: Array<{ farm_id: any; farm_name?: string | null }>) => {
      const gen = ++farmGroupMapLoadGenRef.current;
      setSelectedField(null);
      setFieldGeoJson(null);
      setIsMultiFieldAnalysis(false);
      setMultiFieldSelection(null);
      setFarmGroupGeoJson(null);

      if (!members?.length) return;

      let farmsById = new Map<string, Record<string, unknown>>();
      try {
        const list = await fetchSavedFieldsListForUser();
        farmsById = new Map(
          list.map((f) => [String(f.farm_id), f as Record<string, unknown>])
        );
        // Keep click → analysis lookup in sync
        const byId = new Map<string, any>();
        for (const row of list) {
          if (row?.farm_id == null) continue;
          byId.set(String(row.farm_id), row);
        }
        savedFarmsByIdRef.current = byId;
      } catch (err) {
        console.warn('Could not preload webgis farms for group map:', err);
      }

      const rowsForBlocks = members.map((m) => {
        const row = farmsById.get(String(m.farm_id));
        if (row) {
          return {
            ...row,
            farm_name: m.farm_name || row.farm_name || row.name,
          };
        }
        return {
          farm_id: m.farm_id,
          farm_name: m.farm_name,
        };
      });
      const fromList = farmRowsToBlockFeatureCollection(rowsForBlocks);
      if (fromList.features.length > 0) {
        if (gen !== farmGroupMapLoadGenRef.current) return;
        setFarmGroupGeoJson(fromList);
        setFarmGroupFit(true);
        setAnalysisMode('field-level');
        return;
      }

      const settled = await Promise.all(
        members.map(async (m) => {
          const farmId = m.farm_id;
          if (farmId == null || farmId === '') return null;
          const row = farmsById.get(String(farmId));
          const fromRow = resolveFarmGeoJsonFromRow(row || null);
          if (fromRow?.geometry) {
            return {
              type: 'Feature' as const,
              geometry: fromRow.geometry,
              properties: {
                ...(fromRow.properties || {}),
                farm_id: farmId,
                farm_name: displayFarmName({
                  farm_id: farmId,
                  farm_name: m.farm_name ?? fromRow.properties?.farm_name ?? fromRow.properties?.name,
                  farm_unique_id: row?.farm_unique_id ?? fromRow.properties?.farm_unique_id,
                }),
              },
            };
          }
          try {
            const feature = await resolveFarmGeoJsonWithDetail(
              row ?? { farm_id: farmId, farm_name: m.farm_name }
            );
            if (!feature?.geometry) return null;
            return {
              type: 'Feature' as const,
              geometry: feature.geometry,
              properties: {
                ...(feature.properties || {}),
                farm_id: farmId,
                farm_name: displayFarmName({
                  farm_id: farmId,
                  farm_name:
                    m.farm_name ?? feature.properties?.farm_name ?? feature.properties?.name,
                  farm_unique_id:
                    row?.farm_unique_id ?? feature.properties?.farm_unique_id,
                }),
              },
            };
          } catch (err) {
            console.error('Failed to load farm geometry for group map:', farmId, err);
            return null;
          }
        })
      );

      if (gen !== farmGroupMapLoadGenRef.current) return;

      const features = settled.filter(Boolean);
      if (features.length === 0) {
        setToast({
          message: 'Could not load farm boundaries for this group.',
          type: 'error',
        });
        return;
      }

      setFarmGroupGeoJson({
        type: 'FeatureCollection',
        features,
      });
      setFarmGroupFit(true);
      setAnalysisMode('field-level');
    },
    []
  );

  const handleDeleteField = async (fieldId) => {
    // Sidebar already performed the API delete; only clear selection / analysis UI here.
    try {
      if (selectedField && selectedField.farm_id === fieldId) {
        setSelectedField(null);
        setFieldGeoJson(null);
        setAnalysisPanelOpen(false);
        setAnalysisPanelReady(false);
        setAnalysisResults(null);
        setIndexHeatMapData(null);
        setTimeSeriesData(null);
        setIsChangeDetectionMode(false);
        setChangeDetectionData(null);
        setAutoGptRequest(null);
        hideAllWMSLayers();
      }
    } catch (error) {
      console.error('Delete field cleanup error:', error);
    }
  };

  // Handle edit boundary for a saved field
  const handleEditBoundary = async (field: any) => {
    // LGU is monitoring-only — no boundary edits
    if (String(authUser?.role || '').toLowerCase() === 'lgu') return;

    console.log('Edit boundary for field:', field);

    setFieldToUpdate(field);
    setIsUpdatingMode(true);

    try {
      // Prefer polygons from the webgis farms list (all blocks).
      let geometry: any = null;
      const fromRow = resolveFarmGeoJsonFromRow(field);
      if (fromRow?.geometry) {
        geometry = fromRow.geometry;
      } else {
        const detailFeature = await resolveFarmGeoJsonWithDetail(field);
        geometry = detailFeature?.geometry ?? null;
      }

      const hasCoords =
        geometry &&
        (geometry.type === 'FeatureCollection'
          ? geometry.features?.some((f: { geometry?: { coordinates?: unknown } }) => f?.geometry?.coordinates)
          : geometry.coordinates);

      if (hasCoords) {
        let editGeometry = geometry;
        let wasBuffered = false;
        if (geometry.type !== 'FeatureCollection' && isDegenerateOrPointGeometry(geometry)) {
          editGeometry = bufferPointToPolygon(geometry);
          wasBuffered = true;
          console.log(
            `Auto-buffered ${geometry.type} [${JSON.stringify(geometry.coordinates)}] → Polygon for editing (${formatBufferAreaMessage()})`
          );
        }

        setIsDrawingMode(true);
        setManualDrawEditing(true);
        setManualDrawRecoverable(false);

        if (editGeometry?.type === 'FeatureCollection') {
          const rings: number[][][] = [];
          for (const f of editGeometry.features || []) {
            rings.push(...geoJsonGeometryToPolygonRings(f?.geometry));
          }
          editOriginalRingsRef.current = rings;
        } else {
          editOriginalRingsRef.current = geoJsonGeometryToPolygonRings(editGeometry);
        }

        layerControls?.loadGeometryForEdit?.(editGeometry);

        const blockCount =
          editGeometry?.type === 'MultiPolygon'
            ? editGeometry.coordinates?.length ?? 0
            : editOriginalRingsRef.current.length || 1;

        setToast({
          message: wasBuffered
            ? `This field only had a centre point. We buffered it into a ${formatBufferAreaMessage()} rectangle — drag vertices to adjust, draw more blocks, or delete a block, then click "Update the field".`
            : blockCount > 1
              ? `${blockCount} blocks loaded. Drag vertices, draw more, or delete a block — then click "Update the field".`
              : 'Drag vertices, draw more blocks, or delete a block — then click "Update the field" to save.',
          type: 'info'
        });
      } else {
        setToast({
          message: 'This field does not have boundary data to edit.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching field geometry for boundary edit:', error);
      setToast({
        message: 'Failed to load field geometry for editing.',
        type: 'error'
      });
    }
  };

  /**
   * Split a farm block (or whole farm): zoom, run AI delineation, then save pieces
   * as additional blocks on the same farm (block1, block2, …).
   */
  const handleSplitFarm = async (field: any) => {
    // LGU is monitoring-only — no split
    if (String(authUser?.role || '').toLowerCase() === 'lgu') return;
    if (isSplittingFarm || isSavingFarmSplit || farmSplitReview) return;

    const map = layerControls?.getOlMap?.();
    if (!map) {
      setToast({ message: 'Map is not ready. Pan or zoom once and try again.', type: 'error' });
      return;
    }

    setIsSplittingFarm(true);
    setFarmSplitReview(null);
    setAnalysisPanelOpen(false);
    setToast({
      message: 'Splitting block: zooming to boundary and running AI field delineation…',
      type: 'info',
    });

    try {
      // Close any in-progress AI delineation / draw flows
      if (layerControls?.isCropfieldVertexEditActive?.()) {
        layerControls.cancelCropfieldVertexEdit();
      }
      layerControls?.handleCropfieldDelineationUpdate?.(null);
      resetAiFieldDelineation();
      if (isDrawingMode || manualDrawEditing || manualDrawRecoverable) {
        setIsDrawingMode(false);
        setDrawnField(null);
        resetManualDrawState();
      }

      let listRow = savedFarmsByIdRef.current.get(String(field.farm_id)) || field;

      if (field.farm_id != null && field.farm_id !== '') {
        const haveFarmerId =
          parseExplorerFarmerId(field.farmer_id) ?? parseExplorerFarmerId(listRow?.farmer_id);
        const haveStoredPolys =
          (Array.isArray(field._allPolygons) && field._allPolygons.length > 0) ||
          (Array.isArray(listRow?.polygons) && listRow.polygons.length > 0);
        if (haveFarmerId == null || !haveStoredPolys) {
          try {
            const detail = await fetchFarmDetailViaDigisakaApi(field.farm_id);
            if (detail) {
              listRow = { ...listRow, ...detail, ...field };
              savedFarmsByIdRef.current.set(String(field.farm_id), listRow);
            }
          } catch (detailErr) {
            console.warn('Could not load farm detail before split:', detailErr);
          }
        }
      }

      let allPolygons: number[][][] = [];
      if (Array.isArray(field._allPolygons) && field._allPolygons.length > 0) {
        allPolygons = field._allPolygons;
      } else if (Array.isArray(listRow.polygons) && listRow.polygons.length > 0) {
        allPolygons = listRow.polygons;
      } else {
        const fromList = resolveFarmGeoJsonFromRow(listRow);
        if (fromList?.geometry) {
          allPolygons = geoJsonGeometryToPolygonRings(fromList.geometry);
        }
      }

      const sourceBlockIndex =
        field.block_index != null && Number.isFinite(Number(field.block_index))
          ? Math.floor(Number(field.block_index))
          : allPolygons.length === 1
            ? 0
            : null;

      let geometry: any = null;
      if (
        sourceBlockIndex != null &&
        Array.isArray(allPolygons[sourceBlockIndex]) &&
        allPolygons[sourceBlockIndex].length >= 3
      ) {
        geometry = {
          type: 'Polygon',
          coordinates: [allPolygons[sourceBlockIndex]],
        };
      } else {
        const fromRow = resolveFarmGeoJsonFromRow(field);
        geometry = fromRow?.geometry ?? null;
      }

      let propsFromGeometry: Record<string, unknown> = {
        farmer_id: field.farmer_id,
        farm_id: field.farm_id,
      };

      if (!geometry?.coordinates) {
        const detailFeature = await resolveFarmGeoJsonWithDetail(listRow ?? field);
        if (detailFeature?.geometry) {
          geometry = detailFeature.geometry;
          propsFromGeometry = detailFeature.properties || propsFromGeometry;
        }

        const parentFarmerIdFromGeom = parseExplorerFarmerId(propsFromGeometry.farmer_id);
        if (parentFarmerIdFromGeom != null) {
          propsFromGeometry = { ...propsFromGeometry, farmer_id: parentFarmerIdFromGeom };
        }
      }

      if (!geometry?.coordinates) {
        setToast({ message: 'This field does not have boundary data to split.', type: 'error' });
        return;
      }

      const parentFarmerId =
        parseExplorerFarmerId(field.farmer_id) ??
        parseExplorerFarmerId(listRow?.farmer_id) ??
        parseExplorerFarmerId(propsFromGeometry.farmer_id);

      if (parentFarmerId == null && (field.farm_id == null || field.farm_id === '')) {
        setToast({
          message: 'This field has no farm id, so split blocks cannot be saved.',
          type: 'error',
        });
        return;
      }

      if (isDegenerateOrPointGeometry(geometry)) {
        geometry = bufferPointToPolygon(geometry);
      }

      const parentPolygons =
        allPolygons.length > 0 ? allPolygons : geoJsonGeometryToPolygonRings(geometry);

      const geoJsonFormat = new GeoJSON();
      const parentFeature = geoJsonFormat.readFeature(
        { type: 'Feature', geometry },
        {
          dataProjection: 'EPSG:4326',
          featureProjection: map.getView().getProjection(),
        }
      );
      const parentGeom = parentFeature.getGeometry();
      if (!parentGeom) {
        setToast({ message: 'Could not read farm geometry for splitting.', type: 'error' });
        return;
      }

      const extent = parentGeom.getExtent();
      await new Promise<void>((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        map.once('moveend', done);
        map.getView().fit(extent, {
          padding: [24, 24, 24, 24],
          maxZoom: 19,
          duration: 450,
        });
        setTimeout(done, 700);
      });

      await new Promise((r) => setTimeout(r, 1200));

      const zoomCheck = assertFarmLevelMapZoom(getOlMapZoom(map));
      if (!zoomCheck.ok) {
        setToast({ message: zoomCheck.message, type: 'error' });
        return;
      }

      setToast({
        message: 'Running AI field delineation on farm extent…',
        type: 'info',
      });

      const data = (await cropfieldDelineateFromViewport(map, {
        ...AI_FIELD_YOLO_VIEWPORT_PARAMS,
        model: aiFieldDelineationModel,
      })) as {
        success?: boolean;
        field_count?: number;
        geojson_wgs84?: { type?: string; features?: any[] };
        geojson?: { type?: string; features?: any[] };
        crs?: string;
        message?: string;
      };

      if (!data?.success) {
        setToast({
          message: data?.message || 'AI field delineation failed. Please try again.',
          type: 'error',
        });
        return;
      }

      const fc = data.geojson_wgs84 ?? data.geojson;
      const features = Array.isArray(fc?.features) ? fc.features : [];
      if (features.length === 0) {
        setToast({
          message: 'No fields detected inside this farm extent. Try zooming closer or adjusting the boundary.',
          type: 'error',
        });
        return;
      }

      const clippedFeatures = clipFeaturesToParentPolygon(features, geometry, {
        minAreaM2: 50,
        minOverlapRatio: 0.2,
      });

      if (clippedFeatures.length === 0) {
        layerControls?.handleCropfieldDelineationUpdate?.(null);
        setToast({
          message:
            'AI detected fields only outside this farm boundary. Try a closer zoom or adjust the farm boundary.',
          type: 'error',
        });
        return;
      }

      const clippedFc = {
        type: 'FeatureCollection',
        features: clippedFeatures,
      };
      layerControls?.handleCropfieldDelineationUpdate?.({
        ...data,
        field_count: clippedFeatures.length,
        geojson_wgs84: clippedFc,
        geojson: clippedFc,
        crs: 'EPSG:4326',
      });

      const baseName = String(field.farm_name || field.name || `farm-${field.farm_id}`)
        .trim()
        .replace(/\s+/g, '_');

      const pickAdmin = (...vals: unknown[]) => {
        for (const v of vals) {
          if (v == null) continue;
          const s = String(v).trim();
          if (s) return s;
        }
        return '';
      };

      let parentAdmin = {
        region: pickAdmin(
          field.region,
          field.region_id,
          field.region_code,
          propsFromGeometry.region,
          propsFromGeometry.region_id,
          propsFromGeometry.region_code
        ),
        province: pickAdmin(
          field.province,
          field.province_id,
          field.province_code,
          propsFromGeometry.province,
          propsFromGeometry.province_id,
          propsFromGeometry.province_code
        ),
        municipality: pickAdmin(
          field.municipality,
          field.municipality_id,
          field.city,
          field.city_id,
          field.city_code,
          propsFromGeometry.municipality,
          propsFromGeometry.municipality_id,
          propsFromGeometry.city,
          propsFromGeometry.city_code
        ),
        barangay: pickAdmin(
          field.barangay,
          field.barangay_id,
          field.barangay_code,
          propsFromGeometry.barangay,
          propsFromGeometry.barangay_id,
          propsFromGeometry.barangay_code
        ),
      };

      if (
        !parentAdmin.region ||
        !parentAdmin.province ||
        !parentAdmin.municipality ||
        !parentAdmin.barangay
      ) {
        const parentLonLat =
          getPolygonInteriorLonLat({ geometry }) || getPolygonCentroid({ geometry });
        if (parentLonLat) {
          const loc = await fetchCentroidLocation(parentLonLat[0], parentLonLat[1]);
          if (loc) {
            const codes = adminPcodesToDigisakaCodes(loc.adminPcodes);
            parentAdmin = {
              region: parentAdmin.region || codes.region,
              province: parentAdmin.province || codes.province,
              municipality: parentAdmin.municipality || codes.municipality,
              barangay: parentAdmin.barangay || codes.barangay,
            };
          }
        }
      }

      if (
        !parentAdmin.region ||
        !parentAdmin.province ||
        !parentAdmin.municipality ||
        !parentAdmin.barangay
      ) {
        setToast({
          message:
            'Could not resolve region / province / municipality / barangay for this farm. Subplots cannot be saved yet.',
          type: 'error',
        });
        return;
      }

      setFarmSplitReview({
        parentField: { ...listRow, ...field, farmer_id: parentFarmerId },
        farmer_id: parentFarmerId,
        features: clippedFeatures,
        parentAdmin,
        baseName,
        parentPolygons,
        sourceBlockIndex,
        farmId: field.farm_id,
      });
      setSplitReviewStep('picking');
      setSplitSelectedIndex(null);
      setSidebarOpen(true);
      setToast({
        message: `Detected ${clippedFeatures.length} piece${clippedFeatures.length === 1 ? '' : 's'}. Save as new blocks on this field (Block 1, Block 2, …).`,
        type: 'info',
      });
    } catch (error: unknown) {
      console.error('Split farm error:', error);
      setToast({
        message: error instanceof Error ? error.message : 'Failed to split farm.',
        type: 'error',
      });
    } finally {
      setIsSplittingFarm(false);
    }
  };

  const syncSplitReviewFeaturesFromMap = () => {
    const fc = layerControls?.getCropfieldDelineationFeatureCollectionGeoJson?.();
    const features = Array.isArray(fc?.features) ? fc.features : null;
    if (!features) return null;
    setFarmSplitReview((prev) =>
      prev
        ? {
            ...prev,
            features: features as Array<{
              type: string;
              geometry: { type: string; coordinates: any };
              properties?: any;
            }>,
          }
        : prev
    );
    return features;
  };

  const handleSelectSplitSubplot = (index: number) => {
    if (!farmSplitReview || splitReviewStep !== 'picking' || isSavingFarmSplit) return;
    setSplitSelectedIndex(index);
    layerControls?.setCropfieldDelineationSelectedIndex?.(index);
  };

  const handleStartAdjustSplitVertices = () => {
    if (!farmSplitReview || splitSelectedIndex === null || isSavingFarmSplit) return;
    const ok = layerControls?.startCropfieldVertexEdit?.(splitSelectedIndex);
    if (!ok) {
      setToast({ message: 'Could not start vertex editing for that subplot.', type: 'error' });
      return;
    }
    setVertexEditHistory({ canUndo: false, canRedo: false });
    setSplitReviewStep('editing');
  };

  const handleCancelSplitVertexEdit = () => {
    if (layerControls?.isCropfieldVertexEditActive?.()) {
      layerControls.cancelCropfieldVertexEdit();
    }
    setVertexEditHistory({ canUndo: false, canRedo: false });
    setSplitReviewStep('picking');
    if (splitSelectedIndex !== null) {
      layerControls?.setCropfieldDelineationSelectedIndex?.(splitSelectedIndex);
    }
  };

  const handleConfirmSplitVertexEdit = () => {
    const result = layerControls?.applyCropfieldVertexEditInPlace?.();
    if (!result) {
      setToast({ message: 'Could not apply boundary edits.', type: 'error' });
      return;
    }
    syncSplitReviewFeaturesFromMap();
    setSplitSelectedIndex(result.featureIndex);
    setSplitReviewStep('picking');
    setToast({ message: 'Subplot boundary updated.', type: 'success' });
  };

  const handleRemoveSplitSubplot = () => {
    if (!farmSplitReview || splitSelectedIndex === null || isSavingFarmSplit) return;
    const remaining = layerControls?.removeCropfieldDelineationFeatureAt?.(splitSelectedIndex) ?? 0;
    if (remaining <= 0) {
      layerControls?.handleCropfieldDelineationUpdate?.(null);
      setFarmSplitReview(null);
      setSplitSelectedIndex(null);
      setSplitReviewStep('picking');
      setToast({ message: 'All subplots removed — split cancelled.', type: 'info' });
      return;
    }
    syncSplitReviewFeaturesFromMap();
    setSplitSelectedIndex(null);
    layerControls?.setCropfieldDelineationSelectedIndex?.(null);
    setToast({
      message: `Removed subplot. ${remaining} remaining.`,
      type: 'info',
    });
  };

  const handleCancelFarmSplit = () => {
    if (isSavingFarmSplit) return;
    if (layerControls?.isCropfieldVertexEditActive?.()) {
      layerControls.cancelCropfieldVertexEdit();
    }
    layerControls?.handleCropfieldDelineationUpdate?.(null);
    setFarmSplitReview(null);
    setSplitSelectedIndex(null);
    setSplitReviewStep('picking');
    setToast({ message: 'Split discarded — nothing was saved.', type: 'info' });
  };

  const saveSplitFeatures = async (
    features: Array<{ type?: string; geometry?: { type?: string; coordinates?: any }; properties?: any }>,
    opts: {
      clearAfter: boolean;
      /** When saving one selected subplot, use this 0-based index for naming. */
      selectedNameIndex?: number;
    }
  ): Promise<number> => {
    if (!farmSplitReview || isSavingFarmSplit) return 0;
    const { parentField, parentAdmin, baseName, parentPolygons, sourceBlockIndex } = farmSplitReview;
    let farmId = farmSplitReview.farmId ?? parentField?.farm_id;
    const parentFarmerId =
      parseExplorerFarmerId(farmSplitReview.farmer_id) ??
      parseExplorerFarmerId(parentField?.farmer_id);
    const toSave = features.filter((f) => f?.geometry?.coordinates);
    if (toSave.length === 0) {
      setToast({ message: 'No block geometry to save.', type: 'error' });
      return 0;
    }

    const newRings: number[][][] = [];
    for (const feat of toSave) {
      const rings = geoJsonGeometryToPolygonRings(feat.geometry);
      if (rings.length > 0) newRings.push(...rings);
    }
    if (newRings.length === 0) {
      setToast({ message: 'No valid polygon rings to save as blocks.', type: 'error' });
      return 0;
    }

    // Replace the split source block with the new pieces (or append if no source index).
    let nextPolygons: number[][][];
    if (
      sourceBlockIndex != null &&
      sourceBlockIndex >= 0 &&
      sourceBlockIndex < parentPolygons.length
    ) {
      nextPolygons = [
        ...parentPolygons.slice(0, sourceBlockIndex),
        ...newRings,
        ...parentPolygons.slice(sourceBlockIndex + 1),
      ];
    } else {
      nextPolygons = [...parentPolygons, ...newRings];
    }

    setIsSavingFarmSplit(true);
    setToast({
      message: `Saving ${newRings.length} block${newRings.length === 1 ? '' : 's'} on this farm…`,
      type: 'info',
    });

    try {
      const areaHa = nextPolygons.reduce((sum, ring) => {
        const a = calculatePolygonAreaHectares({
          geometry: { type: 'Polygon', coordinates: [ring] },
        });
        return sum + (a > 0 ? a : 0);
      }, 0);

      if (farmId != null && farmId !== '') {
        await updateFarmViaDigisakaApi(farmId, {
          polygons: nextPolygons,
          polygon_mode: 'replace',
          total_land_area: areaHa > 0 ? Number(areaHa.toFixed(2)) : 0,
          ...(parentFarmerId != null ? { farmer_id: parentFarmerId } : {}),
        });
      } else {
        if (parentFarmerId == null) {
          setToast({
            message:
              'Cannot save split blocks: farmer_id is required when creating a new farm record.',
            type: 'error',
          });
          return 0;
        }
        await createFarmsViaDigisakaApi({
          farms: [
            {
              name: String(parentField?.farm_name || parentField?.name || baseName).trim() || baseName,
              farmer_id: parentFarmerId,
              region: parentAdmin.region,
              province: parentAdmin.province,
              municipality: parentAdmin.municipality,
              barangay: parentAdmin.barangay,
              total_land_area: areaHa > 0 ? Number(areaHa.toFixed(2)) : 0,
              polygons: nextPolygons,
            },
          ],
        });
      }

      let newFarmId: string | number | null = farmId ?? null;
      if (newFarmId != null) {
        const byId = new Map(savedFarmsByIdRef.current);
        const prev = byId.get(String(newFarmId)) || parentField || {};
        byId.set(String(newFarmId), {
          ...prev,
          farm_id: newFarmId,
          polygons: nextPolygons,
          plot_count: nextPolygons.length,
        });
        savedFarmsByIdRef.current = byId;
      } else {
        try {
          const list = await fetchSavedFieldsListForUser();
          const match = list.find(
            (r) =>
              String(r.farm_name || r.name || '') ===
                String(parentField?.farm_name || parentField?.name || baseName) &&
              Array.isArray(r.polygons) &&
              r.polygons.length === nextPolygons.length
          );
          newFarmId = match?.farm_id ?? null;
          if (newFarmId != null) {
            const byId = new Map(savedFarmsByIdRef.current);
            byId.set(String(newFarmId), match);
            savedFarmsByIdRef.current = byId;
          }
        } catch {
          /* reload below will refresh */
        }
      }

      if (opts.clearAfter) {
        if (layerControls?.isCropfieldVertexEditActive?.()) {
          layerControls.cancelCropfieldVertexEdit();
        }
        layerControls?.handleCropfieldDelineationUpdate?.(null);
        setFarmSplitReview(null);
        setSplitSelectedIndex(null);
        setSplitReviewStep('picking');
      } else {
        // Keep review open for more pieces: source already replaced; further saves append.
        setFarmSplitReview((prev) =>
          prev
            ? {
                ...prev,
                parentPolygons: nextPolygons,
                sourceBlockIndex: null,
                farmId: newFarmId ?? prev.farmId,
                parentField: {
                  ...prev.parentField,
                  farm_id: newFarmId ?? prev.farmId,
                  polygons: nextPolygons,
                  plot_count: nextPolygons.length,
                },
              }
            : prev
        );
      }

      setReloadFields(true);
      setToast({
        message: `Saved ${newRings.length} block${newRings.length === 1 ? '' : 's'} on the farm (now ${nextPolygons.length} total).`,
        type: 'success',
      });
      return newRings.length;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save split blocks.';
      console.error('Split block save error:', err);
      setToast({ message: msg, type: 'error' });
      return 0;
    } finally {
      setIsSavingFarmSplit(false);
    }
  };

  const handleSaveFarmSplit = async () => {
    if (!farmSplitReview || isSavingFarmSplit || splitReviewStep === 'editing') return;
    const live = syncSplitReviewFeaturesFromMap();
    const features = live || farmSplitReview.features;
    await saveSplitFeatures(features, { clearAfter: true });
  };

  const handleSaveSelectedSplitSubplot = async () => {
    if (!farmSplitReview || splitSelectedIndex === null || isSavingFarmSplit || splitReviewStep === 'editing') {
      return;
    }
    const selectedIndex = splitSelectedIndex;
    const live = syncSplitReviewFeaturesFromMap();
    const features = live || farmSplitReview.features;
    const selected = features[selectedIndex];
    if (!selected?.geometry?.coordinates) {
      setToast({ message: 'Selected piece has no geometry to save as a block.', type: 'error' });
      return;
    }

    const savedCount = await saveSplitFeatures([selected], {
      clearAfter: false,
      selectedNameIndex: selectedIndex,
    });
    if (savedCount <= 0) return;

    const remaining =
      layerControls?.removeCropfieldDelineationFeatureAt?.(selectedIndex) ?? 0;
    if (remaining <= 0) {
      layerControls?.handleCropfieldDelineationUpdate?.(null);
      setFarmSplitReview(null);
      setSplitSelectedIndex(null);
      setSplitReviewStep('picking');
      return;
    }
    syncSplitReviewFeaturesFromMap();
    setSplitSelectedIndex(null);
    layerControls?.setCropfieldDelineationSelectedIndex?.(null);
  };

  const handleTimeSeriesSliderChange = (imageData) => {
    console.log('Time series slider changed:', imageData);
    if (imageData?.date) {
      setExternalTimeSeriesDate(imageData.date);
    }
  };

  // Handle successful field save from SaveFieldModal
  const handleFieldSaved = (savedFieldData) => {
    console.log('Field saved successfully:', savedFieldData);

    // Clear the drawn field and close modal
    setDrawnField(null);
    agLandCheckGenRef.current += 1;
    setAgLandWarning(null);
    setShowSaveModal(false);

    // Trigger field reload to show the newly saved field
    setReloadFields(true);

    const remaining = layerControls?.getCropfieldDelineationCount?.() ?? 0;
    if (remaining > 0) {
      syncDelineationRemainingState(remaining);
      setToast({
        message: `Field saved! ${remaining} more detected field${remaining === 1 ? '' : 's'} on the map — click one to save.`,
        type: 'success',
      });
    } else {
      console.log('Field has been saved to the database');
    }
  };

  // Handle fields reloaded callback
  const handleFieldsReloaded = () => {
    setReloadFields(false);
    console.log('Fields have been reloaded');
  };

  // Handle closing the save modal
  const handleCloseSaveModal = () => {
    setShowSaveModal(false);
  };

  // Handle update field boundary
  const handleUpdateFieldBoundary = async (fieldId: string | number, payload: any) => {
    /**
     * digisaka.app PUT /webgis/farms/{id} **appends** any ``polygons`` it receives —
     * it does not replace or reshape existing blocks. So:
     * - metadata-only / unchanged rings → PUT without polygons
     * - brand-new blocks only → PUT with just those new rings (append)
     * - edited or removed blocks → create a new farm with the full geometry, then delete the old one
     */
    const ringsApproxEqual = (a: number[][], b: number[][], eps = 1e-6): boolean => {
      if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        const pa = a[i];
        const pb = b[i];
        if (!pa || !pb || pa.length < 2 || pb.length < 2) return false;
        if (Math.abs(Number(pa[0]) - Number(pb[0])) > eps) return false;
        if (Math.abs(Number(pa[1]) - Number(pb[1])) > eps) return false;
      }
      return true;
    };

    const matchAgainstPool = (rings: number[][][], pool: number[][][]) => {
      const used = new Set<number>();
      const unmatched: number[][][] = [];
      for (const ring of rings) {
        let found = -1;
        for (let i = 0; i < pool.length; i++) {
          if (used.has(i)) continue;
          if (ringsApproxEqual(ring, pool[i])) {
            found = i;
            break;
          }
        }
        if (found >= 0) used.add(found);
        else unmatched.push(ring);
      }
      return { unmatched, matchedCount: used.size };
    };

    try {
      const nextPayload: Record<string, unknown> = { ...payload };
      const currentRings = Array.isArray(nextPayload.polygons)
        ? (nextPayload.polygons as number[][][])
        : [];
      const original = editOriginalRingsRef.current;

      let geometryMode: 'none' | 'append' | 'replace' = 'none';
      let appendRings: number[][][] = [];

      if (currentRings.length > 0 && original.length > 0) {
        const { unmatched: newOrEdited, matchedCount: matchedFromCurrent } =
          matchAgainstPool(currentRings, original);
        const { unmatched: missingOriginals } = matchAgainstPool(original, currentRings);
        const pureAppend =
          missingOriginals.length === 0 &&
          matchedFromCurrent === original.length &&
          newOrEdited.length > 0;

        if (newOrEdited.length === 0 && missingOriginals.length === 0) {
          // Same blocks (possibly float noise) — metadata-only update
          delete nextPayload.polygons;
          geometryMode = 'none';
        } else if (pureAppend) {
          nextPayload.polygons = newOrEdited;
          appendRings = newOrEdited;
          geometryMode = 'append';
        } else {
          // Reshape / delete / mix — cannot PUT-append without duplicating
          geometryMode = 'replace';
        }
      } else if (currentRings.length > 0 && original.length === 0) {
        // No baseline stored — treat as append of whatever was drawn
        geometryMode = 'append';
        appendRings = currentRings;
      } else {
        delete nextPayload.polygons;
      }

      if (geometryMode === 'replace') {
        const farmerId =
          parseExplorerFarmerId(nextPayload.farmer_id) ??
          parseExplorerFarmerId(fieldToUpdate?.farmer_id);
        if (farmerId == null) {
          throw new Error(
            'Cannot replace boundary: farmer_id is missing. Refresh the farm list and try again.'
          );
        }
        for (const key of ['region', 'province', 'municipality', 'barangay'] as const) {
          if (nextPayload[key] == null || nextPayload[key] === '') {
            throw new Error(
              `Cannot replace boundary: missing ${key}. Check location fields in the update form.`
            );
          }
        }

        // Create first (keeps old farm if create fails), then delete the old id.
        await createFarmsViaDigisakaApi({
          farms: [
            {
              name: String(nextPayload.name || fieldToUpdate?.farm_name || 'Farm').trim(),
              farmer_id: farmerId,
              region: nextPayload.region,
              province: nextPayload.province,
              municipality: nextPayload.municipality,
              barangay: nextPayload.barangay,
              total_land_area: nextPayload.total_land_area ?? 0,
              polygons: currentRings,
            },
          ],
        });
        try {
          await deleteFarmViaDigisakaApi(fieldId);
        } catch (delErr) {
          console.warn('Created replacement farm but failed to delete old farm:', delErr);
          setToast({
            message:
              'New boundary saved as a new farm, but the old farm could not be removed — delete the duplicate in the list if needed.',
            type: 'info',
          });
        }
      } else {
        if (geometryMode === 'append' && appendRings.length > 0) {
          nextPayload.polygons = appendRings;
        }
        await updateFarmViaDigisakaApi(fieldId, nextPayload);
      }

      console.log('Field boundary updated successfully:', geometryMode);

      // Close update modal and clean up
      setShowUpdateModal(false);
      setIsUpdatingMode(false);
      setFieldToUpdate(null);
      editOriginalRingsRef.current = [];
      setConfirmedUpdatedGeometry(null);
      setDrawnField(null);
      resetManualDrawState();

      // Trigger field reload
      setReloadFields(true);

      setToast({
        message:
          geometryMode === 'replace'
            ? 'Field boundary replaced successfully.'
            : geometryMode === 'append'
              ? `Added ${appendRings.length} new block${appendRings.length === 1 ? '' : 's'}.`
              : 'Field updated successfully.',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Error updating field boundary:', error);
      setToast({
        message: `Failed to update field: ${error.message}`,
        type: 'error'
      });
    }
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setIsUpdatingMode(false);
    setFieldToUpdate(null);
    setConfirmedUpdatedGeometry(null);
    setDrawnField(null);
    resetManualDrawState();
  };

  const analysisDockReady =
    analysisPanelOpen &&
    analysisPanelReady &&
    !!activeRailIcon &&
    !analysisPanelMinimized &&
    !isCompactViewport &&
    !isDrawingMode &&
    !manualDrawEditing &&
    !manualDrawRecoverable &&
    !isSplittingFarm &&
    !isSavingFarmSplit &&
    !farmSplitReview;
  /** Field analysis is its own column beside the saved-fields list. */
  const farmAnalysisInFieldsSidebar =
    analysisDockReady &&
    activeRailIcon === 'farm' &&
    analysisMode !== 'large-scale' &&
    (!!selectedField || isMultiFieldAnalysis);
  const dockAnalysisInLeftSidebar =
    analysisDockReady &&
    analysisMode === 'large-scale' &&
    !farmAnalysisInFieldsSidebar &&
    activeRailIcon !== 'cropArea';
  /** CADI, pest, drone, and the other nav tools replace the fields list and its map shapes. */
  const otherToolHidesFields =
    analysisMode === 'large-scale' || (!!activeRailIcon && activeRailIcon !== 'farm');

  const showLeftDockColumn = dockAnalysisInLeftSidebar || showLeftAnalyticsSidebar;
  const leftDockPaperClass = isDark
    ? 'border-r border-[#2d3748] bg-[#161b22] text-[#e6edf3]'
    : 'border-r border-[#e5e7eb] bg-white text-[#111827]';
  const leftDockSplitRule = dockAnalysisInLeftSidebar
    ? isDark
      ? 'border-t border-[#2d3748]'
      : 'border-t border-[#e5e7eb]'
    : '';

  /** Settings is a page next to the side menu (not a pop-up). Only signed-in, non-guest users have it. */
  const settingsPageOpen = profileModalOpen && !!authUser && !authUser.is_system_guest;
  const pickNavTool = (icon: MapToolRailIcon) => {
    setDroneProcessingOpen(false);
    setPandoyPageOpen(false);
    requestClosePandoy();
    if (settingsPageOpen) {
      setProfileModalOpen(false);
      // Going back to the tool that is already open just closes Settings.
      if (icon === activeRailIcon) return;
    }
    handleRailIconClick(icon);
  };
  const pickNavSection = (id: string) => {
    setDroneProcessingOpen(false);
    setProfileModalOpen(false);
    setPandoyPageOpen(false);
    requestClosePandoy();
    handleHeaderSectionClick(id);
  };

  return (
    <div
      ref={dashboardScreenshotRef}
      className={`relative h-screen flex flex-col overflow-hidden transition-[padding-left] duration-200 ease-out motion-reduce:transition-none lg:pl-[var(--nav-sidebar-w)] ${pageShellClass(theme)}`}
      style={
        {
          '--nav-sidebar-w': `${navMenuExpanded ? NAV_SIDEBAR_EXPANDED_PX : NAV_SIDEBAR_COLLAPSED_PX}px`,
        } as CSSProperties
      }
      data-digisaka-map-shell
    >
      <WelcomeAccessModal />
      <GuestTimeUpOverlay />
      {/* Onboarding tour — first-time prompt + step-by-step spotlight guide. */}
      <TourPromptModal
        open={tourPromptOpen}
        onStart={handleTourPromptStart}
        onLater={handleTourPromptLater}
        onDontShowAgain={handleTourPromptDontShowAgain}
      />
      <TourGuide
        steps={tourSteps}
        active={tourActive}
        currentStep={tourStep}
        onPrev={() => setTourStep((s) => Math.max(0, s - 1))}
        onNext={() => setTourStep((s) => Math.min(tourSteps.length - 1, s + 1))}
        onSkip={handleTourSkip}
        onComplete={handleTourComplete}
      />
      <WhatsNewModal
        open={whatsNewOpen}
        item={whatsNewItem}
        dismissing={whatsNewDismissing}
        onDismiss={() => void handleWhatsNewDismiss()}
      />
      <DashboardNavSidebar
        expanded={navMenuExpanded}
        onToggleExpanded={toggleNavMenu}
        activeIcon={settingsPageOpen || droneProcessingOpen || pandoyPageOpen ? null : activeRailIcon}
        onIconClick={pickNavTool}
        onOpenSettings={() => {
          setDroneProcessingOpen(false);
          setPandoyPageOpen(false);
          requestClosePandoy();
          setProfileModalOpen(true);
        }}
        settingsActive={settingsPageOpen}
        allowedEntitlementSet={layerAllowedEntitlementSet}
        toolOpen={navToolFocused && !settingsPageOpen && !droneProcessingOpen && !pandoyPageOpen}
        subItems={headerNavSections}
        activeSubItemId={activeRailIcon === 'farm' ? farmAnalysisTab : pinnedNavSection}
        onSubItemClick={pickNavSection}
        onSubItemClose={handleHeaderSectionClose}
        droneProcessingActive={droneProcessingOpen}
        onOpenDroneProcessing={openDroneProcessing}
        showCarbonProjects={isProjectDeveloper(authUser?.role)}
        onOpenCarbonProjects={() => navigate('/carbon-projects')}
      />
      <DashboardTopbar
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        onStartTour={handleStartTourManually}
        onOpenProfile={() => {
          setDroneProcessingOpen(false);
          setPandoyPageOpen(false);
          requestClosePandoy();
          setProfileModalOpen(true);
        }}
        activeAnalysisIcon={settingsPageOpen || droneProcessingOpen || pandoyPageOpen ? null : activeRailIcon}
        settingsOpen={settingsPageOpen}
        pageOverride={
          droneProcessingOpen
            ? { title: 'Drone Processing', subtitle: 'Upload a flight and build an orthomosaic' }
            : pandoyPageOpen
              ? { title: 'Pandoy AI', subtitle: 'Ask about your farms and maps' }
              : null
        }
        droneProcessingActive={droneProcessingOpen}
        onOpenDroneProcessing={openDroneProcessing}
        showCarbonProjects={isProjectDeveloper(authUser?.role)}
        onOpenCarbonProjects={() => navigate('/carbon-projects')}
        onAnalysisIconClick={pickNavTool}
        allowedEntitlementSet={layerAllowedEntitlementSet}
        navFocused={navToolFocused && !pandoyPageOpen}
        navToolLabel={headerToolLabel}
        navSections={headerNavSections}
        activeNavSectionId={activeRailIcon === 'farm' ? farmAnalysisTab : pinnedNavSection}
        pinnedNavSectionId={pinnedNavSection}
        onNavSectionClick={handleHeaderSectionClick}
        onCloseNavTool={closeHeaderTool}
        onNavSectionInfo={setNavInfoSection}
      />
      <div className="flex min-h-0 flex-1">
      {/* Fields sidebar only for farm analysis. A large-scale layer group replaces it. */}
      <Sidebar
        isOpen={
          sidebarOpen &&
          !settingsPageOpen &&
          !droneProcessingOpen &&
          !pandoyPageOpen &&
          !showLeftAnalyticsSidebar &&
          !dockAnalysisInLeftSidebar &&
          !otherToolHidesFields
        }
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onFieldSelect={handleFieldSelect}
        onFarmFocus={handleFarmFocus}
        onDeleteField={handleDeleteField}
        onEditBoundary={handleEditBoundary}
        onSplitFarm={handleSplitFarm}
        isSplittingFarm={isSplittingFarm}
        farmSplitReview={
          farmSplitReview
            ? {
                baseName: farmSplitReview.baseName,
                featureCount: farmSplitReview.features.length,
                step: splitReviewStep,
                selectedIndex: splitSelectedIndex,
              }
            : null
        }
        isSavingFarmSplit={isSavingFarmSplit}
        onSaveFarmSplit={handleSaveFarmSplit}
        onCancelFarmSplit={handleCancelFarmSplit}
        onSelectSplitSubplot={handleSelectSplitSubplot}
        onStartAdjustSplitVertices={handleStartAdjustSplitVertices}
        onCancelSplitVertexEdit={handleCancelSplitVertexEdit}
        onConfirmSplitVertexEdit={handleConfirmSplitVertexEdit}
        onSaveSelectedSplitSubplot={handleSaveSelectedSplitSubplot}
        onRemoveSplitSubplot={handleRemoveSplitSubplot}
        onOpenDroneData={handleOpenDroneDataFromField}
        isUpdatingMode={isUpdatingMode}
        selectedField={selectedField}
        selectedFieldId={selectedField?.farm_id}
        isDrawingMode={isDrawingMode}
        manualDrawEditing={manualDrawEditing}
        manualDrawRecoverable={manualDrawRecoverable}
        onDrawingModeToggle={handleDrawingModeToggle}
        onConfirmManualDraw={handleConfirmManualDraw}
        onCancelManualDrawEdit={handleCancelManualDrawEdit}
        onResumeManualDrawEdit={handleResumeManualDrawEdit}
        onDiscardManualDraw={handleDiscardManualDraw}
        onRedrawManualDraw={handleRedrawManualDraw}
        onDeleteManualDrawBlock={handleDeleteManualDrawBlock}
        drawnField={drawnField}
        agLandWarning={agLandWarning}
        onSaveDemoField={handleSaveDemoField}
        onDeleteDemoField={handleDeleteDemoField}
        onFileUpload={handleFileUpload}
        reloadFields={reloadFields}
        onFieldsReloaded={handleFieldsReloaded}
        onStartMultiFieldAnalysis={handleStartMultiFieldAnalysis}
        onOpenFarmGroup={handleOpenFarmGroupOnMap}
        onClearFarmGroupMap={handleClearFarmGroupOnMap}
        onFieldsLoaded={handleFieldsLoaded}
        onFarmRiskStatuses={handleFarmRiskStatuses}
        onStartCreateFarmTour={handleStartCreateFarmTour}
        onBloFarmCatalogEmptyChange={setBloFarmCatalogEmpty}
        multiFieldSelectionLocked={isMultiFieldProcessing}
        aiFieldDelineationStep={aiFieldDelineationStep}
        aiFieldDelineationLoading={aiFieldDelineationLoading}
        aiFieldDelineationError={aiFieldDelineationError}
        aiFieldDelineationFieldCount={aiFieldDelineationData?.field_count ?? 0}
        aiDelineationSelectedIndex={aiDelineationSelectedIndex}
        aiDelineationMultiSelect={aiDelineationMultiSelect}
        aiDelineationCheckedIndexes={aiDelineationCheckedIndexes}
        onAiFieldDelineationOpen={handleAiFieldDelineationOpen}
        onAiFieldDelineationClose={handleAiFieldDelineationClose}
        onAiFieldDelineationProcess={handleAiFieldDelineationProcess}
        aiFieldDelineationInputMode={aiFieldDelineationInputMode}
        aiFieldDelineationModel={aiFieldDelineationModel}
        onAiFieldDelineationModelChange={setAiFieldDelineationModel}
        onAiFieldDelineationInputModeChange={(mode) => {
          setAiFieldDelineationInputMode(mode);
          if (mode === 'mapView') setAiFieldDelineationGeoTiff(null);
        }}
        aiFieldDelineationGeoTiffName={aiFieldDelineationGeoTiff?.name ?? null}
        onAiFieldDelineationGeoTiffChange={(file) => setAiFieldDelineationGeoTiff(file)}
        onSelectDelineationField={handleSelectDelineationField}
        onStartAdjustDelineationVertices={handleStartAdjustDelineationVertices}
        onCancelDelineationVertexEdit={handleCancelDelineationVertexEdit}
        onConfirmDelineationAsField={handleConfirmDelineationAsField}
        onUndoVertexEdit={handleUndoVertexEdit}
        onRedoVertexEdit={handleRedoVertexEdit}
        vertexEditCanUndo={vertexEditHistory.canUndo}
        vertexEditCanRedo={vertexEditHistory.canRedo}
        onUseSelectedDelineationField={handleUseSelectedDelineationField}
        onToggleDelineationMultiSelect={handleToggleDelineationMultiSelect}
        onSaveAllDelineationAsOneField={handleSaveAllDelineationAsOneField}
        onSaveCheckedDelineationAsOneField={handleSaveCheckedDelineationAsOneField}
        onRemoveDelineationBlock={handleRemoveDelineationBlock}
        onRemoveCheckedDelineationBlocks={handleRemoveCheckedDelineationBlocks}
        onSelectAllDelineationBlocks={handleSelectAllDelineationBlocks}
        onClearDelineationSelection={handleClearDelineationSelection}
        onUseCheckedDelineationField={handleUseCheckedDelineationField}
        showSavedFieldsDuringDelineation={showSavedFieldsDuringDelineation}
        onShowSavedFieldsDuringDelineationChange={setShowSavedFieldsDuringDelineation}
        onDismissOverlappingSavedDelineations={handleDismissOverlappingSavedDelineations}
        aiFieldDelineationOrigin={aiFieldDelineationOrigin}
        aiFieldDelineationFieldLabels={aiFieldDelineationFieldLabels}
        mapZoom={layerControls?.zoomLevel}
      />

      {farmAnalysisInFieldsSidebar && sidebarOpen && !droneProcessingOpen && (
        <div
          ref={setFarmAnalysisSlotEl}
          className={`relative z-[80] hidden h-full w-[22rem] shrink-0 flex-col overflow-hidden border-r sm:flex ${
            isDark ? 'border-[#2d3748] bg-[#161b22]' : 'border-[#e5e7eb] bg-white'
          }`}
        />
      )}

      {showLeftDockColumn && sidebarOpen && !droneProcessingOpen && (
        <div
          className={`relative z-[80] hidden h-full w-[22rem] shrink-0 flex-col overflow-hidden sm:flex ${leftDockPaperClass}`}
        >
          {dockAnalysisInLeftSidebar && (
            <div
              ref={setLargeScaleLeftSlotEl}
              className={`analysis-left-slot flex min-h-0 flex-col overflow-y-auto ${
                showLeftAnalyticsSidebar ? 'max-h-[42%] shrink-0' : 'flex-1'
              }`}
            />
          )}
          {showDronePrescriptionSidebar && dronePrescriptionPanel && (
            <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto p-2 sm:p-3 ${leftDockSplitRule}`}>
              <DronePrescriptionPanel
                state={dronePrescriptionPanel.state}
                mission={dronePrescriptionPanel.mission}
                usingMissionExtent={dronePrescriptionPanel.usingMissionExtent}
                variant="inline"
                onClose={handleCloseDronePrescriptionSidebar}
              />
            </div>
          )}
        </div>
      )}

      {/* Drone NDVI analysis — phone overlay (desktop lives in the left dock column) */}
      {showDronePrescriptionSidebar && dronePrescriptionPanel && (
        <>
          <div
            className="sm:hidden fixed inset-0 bg-black bg-opacity-50 z-[115]"
            onClick={handleCloseDronePrescriptionSidebar}
            aria-hidden
          />
          <div
            className={`sm:hidden fixed left-0 top-0 z-[120] flex h-full w-80 flex-col ${leftDockPaperClass}`}
          >
            <div className="flex-1 min-h-0 overflow-y-auto p-2 sm:p-3">
              <DronePrescriptionPanel
                state={dronePrescriptionPanel.state}
                mission={dronePrescriptionPanel.mission}
                usingMissionExtent={dronePrescriptionPanel.usingMissionExtent}
                variant="inline"
                onClose={handleCloseDronePrescriptionSidebar}
              />
            </div>
          </div>
        </>
      )}

      {droneProcessingOpen && sidebarOpen && !settingsPageOpen && (
        <div
          className={`fixed left-0 top-0 z-[120] flex h-full w-[min(100vw,22rem)] shrink-0 translate-x-0 flex-col lg:relative lg:z-auto lg:h-full lg:w-[22rem] ${leftDockPaperClass}`}
        >
          <div className={`flex items-center justify-between border-b px-3 py-2.5 lg:hidden ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
            <p className="text-sm font-semibold">Drone Processing</p>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className={`rounded p-0.5 ${isDark ? 'hover:bg-white/10' : 'hover:bg-[#f3f4f6]'}`}
              aria-label="Close drone processing"
            >
              <i className="ri-close-line text-lg" aria-hidden />
            </button>
          </div>
          <DroneProcessingPanel
            onOverlayChange={handleDroneOverlay}
            initialWorkspaceId={joinedWorkspaceId}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Map and Results Container */}
        <div className={`flex-1 flex ${(forecastResults || strawBurnResults || treeDetectionResults || crownSegmentationResults || cropfieldDelineationResults) ? 'flex-col' : ''} min-h-0`}>
          {/* Map Area: ~68.5% map when rice forecast / straw burn results (panel 31.5%); 65% other bottom panels; else full */}
          <div
            ref={mapScreenshotRef}
            className={`relative ${
              forecastResults || strawBurnResults
                ? 'flex-[0.717] sm:flex-[0.685]'
                : treeDetectionResults || crownSegmentationResults || cropfieldDelineationResults
                  ? 'flex-[0.65]'
                  : 'flex-1'
            } transition-all duration-300`}
          >
            {/* Reopen tab for whichever left panel is hidden: fields, farm analysis, or the
                active layer group. Sits on the map's left edge so it never covers map controls. */}
            {!sidebarOpen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className={showFieldsPanelTabClass(theme)}
                aria-label="Show panel"
                title="Show panel"
              >
                <i className="ri-arrow-right-s-line text-xl" aria-hidden />
              </button>
            )}

            {(aiFieldDelineationStep === 'editing' ||
              (!!farmSplitReview && splitReviewStep === 'editing')) && (
              <div className="pointer-events-none absolute top-4 left-1/2 z-40 w-[min(calc(100%-8rem),28rem)] -translate-x-1/2">
                <div
                  className={`pointer-events-auto flex flex-wrap items-center justify-center gap-1.5 rounded-xl px-2 py-1.5 shadow-lg backdrop-blur-md ${
                    isDark
                      ? 'bg-[rgba(22,27,34,.95)] text-[#e6edf3]'
                      : 'bg-[rgba(248,245,236,.96)] text-[#111827]'
                  }`}
                >
                  <span className="px-1 text-[11px] font-semibold">Adjust boundary</span>
                  <button
                    type="button"
                    disabled={!vertexEditHistory.canUndo}
                    onClick={handleUndoVertexEdit}
                    className={`rounded-lg px-2 py-1 text-[11px] font-medium disabled:opacity-40 ${
                      isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                    }`}
                    title="Undo last vertex change (Ctrl/⌘+Z)"
                  >
                    Undo
                  </button>
                  <button
                    type="button"
                    disabled={!vertexEditHistory.canRedo}
                    onClick={handleRedoVertexEdit}
                    className={`rounded-lg px-2 py-1 text-[11px] font-medium disabled:opacity-40 ${
                      isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                    }`}
                    title="Redo last undone change (Ctrl/⌘+Shift+Z)"
                  >
                    Redo
                  </button>
                  <button
                    type="button"
                    onClick={
                      farmSplitReview && splitReviewStep === 'editing'
                        ? handleConfirmSplitVertexEdit
                        : handleConfirmDelineationAsField
                    }
                    className="inline-flex items-center gap-1 rounded-lg bg-[#008540] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#006b34]"
                  >
                    <i className="ri-check-line text-sm" aria-hidden />
                    Done
                  </button>
                </div>
              </div>
            )}

            {/* Location search — occupies the old analysis-rail corner so the top bar can hold tools. */}
            <div
              data-tour-id="top-search"
              className="absolute top-4 right-[18px] z-30 w-[min(calc(100%-2rem),16.5rem)]"
            >
              <SearchBar
                placeholder="Search location"
                onLocationSelect={(lat, lon, name) => {
                  const sel = layerControls?.selectSearchLocation;
                  if (typeof sel === 'function') {
                    sel(lat, lon, name);
                  } else if (typeof layerControls?.flyToLocation === 'function') {
                    layerControls.flyToLocation([lon, lat], 15);
                  }
                }}
              />
            </div>

            {/* Analysis panel — left dock on desktop (never a floating map card).
                Phones (`< sm`): full-screen sheet. */}
            {analysisPanelOpen && !isDrawingMode && !manualDrawEditing && !manualDrawRecoverable && !isSplittingFarm && !isSavingFarmSplit && !farmSplitReview && activeRailIcon && activeRailIcon !== 'cropArea' && analysisPanelReady && (analysisMode === 'large-scale' || selectedField || isMultiFieldAnalysis) && (() => {
              const analysisPanelElement = (
                <AnalysisPanel
                  isOpen={true}
                  onToggle={handleAnalysisPanelToggle}
                  selectedField={selectedField}
                  fieldGeoJson={fieldGeoJson}
                  onAnalysisRun={handleAnalysisRun}
                  onZoomToField={handleZoomToField}
                  onCloseAnalysis={handleCloseAnalysis}
                  processing={processing}
                  processingType={processingType}
                  analysisResults={analysisResults}
                  indexData={indexData}
                  vhiData={vhiData}
                  indexHeatMapData={indexHeatMapData}
                  timeSeriesData={timeSeriesData}
                  filteredTimeSeriesData={filteredTimeSeriesData}
                  onTimeSeriesSliderChange={handleTimeSeriesSliderChange}
                  externalTimeSeriesDate={externalTimeSeriesDate}
                  showCloudFilter={showCloudFilter}
                  maxCloudCoverage={maxCloudCoverage}
                  onCloudFilterChange={handleCloudFilterChange}
                  onActiveFarmTabChange={(tab) => {
                    setFarmAnalysisTabRemembered(true);
                    setFarmAnalysisTab(tab);
                  }}
                  initialFarmTab={farmAnalysisTabRemembered ? farmAnalysisTab : null}
                  farmTabsInHeader
                  onFarmHeaderTabsChange={handleFarmHeaderTabsChange}
                  requestedFarmTab={farmTabRequest}
                  cancelFarmWorkEpoch={farmWorkCancelEpoch}
                  pinnedLargeScaleSection={activeRailIcon === 'farm' ? null : pinnedNavSection}
                  infoSectionId={navInfoSection}
                  onInfoSectionHandled={() => setNavInfoSection(null)}
                  onClosePinnedSection={handleHeaderSectionClose}
                  onHeatMapIndexChange={handleHeatMapIndexChange}
                  heatMapSatellite={heatMapApiSatellite}
                  externalHeatMapObservationDate={heatMapObservationDate}
                  wmsLayerStates={wmsLayerStates}
                  onWMSAnalysisRun={(layerType) => handleAnalysisRun(`wms_${layerType}`, { layerType })}
                  isChangeDetectionMode={isChangeDetectionMode}
                  changeDetectionData={changeDetectionData}
                  analysisMode={analysisMode}
                  multiFieldMode={isMultiFieldAnalysis}
                  multiFieldSelection={multiFieldSelection || []}
                  onMultiFieldProcessingChange={setIsMultiFieldProcessing}
                  layerVisibility={layerControls?.layerVisibility || (layerControls && layerControls.getCurrentLayerVisibility ? layerControls.getCurrentLayerVisibility() : undefined)}
                  layerOpacity={layerControls?.layerOpacity}
                  onLayerToggle={layerControls?.handleLayerToggle}
                  onOpacityChange={layerControls?.handleOpacityChange}
                  legendUrls={layerControls && layerControls.getLegendUrls ? layerControls.getLegendUrls() : undefined}
                  cropAreaLegends={layerControls && layerControls.getCropAreaLegendInfo ? layerControls.getCropAreaLegendInfo() : undefined}
                  loadingLayers={layerControls?.cropAreaLoadingLayers}
                  onForecastDataUpdate={layerControls?.handleForecastDataUpdate}
                  onForecastResultsUpdate={handleForecastResultsUpdate}
                  onStrawBurnResultsUpdate={handleStrawBurnResultsUpdate}
                  onFloodLayerShow={layerControls?.handleFloodLayerShow}
                  onBSRIDateRangeUpdate={layerControls?.handleBSRIDateRangeUpdate}
                  bsriIsLoading={layerControls?.bsriIsLoading}
                  riceGrowthStageLoading={layerControls?.riceGrowthStageLoading}
                  irrigationTypeLoading={layerControls?.irrigationTypeLoading}
                  onBSRIAnalyseRequest={handleBSRIAnalyseRequest}
                  onStrawBurnDataFetch={layerControls?.handleStrawBurnDataFetch}
                  strawBurnIsLoading={layerControls?.strawBurnIsLoading}
                  strawBurnData={layerControls?.strawBurnData}
                  onPlantingMethodZoom={layerControls?.handlePlantingMethodZoom}
                  onTreeDetectionDataUpdate={handleTreeDetectionDataUpdate}
                  onCrownSegmentationResultsUpdate={handleCrownSegmentationResultsUpdate}
                  onCropfieldDelineationUpdate={handleCropfieldDelineationUpdate}
                  onFarmForecastResultsUpdate={handleFarmForecastResultsUpdate}
                  mapCenter={layerControls?.mapCoordinates}
                  mapZoom={layerControls?.zoomLevel}
                  onExpandedTabChange={handleExpandedTabChange}
                  onFloodDamageSelect={handleFloodDamageSelect}
                  selectedFloodDamageId={floodDamageSelectedData?.id ?? null}
                  onLSTPHDateRangeUpdate={layerControls?.handleLSTPHDateRangeUpdate}
                  lstIsLoading={layerControls?.lstPHIsLoading}
                  onVHIPHDateRangeUpdate={layerControls?.handleVHIPHDateRangeUpdate}
                  vhiIsLoading={layerControls?.vhiPHIsLoading}
                  getOlMap={layerControls?.getOlMap}
                  pestRiskPestKey={layerControls?.pestRiskPestKey}
                  onPestRiskPestChange={layerControls?.handlePestRiskPestChange}
                  onRequestDronePrescriptionStats={layerControls?.requestDronePrescriptionStats}
                  onClearDronePrescriptionStats={layerControls?.clearDronePrescriptionStats}
                  droneSites={
                    layerControls?.handleDroneSiteLayerToggle
                      ? {
                          activeLayerId: layerControls.droneSiteActiveLayerId ?? null,
                          opacity: layerControls.droneSiteOpacity ?? {},
                          onToggle: layerControls.handleDroneSiteLayerToggle,
                          onOpacityChange: layerControls.handleDroneSiteOpacityChange,
                        }
                      : undefined
                  }
                  zonalState={multitemporalZonalPanel?.state}
                  zonalLayer={
                    multitemporalZonalPanel?.layer ??
                    (layerControls?.layerVisibility?.cadiDrought ? 'cadi' : 'pestRisk')
                  }
                  zonalFrameLabel={multitemporalZonalPanel?.frameLabel}
                  zonalPestLabel={multitemporalZonalPanel?.pestLabel}
                  zonalProvinces={multitemporalProvinces}
                  zonalSelectedProvince={multitemporalSelectedProvince}
                  onZonalProvinceChange={handleMultitemporalProvinceChange}
                  onZonalMunicipalitySelect={handleSelectMultitemporalMunicipality}
                  onShowDroneOrthoIndexMap={layerControls?.showDroneOrthoIndexMap}
                  onClearDroneOrthoIndexMap={layerControls?.clearDroneOrthoIndexMap}
                  cropCalendarFarmerInput={cropCalendarFarmerInput}
                  cropCalendarOpenRequest={cropCalendarOpenRequest}
                  onCropCalendarAskPandoy={(pasteText, payload) => {
                    setAutoGptRequest({ source: 'cropCalendar', payload, pasteText });
                  }}
                  droneDataOpenRequest={droneDataOpenRequest}
                  mapScreenshotRef={mapScreenshotRef}
                  allowedLayerKeys={effectiveAllowedLayerKeys}
                  displayMode="inline-card"
                  controlledLargeScaleTab={analysisMode === 'large-scale' && activeRailIcon !== 'farm' ? activeRailIcon : null}
                  onControlledLargeScaleTabChange={handleLargeScaleTabRequestChange}
                  onBackToFields={handleBackToFields}
                />
              );

              const minimizedMeta = activeRailIcon ? RAIL_TAB_META[activeRailIcon] : null;
              const restoreChip = analysisPanelMinimized && minimizedMeta ? (
                <button
                  type="button"
                  onClick={() => setAnalysisPanelMinimized(false)}
                  className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full shadow-2xl border transition-colors hover:opacity-90 animate-[gpt-pill-in_220ms_ease-out_both] fixed top-24 left-1/2 -translate-x-1/2 z-[85] sm:absolute sm:top-20 sm:left-auto sm:translate-x-0 sm:z-20 ${
                    theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white'
                  }`}
                  style={{
                    ...(!isCompactViewport ? { right: 18 } : {}),
                    borderColor: RAIL_LAYER_ACTIVE_COLOR,
                    color: RAIL_LAYER_ACTIVE_COLOR,
                  }}
                  title={`Restore ${minimizedMeta.fullLabel}`}
                  aria-label={`Restore ${minimizedMeta.fullLabel} analysis panel`}
                >
                  <span
                    className="flex h-7 w-7 items-center justify-center rounded-full shrink-0"
                    style={getRailIconCircleStyle(true, false, theme)}
                  >
                    <RailToolIcon
                      meta={minimizedMeta}
                      active
                      imgClassName="h-4 w-4 object-contain"
                      iconClassName="text-base"
                    />
                  </span>
                  <span className="text-xs font-semibold whitespace-nowrap">
                    {minimizedMeta.fullLabel}
                  </span>
                  <i className="ri-arrow-up-s-line text-base" aria-hidden />
                </button>
              ) : null;

              if (isCompactViewport) {
                if (analysisPanelMinimized) {
                  return restoreChip;
                }
                return (
                  <div
                    className={`fixed inset-0 z-[85] flex flex-col sm:hidden ${
                      theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
                    }`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Analysis panel"
                    style={{ animation: 'rail-sheet-up 220ms ease-out both' }}
                  >
                    {analysisPanelElement}
                  </div>
                );
              }

              if (farmAnalysisInFieldsSidebar) {
                if (!farmAnalysisSlotEl) return null;
                return createPortal(
                  <div className="flex h-full min-h-0 flex-col [&_.analysis-panel-shell]:rounded-none [&_.analysis-panel-shell]:bg-transparent [&_.analysis-panel-shell]:shadow-none">
                    {analysisPanelElement}
                  </div>,
                  farmAnalysisSlotEl,
                );
              }

              if (dockAnalysisInLeftSidebar) {
                if (!largeScaleLeftSlotEl) return null;
                return (
                  <>
                    {createPortal(
                      <div className="flex h-full min-h-0 flex-col [&_.analysis-panel-shell]:rounded-none [&_.analysis-panel-shell]:bg-transparent [&_.analysis-panel-shell]:shadow-none">
                        {analysisPanelElement}
                      </div>,
                      largeScaleLeftSlotEl,
                    )}
                    {restoreChip}
                  </>
                );
              }

              return restoreChip;
            })()}

            {/* Processing indicator */}
            {processing && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30 bg-white rounded-lg shadow-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
                  <div className="text-sm text-gray-700">
                    <div className="font-medium">{processingType}</div>
                    <div className="text-gray-500">Please wait...</div>
                  </div>
                </div>
              </div>
            )}



            {/* Change Detection Status Indicator */}
            {isChangeDetectionMode && changeDetectionData && (
              <div className="absolute top-4 right-4 z-30 bg-gradient-to-r from-blue-100 to-orange-100 border border-gray-300 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-700 font-medium">
                    Change Detection Active
                  </span>
                  <i className="ri-compare-line text-orange-600"></i>
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {new Date(changeDetectionData.before.date).toLocaleDateString()}
                  <i className="ri-arrow-right-line mx-1"></i>
                  {new Date(changeDetectionData.after.date).toLocaleDateString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Δyield: {(changeDetectionData.after.mean_index_value - changeDetectionData.before.mean_index_value).toFixed(3)}
                </div>
              </div>
            )}

            {/* LGU administrative boundary is hidden for now. */}
            <MapView
              droneJobOverlay={droneProcessingOpen ? droneJobOverlay : null}
              selectedField={
                otherToolHidesFields ||
                aiFieldDelineationStep === 'picking' ||
                aiFieldDelineationStep === 'editing'
                  ? null
                  : selectedField
              }
              fieldGeoJson={
                otherToolHidesFields ||
                aiFieldDelineationStep === 'picking' ||
                aiFieldDelineationStep === 'editing'
                  ? null
                  : fieldGeoJson
              }
              farmGroupGeoJson={
                otherToolHidesFields ||
                (aiFieldDelineationStep === 'picking' && !showSavedFieldsDuringDelineation)
                  ? null
                  : (farmGroupGeoJson ?? farmsOverviewGeoJson ?? selectedFarmBlocksGeoJson)
              }
              farmGroupFit={!otherToolHidesFields && !selectedField && farmGroupFit}
              monitoringAreaBoundaryGeoJson={null}
              analysisResults={analysisResults}
              indexHeatMapData={indexHeatMapData}
              timeSeriesData={filteredTimeSeriesData || timeSeriesData}
              onFieldClick={handleMapFarmPolygonClick}
              onDelineationFieldClick={handleSelectDelineationField}
              onDelineationClearSelection={handleClearDelineationSelection}
              isAiDelineationPicking={
                aiFieldDelineationStep === 'picking' ||
                (!!farmSplitReview && splitReviewStep === 'picking')
              }
              onFieldContextMenu={handleFieldContextMenu}
              onZoomToField={handleZoomToField}
              onFarmWalkReport={(report) => {
                setAutoGptRequest({ source: 'farmWalk', payload: report });
              }}
              isLoadingGeoJson={isLoadingGeoJson}
              isDrawingMode={isDrawingMode}
              manualDrawEditing={manualDrawEditing}
              onDrawPolygonReady={handleDrawPolygonReady}
              onFieldDrawn={handleFieldDrawn}
              onTimeSeriesSliderChange={handleTimeSeriesSliderChange}
              // Pass WMS layer states
              wmsLayerStates={wmsLayerStates}
              wmsDateArrays={wmsDateArrays}
              onWMSDateChange={handleWMSDateChange}
              onWMSLayerToggle={handleWMSLayerToggle}
              onWMSPlayToggle={handleWMSPlayToggle}
              // Pass change detection handler
              onChangeDetectionToggle={handleChangeDetectionToggle}
              // Hamburger opens the dark Analysis panel; do not gate on `can_use_large_scale` (that
              // flag is false when fewer than two layer keys are allowed, which incorrectly left
              // only the legacy white map overlay).
              onSwitchToLargeScaleMode={handleSwitchToLargeScaleMode}
              // Pass layer control callback
              onLayerControlReady={setLayerControls}
              onDronePrescriptionUpdate={handleDronePrescriptionUpdate}
              onMultitemporalZonalUpdate={handleMultitemporalZonalUpdate}
              allowedLayerKeys={effectiveAllowedLayerKeys}
              heatMapObservationBar={
                showHeatMapObservationBar
                  ? {
                      observations: heatMapObservations,
                      selectedId: heatMapSelectedSceneId,
                      onSelect: (scene: HeatMapObservationScene) => {
                        setHeatMapSelectedSceneId(scene.id);
                        setHeatMapObservationDate(scene.date);
                        setHeatMapSceneSatellite(
                          resolveHeatmapSatellite(heatMapSatellite, String(scene.satellite))
                        );
                      },
                      satellite: heatMapSatellite,
                      onSatelliteChange: setHeatMapSatellite,
                      allowedSatellites: heatMapAllowedSatellites,
                      cloudFilterEnabled: heatMapCloudFilterEnabled,
                      onCloudFilterEnabledChange: setHeatMapCloudFilterEnabled,
                      maxCloudCover: heatMapMaxCloudCover,
                      onMaxCloudCoverChange: setHeatMapMaxCloudCover,
                      onLoadMore: heatMapHasMore ? handleHeatMapLoadMore : undefined,
                      loadingMore: heatMapLoadingMore,
                      loadingInitial: heatMapLoadingInitial,
                    }
                  : null
              }
            />
            {floodDamageSelectedData && (
              <FloodDamagePopup
                data={floodDamageSelectedData}
                onClose={() => handleFloodDamageSelect(null)}
                hasPrev={floodDamageHasPrev}
                hasNext={floodDamageHasNext}
                onPrev={() => handleFloodDamageSelect(floodDamageOrderedList[floodDamageCurrentIndex - 1])}
                onNext={() => handleFloodDamageSelect(floodDamageOrderedList[floodDamageCurrentIndex + 1])}
              />
            )}
          </div>

          {/* Forecast Results Display */}
          {forecastResults && forecastResults.forecasts && (
            <div className={`flex-[0.60] sm:flex-[0.315] ${rsEmerald.shell}`}>
              {/* PDF + close (same pattern as straw burning report) */}
              <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleRiceForecastDownloadPDF}
                  disabled={isGeneratingForecastPDF}
                  className={rsEmerald.actionBtn}
                  title="Export forecast report to PDF"
                >
                  {isGeneratingForecastPDF ? (
                    <i className="ri-loader-4-line text-xl animate-spin"></i>
                  ) : (
                    <i className="ri-file-pdf-2-line text-xl"></i>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForecastResults(null);
                    setAutoGptRequest(null);
                    if (layerControls?.handleLayerToggle) {
                      layerControls.handleLayerToggle('riceForecast', false);
                    }
                    if (layerControls?.handleForecastDataUpdate) {
                      layerControls.handleForecastDataUpdate(null);
                    }
                  }}
                  className={rsEmerald.closeBtn}
                  title="Close results"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="p-2 sm:p-3">
                  {/* Mobile-only filter tags - switch between Summary and Chart */}
                  <div className="flex sm:hidden gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setForecastResultsMobileTab('summary')}
                      className={`flex-1 ${forecastResultsMobileTab === 'summary' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                    >
                      Yield & Price
                    </button>
                    <button
                      type="button"
                      onClick={() => setForecastResultsMobileTab('chart')}
                      className={`flex-1 ${forecastResultsMobileTab === 'chart' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                    >
                      Yield range
                    </button>
                  </div>

                {/* Forecast Cards */}
                {Object.entries(forecastResults.forecasts).map(([month, forecast]: [string, any]) => {
                  // Calculate max value for chart scaling
                  const stats = forecast.region_statistics;
                  const maxValue = stats ? Math.max(stats.mean_yield, stats.min_yield, stats.max_yield) : 0;
                  
                  return (
                    <div key={month} className="w-full mb-4">
                      {/* Header */}
                      <div className="flex items-center space-x-2 mb-4">
                        <i className={`ri-calendar-check-line text-lg sm:text-xl transition-transform duration-300 hover:scale-110 ${rsEmerald.icon}`}></i>
                        <h6 className={`text-sm sm:text-base font-bold transition-colors duration-300 ${rsEmerald.title}`}>{month} Forecast</h6>
                        <span className={`text-xs sm:text-sm font-semibold px-3 py-1 rounded-full transition-all duration-300 hover:scale-105 max-w-[min(100%,18rem)] truncate ${rsEmerald.badge}`} title={(forecastResults as { scope_label?: string }).scope_label || forecastResults.region_name}>
                          {(forecastResults as { scope_label?: string }).scope_label || forecastResults.region_name}
                        </span>
                      </div>

                      {/* Main Content - Side by Side; on mobile show only selected tab */}
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                        {/* Left Side - Circular Base Yield and Palay Price (mobile: only when Summary tab) */}
                        <div className={`flex justify-center sm:justify-start gap-6 sm:gap-8 flex-shrink-0 ${forecastResultsMobileTab === 'chart' ? 'hidden sm:flex' : ''}`}>
                          {/* Base Yield - Circular */}
                          <div className="flex flex-col items-center group cursor-pointer">
                            <div className={`relative w-24 h-24 sm:w-28 sm:h-28 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl`}>
                              <div className="text-center transition-all duration-300 group-hover:scale-105">
                                <div className={`text-lg sm:text-xl font-bold transition-colors ${rsEmerald.statValue}`}>
                                  {forecast.base_yield?.toFixed(2)}
                                </div>
                                <div className={`text-[10px] sm:text-xs transition-colors ${rsEmerald.statUnit}`}>t/ha</div>
                              </div>
                            </div>
                            <div className="mt-2 text-center transition-all duration-300 group-hover:scale-105">
                              <i className={`ri-seedling-line text-sm transition-colors ${rsEmerald.icon}`}></i>
                              <div className={`text-[10px] sm:text-xs font-medium mt-1 transition-colors ${rsEmerald.statLabel}`}>Base Yield</div>
                            </div>
                          </div>

                          {/* Palay Price - Circular */}
                          <div className="flex flex-col items-center group cursor-pointer">
                            <div className={`relative w-24 h-24 sm:w-28 sm:h-28 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl`}>
                              <div className="text-center transition-all duration-300 group-hover:scale-105">
                                <div className={`text-lg sm:text-xl font-bold transition-colors ${rsEmerald.statValue}`}>
                                  ₱{forecast.rice_price?.toFixed(2)}
                                </div>
                                <div className={`text-[10px] sm:text-xs transition-colors ${rsEmerald.statUnit}`}>/kg</div>
                              </div>
                            </div>
                            <div className="mt-2 text-center transition-all duration-300 group-hover:scale-105">
                              <i className={`ri-money-dollar-circle-line text-sm transition-colors ${rsEmerald.icon}`}></i>
                              <div className={`text-[10px] sm:text-xs font-medium mt-1 transition-colors ${rsEmerald.statLabel}`}>Palay Price</div>
                            </div>
                          </div>
                        </div>

                        {/* Right Side - Region Statistics - Bar Chart with Error Bars (mobile: only when Yield range tab) */}
                        {stats && (
                          <div className={`flex-1 min-w-0 ${forecastResultsMobileTab === 'summary' ? 'hidden sm:block' : ''}`}>
                            <svg width="100%" height="200" viewBox="0 0 400 200" className="w-full" preserveAspectRatio="xMidYMid meet">
                                {/* Grid lines */}
                                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                                  const y = 180 - (ratio * 120);
                                  const value = maxValue * ratio;
                                  return (
                                    <g key={ratio}>
                                      <line x1="60" y1={y} x2="340" y2={y} stroke={rsEmerald.chartGrid} strokeWidth="1" strokeDasharray="2,2"/>
                                      <text x="55" y={y + 4} fill={rsEmerald.chartLabel} fontSize="10" textAnchor="end">
                                        {value.toFixed(1)}
                                      </text>
                                    </g>
                                  );
                                })}
                                
                                {/* Single Bar with Error Bars */}
                                <g>
                                  {/* Error Bar - Vertical line */}
                                  <line
                                    x1="200"
                                    y1={180 - (stats.max_yield / maxValue) * 120}
                                    x2="200"
                                    y2={180 - (stats.min_yield / maxValue) * 120}
                                    stroke={rsEmerald.chartAxis}
                                    strokeWidth="2"
                                  />
                                  
                                  {/* Error Bar - Top horizontal line */}
                                  <line
                                    x1="190"
                                    y1={180 - (stats.max_yield / maxValue) * 120}
                                    x2="210"
                                    y2={180 - (stats.max_yield / maxValue) * 120}
                                    stroke={rsEmerald.chartAxis}
                                    strokeWidth="2"
                                  />
                                  
                                  {/* Error Bar - Bottom horizontal line */}
                                  <line
                                    x1="190"
                                    y1={180 - (stats.min_yield / maxValue) * 120}
                                    x2="210"
                                    y2={180 - (stats.min_yield / maxValue) * 120}
                                    stroke={rsEmerald.chartAxis}
                                    strokeWidth="2"
                                  />
                                  
                                  {/* Mean Bar */}
                                  <rect
                                    x="150"
                                    y={180 - (stats.mean_yield / maxValue) * 120}
                                    width="100"
                                    height={(stats.mean_yield / maxValue) * 120}
                                    fill="none"
                                    stroke={rsEmerald.chartBar}
                                    strokeWidth="2"
                                    rx="4"
                                    className="transition-all duration-300 cursor-pointer"
                                  />
                                  
                                  {/* Mean value label */}
                                  <text
                                    x="200"
                                    y={180 - (stats.mean_yield / maxValue) * 120 - 5}
                                    fill={rsEmerald.chartValue}
                                    fontSize="13"
                                    fontWeight="bold"
                                    textAnchor="middle"
                                    className="transition-all duration-300"
                                  >
                                    {stats.mean_yield?.toFixed(2)}
                                  </text>
                                  
                                  {/* Min value label */}
                                  <text
                                    x="220"
                                    y={180 - (stats.min_yield / maxValue) * 120 + 3}
                                    fill={rsEmerald.chartValue}
                                    fontSize="11"
                                    fontWeight="bold"
                                    textAnchor="start"
                                  >
                                    Min: {stats.min_yield?.toFixed(2)}
                                  </text>
                                  
                                  {/* Max value label */}
                                  <text
                                    x="220"
                                    y={180 - (stats.max_yield / maxValue) * 120 - 3}
                                    fill={rsEmerald.chartValue}
                                    fontSize="11"
                                    fontWeight="bold"
                                    textAnchor="start"
                                  >
                                    Max: {stats.max_yield?.toFixed(2)}
                                  </text>
                                </g>

                                {/* Y-axis */}
                                <line x1="60" y1="60" x2="60" y2="180" stroke={rsEmerald.chartAxis} strokeWidth="2"/>
                                {/* X-axis */}
                                <line x1="60" y1="180" x2="340" y2="180" stroke={rsEmerald.chartAxis} strokeWidth="2"/>
                              </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          )}

          {/* Straw Burn Results Display */}
          {strawBurnResults && strawBurnResults.success && (
            <div className={`flex-[0.60] sm:flex-[0.315] ${rsRed.shell}`}>
              {/* Action Buttons (top-right) */}
              <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                {/* Export PDF Button */}
                <button
                  onClick={handleStrawBurnDownloadPDF}
                  disabled={isGeneratingPDF}
                  className={rsRed.actionBtn}
                  title="Export to PDF"
                >
                  {isGeneratingPDF ? (
                    <i className="ri-loader-4-line text-xl animate-spin"></i>
                  ) : (
                    <i className="ri-file-pdf-2-line text-xl"></i>
                  )}
                </button>
                {/* Close Button */}
                <button
                  onClick={() => {
                    setStrawBurnResults(null);
                    setAutoGptRequest(null);
                    if (layerControls?.handleLayerToggle) {
                      layerControls.handleLayerToggle('strawBurn', false);
                    }
                    if (layerControls?.handleStrawBurnDataClear) {
                      layerControls.handleStrawBurnDataClear();
                    }
                  }}
                  className={rsRed.closeBtn}
                  title="Close results"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="p-2 sm:p-3">
                  {/* Header */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 pr-20">
                    <i className={`ri-fire-line text-lg sm:text-xl transition-transform duration-300 hover:scale-110 ${rsRed.icon}`}></i>
                    <h6 className={`text-sm sm:text-base font-bold transition-colors duration-300 ${rsRed.title}`}>Detection Results</h6>
                    <span className={`text-xs sm:text-sm font-semibold px-3 py-1 rounded-full transition-all duration-300 hover:scale-105 ${rsRed.badge}`}>
                      {strawBurnResults.province ?? strawBurnResults.municipality}
                    </span>
                  </div>

                  {/* Mobile-only filter tags */}
                  <div className="flex lg:hidden gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setStrawBurnResultsMobileTab('summary')}
                      className={`flex-1 min-w-0 ${strawBurnResultsMobileTab === 'summary' ? rsRed.mobileTabActive : rsRed.mobileTabInactive}`}
                    >
                      Summary
                    </button>
                    <button
                      type="button"
                      onClick={() => setStrawBurnResultsMobileTab('info')}
                      className={`flex-1 min-w-0 ${strawBurnResultsMobileTab === 'info' ? rsRed.mobileTabActive : rsRed.mobileTabInactive}`}
                    >
                      Info
                    </button>
                  </div>

                {/* Main Content - Side by Side; on mobile show only selected tab */}
                {strawBurnResults.statistics && (
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    {/* Left Side - Circular Statistics (mobile: only when Summary tab) */}
                    <div className={`flex justify-center sm:justify-start gap-6 sm:gap-8 flex-shrink-0 ${strawBurnResultsMobileTab !== 'summary' ? 'hidden sm:flex' : ''}`}>
                      {/* Burned Area - Circular */}
                      <div className="flex flex-col items-center group cursor-pointer">
                        <div className={`relative w-24 h-24 sm:w-28 sm:h-28 rounded-full ${rsRed.statRing} flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl`}>
                          <div className="text-center transition-all duration-300 group-hover:scale-105">
                            <div className={`text-lg sm:text-xl font-bold transition-colors ${rsRed.statValue}`}>
                              {strawBurnResults.statistics.burned_area_hectares?.toFixed(2)}
                            </div>
                            <div className={`text-[10px] sm:text-xs transition-colors ${rsRed.statUnit}`}>ha</div>
                          </div>
                        </div>
                        <div className="mt-2 text-center transition-all duration-300 group-hover:scale-105">
                          <i className={`ri-fire-line text-sm transition-colors ${rsRed.icon}`}></i>
                          <div className={`text-[10px] sm:text-xs font-medium mt-1 transition-colors ${rsRed.statLabel}`}>Burned Area</div>
                        </div>
                      </div>

                      {/* Burned Percentage - Circular */}
                      <div className="flex flex-col items-center group cursor-pointer">
                        <div className={`relative w-24 h-24 sm:w-28 sm:h-28 rounded-full ${isDark ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-2 border-orange-500/50' : 'bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-200'} flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl`}>
                          <div className="text-center transition-all duration-300 group-hover:scale-105">
                            <div className={`text-lg sm:text-xl font-bold transition-colors ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                              {strawBurnResults.statistics.burned_percentage?.toFixed(2)}
                            </div>
                            <div className={`text-[10px] sm:text-xs transition-colors ${rsRed.statUnit}`}>%</div>
                          </div>
                        </div>
                        <div className="mt-2 text-center transition-all duration-300 group-hover:scale-105">
                          <i className={`ri-percent-line text-sm transition-colors ${isDark ? 'text-orange-400' : 'text-orange-600'}`}></i>
                          <div className={`text-[10px] sm:text-xs font-medium mt-1 transition-colors ${rsRed.statLabel}`}>Burned %</div>
                        </div>
                      </div>
                    </div>

                    {/* Right Side (sm+): same content as mobile Info tab */}
                    <div className="hidden sm:flex flex-1 min-w-0">
                      <div className={`h-full w-full p-3 sm:p-4 space-y-2.5 ${rsRed.infoCard}`}>
                        <div className="flex items-center justify-between text-[10px] sm:text-[11px]">
                          <span className="font-semibold flex items-center gap-1 text-blue-300">
                            <i className="ri-map-pin-line"></i>
                            Region: {strawBurnResults.region}
                          </span>
                          <span className="font-semibold flex items-center gap-1 text-indigo-300">
                            <i className="ri-calendar-line"></i>
                            {strawBurnResults.images_processed} images
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] sm:text-[11px] text-blue-300">
                          <i className="ri-calendar-event-line text-blue-400"></i>
                          <strong>Analysis Period:</strong>
                          <span className="ml-1">{strawBurnResults.period}</span>
                        </div>
                        {strawBurnResults.algorithm && (
                          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-purple-300">
                            <i className="ri-checkbox-circle-fill text-purple-400"></i>
                            <span className="font-medium">Calibrated by drone data (RBI)</span>
                          </div>
                        )}
                        <div className={`flex items-start gap-1.5 text-[10px] sm:text-[11px] border-t pt-2.5 mt-0.5 ${rsRed.infoDivider} ${isDark ? 'text-amber-200/90' : 'text-amber-800'}`}>
                          <i className="ri-smartphone-line text-amber-400 flex-shrink-0 mt-0.5"></i>
                          <span className="leading-snug">
                            <span className="font-medium text-amber-100/95">Digisaka mobile app validation:</span>{' '}
                            in progress — 10 of 50 random samples completed.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Info Section — mobile Info tab only (desktop uses right column above) */}
                <div
                  className={`mt-4 pt-4 space-y-2 sm:hidden ${rsRed.infoDivider} ${
                    strawBurnResultsMobileTab !== 'info' ? 'hidden' : ''
                  }`}
                >
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="font-semibold flex items-center gap-1 text-blue-300">
                      <i className="ri-map-pin-line"></i>
                      Region: {strawBurnResults.region}
                    </span>
                    <span className="font-semibold flex items-center gap-1 text-indigo-300">
                      <i className="ri-calendar-line"></i>
                      {strawBurnResults.images_processed} images
                    </span>
                  </div>

                  <div className="flex items-center gap-1 text-[10px] text-blue-300">
                    <i className="ri-calendar-event-line text-blue-400"></i>
                    <strong>Analysis Period:</strong>
                    <span className="ml-1">{strawBurnResults.period}</span>
                  </div>

                  {strawBurnResults.algorithm && (
                    <div className="flex items-center gap-1.5 text-[10px] text-purple-300">
                      <i className="ri-checkbox-circle-fill text-purple-400"></i>
                      <span className="font-medium">Calibrated by drone data</span>
                    </div>
                  )}
                  <div className={`flex items-start gap-1.5 text-[10px] border-t pt-2.5 mt-1 ${rsRed.infoDivider} ${isDark ? 'text-amber-200/90' : 'text-amber-800'}`}>
                    <i className="ri-smartphone-line text-amber-400 flex-shrink-0 mt-0.5"></i>
                    <span className="leading-snug">
                      <span className="font-medium text-amber-100/95">Digisaka mobile app validation:</span>{' '}
                      in progress — 10 of 50 random samples completed.
                    </span>
                  </div>
                </div>
                </div>
              </div>
            </div>
          )}

          {/* Tree Detection Results Display - below map, same as rice yield forecast */}
          {treeDetectionResults && treeDetectionResults.success && (
            <div className={`flex-[0.667] sm:flex-[0.35] ${rsEmerald.shell}`}>
              <button
                onClick={() => {
                  setTreeDetectionResults(null);
                  setAutoGptRequest(null);
                  setShowTreeDetectionModal(false);
                  if (layerControls?.handleLayerToggle) {
                    layerControls.handleLayerToggle('treeDetection', false);
                  }
                  if (layerControls?.handleTreeDetectionDataUpdate) {
                    layerControls.handleTreeDetectionDataUpdate(null);
                  }
                }}
                className={`absolute top-2 right-2 z-10 ${rsEmerald.closeBtn}`}
                title="Close results"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="p-2 sm:p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-3 pr-11 sm:pr-14">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                      <i className={`ri-tree-line text-lg sm:text-xl flex-shrink-0 ${rsEmerald.icon}`}></i>
                      <h6 className={`text-sm sm:text-base font-bold ${rsEmerald.title}`}>Tree Detection Results</h6>
                      <span className={`text-xs sm:text-sm font-semibold px-3 py-1 rounded-full ${rsEmerald.badge}`}>
                        {treeDetectionResults.total_detections ?? 0} trees
                      </span>
                    </div>
                    {(treeDetectionResults.geojson || treeDetectionResults.geojson_wgs84) && (
                      <button
                        type="button"
                        onClick={handleExportTreeGeoJson}
                        className={`flex items-center gap-1.5 flex-shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${rsEmerald.exportBtn}`}
                        title="Download GeoJSON (WGS84 when available)"
                      >
                        <i className="ri-download-2-line text-sm"></i>
                        Export GeoJSON
                      </button>
                    )}
                  </div>

                  {/* Mobile-only filter tags */}
                  <div className="flex lg:hidden gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setTreeDetectionResultsMobileTab('summary')}
                      className={`flex-1 min-w-0 ${treeDetectionResultsMobileTab === 'summary' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                    >
                      Summary
                    </button>
                    {treeDetectionResults.statistics && (
                      <button
                        type="button"
                        onClick={() => setTreeDetectionResultsMobileTab('confidence')}
                        className={`flex-1 min-w-0 ${treeDetectionResultsMobileTab === 'confidence' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                      >
                        Confidence
                      </button>
                    )}
                    {treeDetectionResults.visualization_image && (
                      <button
                        type="button"
                        onClick={() => setTreeDetectionResultsMobileTab('visualization')}
                        className={`flex-1 min-w-0 ${treeDetectionResultsMobileTab === 'visualization' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                      >
                        Image
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-stretch overflow-x-hidden">
                    {/* Stats - mobile: only when Summary tab */}
                    <div className={`flex justify-center sm:justify-start gap-3 sm:gap-4 flex-shrink-0 ${treeDetectionResultsMobileTab !== 'summary' ? 'hidden lg:flex' : ''}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg`}>
                          <div className="text-center">
                            <div className={`text-sm sm:text-base lg:text-lg font-bold ${rsEmerald.statValue}`}>
                              {treeDetectionResults.total_detections ?? 0}
                            </div>
                            <div className={`text-[9px] sm:text-[10px] ${rsEmerald.statUnit}`}>detections</div>
                          </div>
                        </div>
                        <div className={`mt-1 text-[9px] sm:text-[10px] font-medium ${rsEmerald.statLabel}`}>Total</div>
                      </div>
                      {treeDetectionResults.statistics && (
                        <div className="flex flex-col items-center">
                          <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg`}>
                            <div className="text-center">
                              <div className={`text-sm sm:text-base lg:text-lg font-bold ${rsEmerald.statValue}`}>
                                {(treeDetectionResults.statistics.average_confidence ?? 0).toFixed(2)}
                              </div>
                              <div className={`text-[9px] sm:text-[10px] ${rsEmerald.statUnit}`}>avg conf</div>
                            </div>
                          </div>
                          <div className={`mt-1 text-[9px] sm:text-[10px] font-medium ${rsEmerald.statLabel}`}>Confidence</div>
                        </div>
                      )}
                    </div>

                    {/* Confidence bar - mobile: only when Confidence tab */}
                    {treeDetectionResults.statistics && (
                      <div className={`flex-[2] min-w-0 w-full flex flex-col justify-center max-w-full sm:max-w-[360px] lg:max-w-[420px] min-h-[200px] sm:min-h-[220px] flex-shrink-0 ${treeDetectionResultsMobileTab !== 'confidence' ? 'hidden lg:flex' : ''}`}>
                        <div className={`text-xs sm:text-sm font-medium mb-1 ${rsEmerald.muted}`}>Confidence</div>
                        <svg width="100%" height="220" viewBox="0 0 400 120" className="w-full max-h-[260px]" preserveAspectRatio="xMidYMid meet">
                        {(() => {
                          const meanVal = treeDetectionResults.statistics.average_confidence ?? 0;
                          const minVal = treeDetectionResults.statistics.min_confidence ?? 0;
                          const maxVal = treeDetectionResults.statistics.max_confidence ?? 0;
                          const chartMax = Math.max(meanVal, minVal, maxVal, 1);
                          return (
                            <>
                              {/* Grid lines */}
                              {[0, 0.5, 1].map((ratio) => {
                                const y = 100 - (ratio * 80);
                                const value = chartMax * ratio;
                                return (
                                  <g key={ratio}>
                                    <line x1="40" y1={y} x2="360" y2={y} stroke={rsEmerald.chartGrid} strokeWidth="1" strokeDasharray="2,2" />
                                    <text x="35" y={y + 3} fill={rsEmerald.chartLabel} fontSize="10" textAnchor="end">{value.toFixed(2)}</text>
                                  </g>
                                );
                              })}
                              {/* Error bar - vertical line at mid (x=200), thicker for visibility */}
                              <line x1="200" y1={100 - (maxVal / chartMax) * 80} x2="200" y2={100 - (minVal / chartMax) * 80} stroke={rsEmerald.chartBar} strokeWidth="4" strokeLinecap="round" />
                              <line x1="185" y1={100 - (maxVal / chartMax) * 80} x2="215" y2={100 - (maxVal / chartMax) * 80} stroke={rsEmerald.chartBar} strokeWidth="4" strokeLinecap="round" />
                              <line x1="185" y1={100 - (minVal / chartMax) * 80} x2="215" y2={100 - (minVal / chartMax) * 80} stroke={rsEmerald.chartBar} strokeWidth="4" strokeLinecap="round" />
                              {/* Mean bar centered at mid */}
                              <rect
                                x="150"
                                y={100 - (meanVal / chartMax) * 80}
                                width="100"
                                height={(meanVal / chartMax) * 80}
                                fill="none"
                                stroke={rsEmerald.chartBar}
                                strokeWidth="3"
                                rx="4"
                                className="hover:stroke-[#34d399] cursor-pointer"
                              />
                              <text x="200" y={100 - (meanVal / chartMax) * 80 - 4} fill={rsEmerald.chartValue} fontSize="12" fontWeight="bold" textAnchor="middle">
                                {meanVal.toFixed(2)}
                              </text>
                              <text x="225" y={100 - (minVal / chartMax) * 80 + 4} fill={rsEmerald.chartLabel} fontSize="11" textAnchor="start">Min: {minVal.toFixed(2)}</text>
                              <text x="225" y={100 - (maxVal / chartMax) * 80 - 4} fill={rsEmerald.chartLabel} fontSize="11" textAnchor="start">Max: {maxVal.toFixed(2)}</text>
                              <line x1="40" y1="20" x2="40" y2="100" stroke={rsEmerald.chartAxis} strokeWidth="2" />
                              <line x1="40" y1="100" x2="360" y2="100" stroke={rsEmerald.chartAxis} strokeWidth="2" />
                            </>
                          );
                        })()}
                      </svg>
                    </div>
                  )}

                    {/* Visualization - mobile: only when Image tab */}
                    {treeDetectionResults.visualization_image && (
                      <div className={`flex flex-col flex-shrink-0 w-full lg:flex-1 lg:max-w-[42%] min-w-0 ${treeDetectionResultsMobileTab !== 'visualization' ? 'hidden lg:flex' : ''}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={rsEmerald.vizLabel}>Visualization</span>
                          <button
                            onClick={() => setShowTreeDetectionModal(true)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${rsEmerald.primaryBtn}`}
                            title="View image full screen"
                          >
                            <i className="ri-fullscreen-line text-sm"></i>
                            Expand
                          </button>
                        </div>
                        <div
                          className={`relative w-full rounded border overflow-hidden cursor-pointer group min-h-[100px] max-h-28 sm:max-h-32 lg:max-h-36 ${rsEmerald.imageBorder}`}
                          onClick={() => setShowTreeDetectionModal(true)}
                        >
                          <img
                            src={`data:image/${treeDetectionResults.visualization_image_format || 'png'};base64,${treeDetectionResults.visualization_image}`}
                            alt="Tree Detection"
                            className="w-full h-full object-contain group-hover:opacity-90 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-fullscreen-line text-2xl text-white drop-shadow"></i>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Crown Segmentation Results Display - below map, same as tree detection */}
          {crownSegmentationResults && crownSegmentationResults.success && (
            <div className={`flex-[0.667] sm:flex-[0.35] ${rsEmerald.shell}`}>
              <button
                onClick={() => {
                  setCrownSegmentationResults(null);
                  setAutoGptRequest(null);
                  setShowCrownSegmentationModal(false);
                  layerControls?.handleCrownSegmentationMapUpdate?.(null);
                }}
                className={`absolute top-2 right-2 z-10 ${rsEmerald.closeBtn}`}
                title="Close results"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="p-2 sm:p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-3 pr-11 sm:pr-14">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                      <i className={`ri-scissors-cut-line text-lg sm:text-xl flex-shrink-0 ${rsEmerald.icon}`}></i>
                      <h6 className={`text-sm sm:text-base font-bold ${rsEmerald.title}`}>Crown Segmentation Results</h6>
                      {(crownSegmentationResults.total_detections ?? crownSegmentationResults.total_segments) !== undefined && (
                        <span className={`text-xs sm:text-sm font-semibold px-3 py-1 rounded-full ${rsEmerald.badge}`}>
                          {crownSegmentationResults.total_detections ?? crownSegmentationResults.total_segments ?? 0} segments
                        </span>
                      )}
                    </div>
                    {(crownSegmentationResults.geojson || crownSegmentationResults.geojson_wgs84) && (
                      <button
                        type="button"
                        onClick={handleExportCrownGeoJson}
                        className={`flex items-center gap-1.5 flex-shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${rsEmerald.exportBtn}`}
                        title="Download GeoJSON (WGS84 / EPSG:4326 when available)"
                      >
                        <i className="ri-download-2-line text-sm"></i>
                        Export GeoJSON
                      </button>
                    )}
                  </div>

                  {/* Mobile-only filter tags */}
                  <div className="flex lg:hidden gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setCrownSegmentationResultsMobileTab('summary')}
                      className={`flex-1 min-w-0 ${crownSegmentationResultsMobileTab === 'summary' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                    >
                      Summary
                    </button>
                    {crownSegmentationResults.statistics && (
                      <button
                        type="button"
                        onClick={() => setCrownSegmentationResultsMobileTab('confidence')}
                        className={`flex-1 min-w-0 ${crownSegmentationResultsMobileTab === 'confidence' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                      >
                        Confidence
                      </button>
                    )}
                    {crownSegmentationResults.visualization_image && (
                      <button
                        type="button"
                        onClick={() => setCrownSegmentationResultsMobileTab('visualization')}
                        className={`flex-1 min-w-0 ${crownSegmentationResultsMobileTab === 'visualization' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                      >
                        Image
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-stretch overflow-x-hidden">
                    {/* Stats - mobile: only when Summary tab */}
                    <div className={`flex justify-center sm:justify-start gap-3 sm:gap-4 flex-shrink-0 ${crownSegmentationResultsMobileTab !== 'summary' ? 'hidden lg:flex' : ''}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg`}>
                          <div className="text-center">
                            <div className={`text-sm sm:text-base lg:text-lg font-bold ${rsEmerald.statValue}`}>
                              {crownSegmentationResults.total_detections ?? crownSegmentationResults.total_segments ?? 0}
                            </div>
                            <div className={`text-[9px] sm:text-[10px] ${rsEmerald.statUnit}`}>segments</div>
                          </div>
                        </div>
                        <div className={`mt-1 text-[9px] sm:text-[10px] font-medium ${rsEmerald.statLabel}`}>Total</div>
                      </div>
                      {crownSegmentationResults.statistics && (
                        <div className="flex flex-col items-center">
                          <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg`}>
                            <div className="text-center">
                              <div className={`text-sm sm:text-base lg:text-lg font-bold ${rsEmerald.statValue}`}>
                                {(crownSegmentationResults.statistics.average_confidence ?? 0).toFixed(2)}
                              </div>
                              <div className={`text-[9px] sm:text-[10px] ${rsEmerald.statUnit}`}>avg conf</div>
                            </div>
                          </div>
                          <div className={`mt-1 text-[9px] sm:text-[10px] font-medium ${rsEmerald.statLabel}`}>Confidence</div>
                        </div>
                      )}
                    </div>

                    {/* Confidence bar - mobile: only when Confidence tab */}
                    {crownSegmentationResults.statistics && (
                      <div className={`flex-[2] min-w-0 w-full flex flex-col justify-center max-w-full sm:max-w-[360px] lg:max-w-[420px] min-h-[200px] sm:min-h-[220px] flex-shrink-0 ${crownSegmentationResultsMobileTab !== 'confidence' ? 'hidden lg:flex' : ''}`}>
                        <div className={`text-xs sm:text-sm font-medium mb-1 ${rsEmerald.muted}`}>Confidence</div>
                        <svg width="100%" height="220" viewBox="0 0 400 120" className="w-full max-h-[260px]" preserveAspectRatio="xMidYMid meet">
                        {(() => {
                          const meanVal = crownSegmentationResults.statistics.average_confidence ?? 0;
                          const minVal = crownSegmentationResults.statistics.min_confidence ?? 0;
                          const maxVal = crownSegmentationResults.statistics.max_confidence ?? 0;
                          const chartMax = Math.max(meanVal, minVal, maxVal, 1);
                          return (
                            <>
                              {[0, 0.5, 1].map((ratio) => {
                                const y = 100 - (ratio * 80);
                                const value = chartMax * ratio;
                                return (
                                  <g key={ratio}>
                                    <line x1="40" y1={y} x2="360" y2={y} stroke={rsEmerald.chartGrid} strokeWidth="1" strokeDasharray="2,2" />
                                    <text x="35" y={y + 3} fill={rsEmerald.chartLabel} fontSize="10" textAnchor="end">{value.toFixed(2)}</text>
                                  </g>
                                );
                              })}
                              <line x1="200" y1={100 - (maxVal / chartMax) * 80} x2="200" y2={100 - (minVal / chartMax) * 80} stroke={rsEmerald.chartBar} strokeWidth="4" strokeLinecap="round" />
                              <line x1="185" y1={100 - (maxVal / chartMax) * 80} x2="215" y2={100 - (maxVal / chartMax) * 80} stroke={rsEmerald.chartBar} strokeWidth="4" strokeLinecap="round" />
                              <line x1="185" y1={100 - (minVal / chartMax) * 80} x2="215" y2={100 - (minVal / chartMax) * 80} stroke={rsEmerald.chartBar} strokeWidth="4" strokeLinecap="round" />
                              <rect
                                x="150"
                                y={100 - (meanVal / chartMax) * 80}
                                width="100"
                                height={(meanVal / chartMax) * 80}
                                fill="none"
                                stroke={rsEmerald.chartBar}
                                strokeWidth="3"
                                rx="4"
                                className="hover:stroke-[#34d399] cursor-pointer"
                              />
                              <text x="200" y={100 - (meanVal / chartMax) * 80 - 4} fill={rsEmerald.chartValue} fontSize="12" fontWeight="bold" textAnchor="middle">
                                {meanVal.toFixed(2)}
                              </text>
                              <text x="225" y={100 - (minVal / chartMax) * 80 + 4} fill={rsEmerald.chartLabel} fontSize="11" textAnchor="start">Min: {minVal.toFixed(2)}</text>
                              <text x="225" y={100 - (maxVal / chartMax) * 80 - 4} fill={rsEmerald.chartLabel} fontSize="11" textAnchor="start">Max: {maxVal.toFixed(2)}</text>
                              <line x1="40" y1="20" x2="40" y2="100" stroke={rsEmerald.chartAxis} strokeWidth="2" />
                              <line x1="40" y1="100" x2="360" y2="100" stroke={rsEmerald.chartAxis} strokeWidth="2" />
                            </>
                          );
                        })()}
                      </svg>
                    </div>
                  )}

                    {/* Visualization - mobile: only when Image tab */}
                    {crownSegmentationResults.visualization_image && (
                      <div className={`flex flex-col flex-shrink-0 w-full lg:flex-1 lg:max-w-[42%] min-w-0 ${crownSegmentationResultsMobileTab !== 'visualization' ? 'hidden lg:flex' : ''}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={rsEmerald.vizLabel}>Visualization</span>
                          <button
                            onClick={() => setShowCrownSegmentationModal(true)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${rsEmerald.primaryBtn}`}
                            title="View image full screen"
                          >
                            <i className="ri-fullscreen-line text-sm"></i>
                            Expand
                          </button>
                        </div>
                        <div
                          className={`relative w-full rounded border overflow-hidden cursor-pointer group min-h-[100px] max-h-28 sm:max-h-32 lg:max-h-36 ${rsEmerald.imageBorder}`}
                          onClick={() => setShowCrownSegmentationModal(true)}
                        >
                          <img
                            src={`data:image/${crownSegmentationResults.visualization_image_format || 'png'};base64,${crownSegmentationResults.visualization_image}`}
                            alt="Crown Segmentation"
                            className="w-full h-full object-contain group-hover:opacity-90 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-fullscreen-line text-2xl text-white drop-shadow"></i>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Field boundary delineation — same layout as tree / crown */}
          {cropfieldDelineationResults && cropfieldDelineationResults.success && (
            <div className={`flex-[0.667] sm:flex-[0.35] ${rsEmerald.shell}`}>
              <button
                onClick={() => {
                  setCropfieldDelineationResults(null);
                  setAutoGptRequest(null);
                  setShowCropfieldDelineationModal(false);
                  if (layerControls?.handleCropfieldDelineationUpdate) {
                    layerControls.handleCropfieldDelineationUpdate(null);
                  }
                }}
                className={`absolute top-2 right-2 z-10 ${rsEmerald.closeBtn}`}
                title="Close results"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="p-2 sm:p-3">
                  {/* pr-* reserves space for the absolute close button (top-right) so Export does not overlap */}
                  <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-3 pr-11 sm:pr-14">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                      <i className={`ri-shape-2-line text-lg sm:text-xl flex-shrink-0 ${rsEmerald.icon}`}></i>
                      <h6 className={`text-sm sm:text-base font-bold ${rsEmerald.title}`}>Field Boundary Delineation Results</h6>
                      <span className={`text-xs sm:text-sm font-semibold px-3 py-1 rounded-full ${rsEmerald.badge}`}>
                        {cropfieldDelineationResults.field_count ?? 0} fields
                      </span>
                    </div>
                    {(cropfieldDelineationResults.geojson || cropfieldDelineationResults.geojson_wgs84) && (
                      <button
                        type="button"
                        onClick={handleExportCropfieldGeoJson}
                        className={`flex items-center gap-1.5 flex-shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${rsEmerald.exportBtn}`}
                        title="Download GeoJSON (WGS84 / EPSG:4326 when available)"
                      >
                        <i className="ri-download-2-line text-sm"></i>
                        Export GeoJSON
                      </button>
                    )}
                  </div>

                  <div className="flex lg:hidden gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setCropfieldDelineationResultsMobileTab('summary')}
                      className={`flex-1 min-w-0 ${cropfieldDelineationResultsMobileTab === 'summary' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                    >
                      Summary
                    </button>
                    {cropfieldDelineationResults.statistics && (
                      <button
                        type="button"
                        onClick={() => setCropfieldDelineationResultsMobileTab('confidence')}
                        className={`flex-1 min-w-0 ${cropfieldDelineationResultsMobileTab === 'confidence' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                      >
                        Confidence
                      </button>
                    )}
                    {cropfieldDelineationResults.preview_png_base64 && (
                      <button
                        type="button"
                        onClick={() => setCropfieldDelineationResultsMobileTab('visualization')}
                        className={`flex-1 min-w-0 ${cropfieldDelineationResultsMobileTab === 'visualization' ? rsEmerald.mobileTabActive : rsEmerald.mobileTabInactive}`}
                      >
                        Image
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-stretch overflow-x-hidden">
                    <div
                      className={`flex justify-center sm:justify-start gap-3 sm:gap-4 flex-shrink-0 ${
                        cropfieldDelineationResultsMobileTab !== 'summary' ? 'hidden lg:flex' : ''
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg`}>
                          <div className="text-center">
                            <div className={`text-sm sm:text-base lg:text-lg font-bold ${rsEmerald.statValue}`}>
                              {cropfieldDelineationResults.field_count ?? 0}
                            </div>
                            <div className={`text-[9px] sm:text-[10px] ${rsEmerald.statUnit}`}>fields</div>
                          </div>
                        </div>
                        <div className={`mt-1 text-[9px] sm:text-[10px] font-medium ${rsEmerald.statLabel}`}>Total</div>
                      </div>
                      {cropfieldDelineationResults.statistics && (
                        <div className="flex flex-col items-center">
                          <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full ${rsEmerald.statRing} flex items-center justify-center shadow-lg`}>
                            <div className="text-center">
                              <div className={`text-sm sm:text-base lg:text-lg font-bold ${rsEmerald.statValue}`}>
                                {(
                                  cropfieldDelineationResults.statistics.avg_confidence ??
                                  cropfieldDelineationResults.statistics.average_confidence ??
                                  0
                                ).toFixed(2)}
                              </div>
                              <div className={`text-[9px] sm:text-[10px] ${rsEmerald.statUnit}`}>avg conf</div>
                            </div>
                          </div>
                          <div className={`mt-1 text-[9px] sm:text-[10px] font-medium ${rsEmerald.statLabel}`}>Confidence</div>
                        </div>
                      )}
                    </div>

                    {cropfieldDelineationResults.statistics && (
                      <div
                        className={`flex-[2] min-w-0 w-full flex flex-col justify-center max-w-full sm:max-w-[360px] lg:max-w-[420px] min-h-[200px] sm:min-h-[220px] flex-shrink-0 ${
                          cropfieldDelineationResultsMobileTab !== 'confidence' ? 'hidden lg:flex' : ''
                        }`}
                      >
                        <div className={`text-xs sm:text-sm font-medium mb-1 ${rsEmerald.muted}`}>Confidence</div>
                        <svg width="100%" height="220" viewBox="0 0 400 120" className="w-full max-h-[260px]" preserveAspectRatio="xMidYMid meet">
                          {(() => {
                            const meanVal =
                              cropfieldDelineationResults.statistics.avg_confidence ??
                              cropfieldDelineationResults.statistics.average_confidence ??
                              0;
                            const minVal = cropfieldDelineationResults.statistics.min_confidence ?? 0;
                            const maxVal = cropfieldDelineationResults.statistics.max_confidence ?? 0;
                            const chartMax = Math.max(meanVal, minVal, maxVal, 1);
                            return (
                              <>
                                {[0, 0.5, 1].map((ratio) => {
                                  const y = 100 - ratio * 80;
                                  const value = chartMax * ratio;
                                  return (
                                    <g key={ratio}>
                                      <line x1="40" y1={y} x2="360" y2={y} stroke={rsEmerald.chartGrid} strokeWidth="1" strokeDasharray="2,2" />
                                      <text x="35" y={y + 3} fill={rsEmerald.chartLabel} fontSize="10" textAnchor="end">
                                        {value.toFixed(2)}
                                      </text>
                                    </g>
                                  );
                                })}
                                <line
                                  x1="200"
                                  y1={100 - (maxVal / chartMax) * 80}
                                  x2="200"
                                  y2={100 - (minVal / chartMax) * 80}
                                  stroke={rsEmerald.chartBar}
                                  strokeWidth="4"
                                  strokeLinecap="round"
                                />
                                <line
                                  x1="185"
                                  y1={100 - (maxVal / chartMax) * 80}
                                  x2="215"
                                  y2={100 - (maxVal / chartMax) * 80}
                                  stroke={rsEmerald.chartBar}
                                  strokeWidth="4"
                                  strokeLinecap="round"
                                />
                                <line
                                  x1="185"
                                  y1={100 - (minVal / chartMax) * 80}
                                  x2="215"
                                  y2={100 - (minVal / chartMax) * 80}
                                  stroke={rsEmerald.chartBar}
                                  strokeWidth="4"
                                  strokeLinecap="round"
                                />
                                <rect
                                  x="150"
                                  y={100 - (meanVal / chartMax) * 80}
                                  width="100"
                                  height={(meanVal / chartMax) * 80}
                                  fill="none"
                                  stroke={rsEmerald.chartBar}
                                  strokeWidth="3"
                                  rx="4"
                                  className="hover:stroke-[#34d399] cursor-pointer"
                                />
                                <text
                                  x="200"
                                  y={100 - (meanVal / chartMax) * 80 - 4}
                                  fill={rsEmerald.chartValue}
                                  fontSize="12"
                                  fontWeight="bold"
                                  textAnchor="middle"
                                >
                                  {meanVal.toFixed(2)}
                                </text>
                                <text x="225" y={100 - (minVal / chartMax) * 80 + 4} fill={rsEmerald.chartLabel} fontSize="11" textAnchor="start">
                                  Min: {minVal.toFixed(2)}
                                </text>
                                <text x="225" y={100 - (maxVal / chartMax) * 80 - 4} fill={rsEmerald.chartLabel} fontSize="11" textAnchor="start">
                                  Max: {maxVal.toFixed(2)}
                                </text>
                                <line x1="40" y1="20" x2="40" y2="100" stroke={rsEmerald.chartAxis} strokeWidth="2" />
                                <line x1="40" y1="100" x2="360" y2="100" stroke={rsEmerald.chartAxis} strokeWidth="2" />
                              </>
                            );
                          })()}
                        </svg>
                      </div>
                    )}

                    {cropfieldDelineationResults.preview_png_base64 && (
                      <div
                        className={`flex flex-col flex-shrink-0 w-full lg:flex-1 lg:max-w-[42%] min-w-0 ${
                          cropfieldDelineationResultsMobileTab !== 'visualization' ? 'hidden lg:flex' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={rsEmerald.vizLabel}>Visualization</span>
                          <button
                            onClick={() => setShowCropfieldDelineationModal(true)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${rsEmerald.primaryBtn}`}
                            title="View image full screen"
                            type="button"
                          >
                            <i className="ri-fullscreen-line text-sm"></i>
                            Expand
                          </button>
                        </div>
                        <div
                          className={`relative w-full rounded border overflow-hidden cursor-pointer group min-h-[100px] max-h-28 sm:max-h-32 lg:max-h-36 ${rsEmerald.imageBorder}`}
                          onClick={() => setShowCropfieldDelineationModal(true)}
                          role="presentation"
                        >
                          <img
                            src={`data:image/png;base64,${cropfieldDelineationResults.preview_png_base64}`}
                            alt="Field delineation"
                            className="w-full h-full object-contain group-hover:opacity-90 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-fullscreen-line text-2xl text-white drop-shadow"></i>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`mt-3 pt-3 border-t space-y-1 text-[10px] sm:text-xs ${rsEmerald.infoDivider} ${rsEmerald.muted}`}>
                    {cropfieldDelineationResults.crs && (
                      <div>
                        <span className={rsEmerald.statUnit}>CRS:</span>{' '}
                        <span className={`font-mono ${rsEmerald.body}`}>{cropfieldDelineationResults.crs}</span>
                      </div>
                    )}
                    <div className={rsEmerald.body}>
                      {cropfieldDelineationResults.geojson?.features?.length ?? 0} polygon feature(s) on the map.
                      {cropfieldDelineationResults.statistics?.processing_time_min != null && (
                        <span className="ml-2">
                          Processing: {cropfieldDelineationResults.statistics.processing_time_min} min
                        </span>
                      )}
                    </div>
                    {cropfieldDelineationResults.message && (
                      <p className={rsEmerald.summaryValue}>{cropfieldDelineationResults.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
      </div>

      {/* Save Field Modal */}
      {showSaveModal && drawnField && (
        <SaveFieldModal
          drawnField={drawnField}
          onClose={handleCloseSaveModal}
          onSave={handleFieldSaved}
        />
      )}

      {/* Update Field Boundary Modal */}
      {showUpdateModal && fieldToUpdate && confirmedUpdatedGeometry && (
        <UpdateFieldModal
          field={fieldToUpdate}
          updatedGeometry={confirmedUpdatedGeometry}
          onClose={handleCloseUpdateModal}
          onUpdate={handleUpdateFieldBoundary}
          allowFarmerAssign={
            !!authUser &&
            (authUser.is_superuser === true || authUser.role === 'admin')
          }
        />
      )}

      {/* Settings page — opens beside the side menu; the top-right user menu can open it too. */}
      {authUser && !authUser.is_system_guest && (
        <UserProfileModal
          variant="page"
          open={profileModalOpen}
          user={authUser}
          onClose={() => setProfileModalOpen(false)}
          onSaved={async () => {
            await refreshUser();
            setReloadFields(true);
          }}
        />
      )}

      {/* Crown Segmentation Image - Full screen modal */}
      {showCrownSegmentationModal && crownSegmentationResults?.visualization_image && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center ${rsEmerald.modalOverlay}`}
          onClick={() => setShowCrownSegmentationModal(false)}
        >
          <div
            className={`relative rounded-lg shadow-2xl max-w-[95vw] max-h-[95vh] p-4 ${rsEmerald.modalPanel}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCrownSegmentationModal(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-gray-700 text-white rounded-full hover:bg-gray-600 transition-colors"
              title="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            <div className="flex items-center justify-between mb-4 pr-10">
              <h3 className={`text-lg font-semibold ${rsEmerald.modalTitle}`}>Crown Segmentation Visualization</h3>
              <div className={`text-sm ${rsEmerald.muted}`}>
                Total: {crownSegmentationResults.total_detections ?? crownSegmentationResults.total_segments ?? 0}
                {crownSegmentationResults.statistics?.average_confidence != null && (
                  <> | Avg conf: {(crownSegmentationResults.statistics.average_confidence).toFixed(3)}</>
                )}
              </div>
            </div>
            <div className="overflow-auto max-h-[85vh]">
              <img
                src={`data:image/${crownSegmentationResults.visualization_image_format || 'png'};base64,${crownSegmentationResults.visualization_image}`}
                alt="Crown Segmentation - Full screen"
                className={`max-w-full h-auto rounded border ${rsEmerald.imageBorder}`}
              />
            </div>
            {crownSegmentationResults.statistics && (
              <div className={`mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-sm ${rsEmerald.infoDivider}`}>
                <div>
                  <div className={rsEmerald.muted}>Average Confidence</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(crownSegmentationResults.statistics.average_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className={rsEmerald.muted}>Min</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(crownSegmentationResults.statistics.min_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className={rsEmerald.muted}>Max</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(crownSegmentationResults.statistics.max_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Field delineation preview — full screen modal */}
      {showCropfieldDelineationModal && cropfieldDelineationResults?.preview_png_base64 && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center ${rsEmerald.modalOverlay}`}
          onClick={() => setShowCropfieldDelineationModal(false)}
        >
          <div
            className={`relative rounded-lg shadow-2xl max-w-[95vw] max-h-[95vh] p-4 ${rsEmerald.modalPanel}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCropfieldDelineationModal(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-gray-700 text-white rounded-full hover:bg-gray-600 transition-colors"
              title="Close"
              type="button"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            <div className="flex items-center justify-between mb-4 pr-10">
              <h3 className={`text-lg font-semibold ${rsEmerald.modalTitle}`}>Field Boundary Delineation</h3>
              <div className={`text-sm ${rsEmerald.muted}`}>
                Fields: {cropfieldDelineationResults.field_count ?? 0}
                {(cropfieldDelineationResults.statistics?.avg_confidence ??
                  cropfieldDelineationResults.statistics?.average_confidence) != null && (
                  <>
                    {' '}
                    | Avg conf:{' '}
                    {(
                      cropfieldDelineationResults.statistics.avg_confidence ??
                      cropfieldDelineationResults.statistics.average_confidence ??
                      0
                    ).toFixed(3)}
                  </>
                )}
              </div>
            </div>
            <div className="overflow-auto max-h-[85vh]">
              <img
                src={`data:image/png;base64,${cropfieldDelineationResults.preview_png_base64}`}
                alt="Field delineation - full screen"
                className={`max-w-full h-auto rounded border ${rsEmerald.imageBorder}`}
              />
            </div>
            {cropfieldDelineationResults.statistics && (
              <div className={`mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-sm ${rsEmerald.infoDivider}`}>
                <div>
                  <div className={rsEmerald.muted}>Average Confidence</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(
                      cropfieldDelineationResults.statistics.avg_confidence ??
                      cropfieldDelineationResults.statistics.average_confidence ??
                      0
                    ).toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className={rsEmerald.muted}>Min</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(cropfieldDelineationResults.statistics.min_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className={rsEmerald.muted}>Max</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(cropfieldDelineationResults.statistics.max_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tree Detection Image - Full screen modal */}
      {showTreeDetectionModal && treeDetectionResults?.visualization_image && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center ${rsEmerald.modalOverlay}`}
          onClick={() => setShowTreeDetectionModal(false)}
        >
          <div
            className={`relative rounded-lg shadow-2xl max-w-[95vw] max-h-[95vh] p-4 ${rsEmerald.modalPanel}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowTreeDetectionModal(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-gray-700 text-white rounded-full hover:bg-gray-600 transition-colors"
              title="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            <div className="flex items-center justify-between mb-4 pr-10">
              <h3 className={`text-lg font-semibold ${rsEmerald.modalTitle}`}>Tree Detection Visualization</h3>
              <div className={`text-sm ${rsEmerald.muted}`}>
                Total: {treeDetectionResults.total_detections ?? 0}
                {treeDetectionResults.statistics?.average_confidence != null && (
                  <> | Avg conf: {(treeDetectionResults.statistics.average_confidence).toFixed(3)}</>
                )}
              </div>
            </div>
            <div className="overflow-auto max-h-[85vh]">
              <img
                src={`data:image/${treeDetectionResults.visualization_image_format || 'png'};base64,${treeDetectionResults.visualization_image}`}
                alt="Tree Detection - Full screen"
                className={`max-w-full h-auto rounded border ${rsEmerald.imageBorder}`}
              />
            </div>
            {treeDetectionResults.statistics && (
              <div className={`mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-sm ${rsEmerald.infoDivider}`}>
                <div>
                  <div className={rsEmerald.muted}>Average Confidence</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(treeDetectionResults.statistics.average_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className={rsEmerald.muted}>Min</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(treeDetectionResults.statistics.min_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className={rsEmerald.muted}>Max</div>
                  <div className={`font-semibold ${rsEmerald.modalTitle}`}>
                    {(treeDetectionResults.statistics.max_confidence ?? 0).toFixed(3)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Crop calendar — farmer input (after right-click on farm) */}
      {showCropCalendarInputModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4"
          onClick={handleCropCalendarInputCancel}
        >
          <div
            className="w-full max-w-xl rounded-xl border border-emerald-500/30 bg-gray-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-600/20">
                <i className="ri-calendar-todo-line text-xl text-emerald-400" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${rsEmerald.modalTitle}`}>Crop calendar</h3>
                <p className="mt-1 text-sm text-gray-400">
                  {cropCalendarFieldInfoLoading
                    ? 'Checking saved crop records for this farm…'
                    : fieldCropHasDisplayableInfo(cropCalendarFieldInfo)
                      ? 'Saved crop details are filled in. Open the crop calendar, then click Load to run it.'
                      : 'No crop record for this farm yet. Enter the details below, then load the crop calendar.'}
                </p>
              </div>
            </div>
            {fieldCropHasDisplayableInfo(cropCalendarFieldInfo) && (
              <div className="mb-4">
                <FieldCropInfoCard info={cropCalendarFieldInfo} />
              </div>
            )}
            <CropCalendarInputs
              value={cropCalendarForm}
              onChange={setCropCalendarForm}
              disabled={cropCalendarFieldInfoLoading}
              inputClass="w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-60"
              labelClass="mb-1 block text-xs font-medium text-gray-400"
              selectClass="w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-60"
            />
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCropCalendarInputCancel}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropCalendarInputConfirm}
                disabled={cropCalendarFieldInfoLoading || !isCropCalendarFormReady(cropCalendarForm)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                View crop calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <FarmSplitProgressOverlay
        variant={
          isSavingFarmSplit
            ? 'split-saving'
            : isSplittingFarm
              ? 'split'
              : aiFieldDelineationLoading
                ? 'delineation'
                : null
        }
      />

      {/* Farm Level Action (Draw/Upload) Confirmation Dialog */}
      {showFarmLevelActionDialog && pendingFarmLevelAction && (
        <LayerSwitchConfirmDialog
          isOpen={showFarmLevelActionDialog}
          currentLayer="Large Scale Analysis"
          nextLayer={
            pendingFarmLevelAction.type === 'draw'
              ? 'Farm Level Analysis (Drawing Field)'
              : pendingFarmLevelAction.type === 'aiDelineation'
                ? 'Farm Level Analysis (AI Field Delineation)'
                : 'Farm Level Analysis (Uploading Field)'
          }
          onConfirm={handleFarmLevelActionConfirm}
          onCancel={handleFarmLevelActionCancel}
        />
      )}

      {delineationDeleteConfirm && delineationDeleteConfirm.indices.length > 0 && (
        <div
          className={deleteConfirmTheme.overlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delineation-delete-title"
          onClick={() => setDelineationDeleteConfirm(null)}
        >
          <div
            className={deleteConfirmTheme.panel}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className={deleteConfirmTheme.warnIconWrap}>
                <i className="ri-delete-bin-line text-lg" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="delineation-delete-title" className={deleteConfirmTheme.title}>
                  {delineationDeleteConfirm.title ??
                    `Delete ${
                      delineationDeleteConfirm.indices.length === 1
                        ? 'this block'
                        : `${delineationDeleteConfirm.indices.length} blocks`
                    }?`}
                </h3>
                <p className={deleteConfirmTheme.body}>
                  {delineationDeleteConfirm.body ??
                    (delineationDeleteConfirm.indices.length === 1
                      ? 'This detected boundary will be removed from the map. Other blocks will stay if any remain.'
                      : `These ${delineationDeleteConfirm.indices.length} boundaries will be removed from the map. Unselected blocks will stay.`)}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={deleteConfirmTheme.cancelBtn}
                onClick={() => setDelineationDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={deleteConfirmTheme.dangerBtn}
                onClick={confirmDeleteDelineationBlocks}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Digisaka LLM Chat — only when `digisakaGpt` (or alias) in allowed_layer_keys, or no filter */}
      {shouldShowDigisakaGpt(effectiveAllowedLayerKeys) && (
        <DigisakaLLMChat
          autoRequest={autoGptRequest}
          screenshotTargetRef={mapScreenshotRef}
          dashboardScreenshotRef={dashboardScreenshotRef}
          onAutoRequestConsumed={() => setAutoGptRequest(null)}
          onChatFullscreenChange={handleChatFullscreenChange}
          onPandoyPageChange={setPandoyPageOpen}
          hideCornerLauncher={settingsPageOpen || pandoyPageOpen}
          fieldContext={
            isValidGptFieldContext(selectedField)
              ? {
                  farmId: String(selectedField.farm_id),
                  farmName: selectedField.farm_name,
                  blockCount: selectedFieldBlockCount(selectedField),
                }
              : null
          }
        />
      )}
    </div>
  );
}